import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import React, { useState, useRef, useCallback, useMemo, useEffect, Suspense, lazy } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ActionButtons from "./ActionButtons";
import { BTN_SX } from "./formSubmissionStyles";
import authAxios from "../../services/Api";
import apiFieldAxios from "../../services/ApiFieldValue";
import { BASE_API_URL } from "../../services/config";
import { toast } from "react-toastify";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import useModal from "./useModal";
import { findAllFieldsByType, findFieldByNameAnywhere, setAtPath, buildFieldIndexMap } from "../FormBuilder/helpers/fieldTree";
// Resizer moved to useMediaHandlers hook
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LazyLoaderTrigger from "../../shared/Loader/LazyLoaderTrigger";
import { useLoading } from "../../shared/Loader/LoadingContext";
import { useSoftLoading } from "../../shared/Loader/SoftLoadingContext";
import {
  formatDateYMD,
  nowHHmm,
  compareTimes as compareTimes_util,
} from '../../utils/dateUtils';
// tryParseDate, parseTime24, diffInDays, isBefore moved to useFormCalculations hook
import { components } from "react-select";
import Cookies from "js-cookie";
import TokenService from '../../services/token.service';
import SVGIcon from "../../components/DynamicIcon";
import { useLocation } from "react-router-dom";
import { IMAGE } from "../../services/config"; // import image api
import { SIGNATURE } from "../../services/config";
import { AUDIO } from "../../services/config";
import { showSuccessToast } from "../../shared/CustomToast/customtoast";
import dataUploadAuthAxios from "../../services/DatauploadApi";
import * as turf from "@turf/turf";
import axios from "axios";
import { useTranslation } from "react-i18next";
// Stylesheets (FormSubmission.css, Submission.scss, popupModal.css) are loaded
// globally and unconditionally in src/index.js — the single source of truth for
// app-wide styles. They were previously re-imported here too; those duplicates
// are removed (webpack deduped them anyway) so there is one declaration point.
import { useFormCalculations } from './hooks/useFormCalculations';
import { useMediaHandlers } from './hooks/useMediaHandlers';
import { resolveRuleUrl } from './utils/resolveRuleUrl';
import { _submissionMath } from './utils/mathInstance';
import useMediaQuery from '../../hooks/useMediaQuery';
import useClickOutside from '../../hooks/useClickOutside';
import useKeyDown from '../../hooks/useKeyDown';

// "2024-01-31T10:00:00Z" -> "31-01-2024 10:00"
const formatApprovalTimestamp = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// "Approved by <name> on <time>" label for one approval-workflow stage.
// Reject and approve both stamp the same firstApprovalActionAt/secondApprovalActionAt
// timestamp field (see handleReject / submitApprovalMode) — the timestamp
// alone can't tell them apart, so the stage's numeric status flag
// (1 = approved, 0 = rejected, -1/undefined = pending) has to gate the verb.
// Rejections show nothing for this stage.
const formatApprovalStageLabel = (stagePrefix, statusValue, timestamp, approvedBy) => {
  if (!timestamp || statusValue === 0) return null;
  const byPart = approvedBy ? ` by ${approvedBy}` : '';
  return `${stagePrefix}Approved on ${formatApprovalTimestamp(timestamp)}${byPart}`;
};

const MapNewModal = lazy(() => import("./MapNewModal"));
const UpiPaymentModal = lazy(() => import("./UpiPaymentModal"));
const ImagePopup = lazy(() => import("./ImagePopup"));
const SignaturePopup = lazy(() => import("./SignaturePopup"));
const PopupDynamicForm = lazy(() => import("./PopupDynamicForm"));
const CustomModal = lazy(() => import("../../shared/CustomModal/CustomModal"));
const ConfirmModal = lazy(() => import("../../shared/ConfirmModal/ConfirmModal"));
const DynamicGridComponent = lazy(() => import("./DynamicGridComponent"));
const FormFieldRenderer = lazy(() => import("./FormFieldRenderer"));
// ─── AGROECO tenant configuration ────────────────────────────────────────────
// These IDs were previously defined inside the component, causing them to be
// re-evaluated on every render. Moved to module level so they are constants.
const AGROECO_ORG_ID = "c71b01a9-681c-4a67-b2cf-6c80b1571505";
const FDSV2_FORMID = "66b531f3995a02e8c74d9df6";

// Returns the subform list, hiding the "Others" tab when the selected project
// is not a premium tier (Carbon Booster / Coco life Plus).
// Extracted from 4 duplicated inline filter blocks — single source of truth.
function applyAgroEcoSubformFilter(subformList, projectValue) {
  if (projectValue === 'Carbon Booster' || projectValue === 'Coco life Plus') {
    return subformList;
  }
  return subformList.filter(item => item.form_name !== "Others");
}
// ─────────────────────────────────────────────────────────────────────────────

// mathjs singleton imported above — shared with hooks via utils/mathInstance.js

// P-11: Extracted from 4 copy-paste blocks inside the component.
// Mutates controlData in-place — sets group_array_index and addFgArrayIndex on
// each item so form-group navigation renders the correct repeat counts.
function buildGroupIndices(controlData) {
  const groupCounts = controlData
    .filter(item => item.group_first)
    .reduce((acc, item) => {
      const groupId = item.group_id;
      acc[groupId] = (acc[groupId] || 0) + 1;
      return acc;
    }, {});

  // Step 1: Find all parent group objects
  const parentGroups = controlData.filter(
    (item) => item.group_parent && item.group_first
  );

  // Step 2: Process each parent group
  parentGroups.forEach((parentGroup) => {
    // Calculate group_array_index for the parent group
    if (parentGroup.group_array_index === undefined || parentGroup.group_array_index === null) {
      parentGroup.group_array_index = groupCounts[parentGroup.group_id] || 1;
    }
    // Step 3: Update group_array_index for related objects
    controlData.forEach((item) => {
      if (
        item.group_id === parentGroup.group_id &&
        item.group_first &&
        item.group_parent &&
        (item.group_array_index === undefined || item.group_array_index === null)
      ) {
        item.group_array_index = groupCounts[parentGroup.group_id] || 1;
      }
    });
    // Step 4: Add addFgArrayIndex for other items in the group
    let index = 1;
    controlData.forEach((item) => {
      if (item.group_id === parentGroup.group_id && item.group_first) {
        item.addFgArrayIndex = index;
        index++;
      }
    });
  });
}
// ─────────────────────────────────────────────────────────────────────────────

// Evaluated once at module load — date doesn't change during a page session.
const today = new Date().toISOString().split('T')[0];

// Pure wrapper — no component state or props needed; stable at module level.
const decryptData = (encryptedData) => TokenService.decryptData(encryptedData);

// The edit GET returns each Radio's stored label, but the client works with (and
// the server expects back) option.value. processControls only converts the subform
// being opened, so subforms the user never visits kept their labels and reached the
// final PUT unconverted. Convert every subform once, right after the GET. Mutates in
// place; unmatched values are left untouched.
function radioLabelsToValues(subforms) {
  if (!Array.isArray(subforms)) return;
  subforms.forEach((subform) => {
    if (!Array.isArray(subform?.controls)) return;
    subform.controls
      .flatMap((c) => (c?.type === "Container" && Array.isArray(c.controls) ? [c, ...c.controls] : [c]))
      .forEach((control) => {
        if (control?.type !== "Radio" || !Array.isArray(control.values)) return;
        if (control.value === undefined || control.value === null || control.value === "") return;
        const raw = String(control.value);
        const matched = control.values.find(
          (opt) => String(opt.value) === raw || String(opt.label).toLowerCase() === raw.toLowerCase()
        );
        if (matched) control.value = matched.value;
      });
  });
}

// ─── Pure utility functions ───────────────────────────────────────────────────
// Defined at module level so they have a single stable reference across all
// renders and are never recreated when the component re-renders.

function formatNumberWithDecimal(number, decimalPlaces) {
  if (typeof number !== "number" || isNaN(number)) {
    return "Invalid number";
  }
  return number.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

function removeThousandSeparator(formattedNumber) {
  if (typeof formattedNumber !== "string") {
    return "Invalid input";
  }
  return formattedNumber.replace(/,/g, "");
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function formatDateToYYYYMMDD(dateString) {
  if (!dateString) return dateString;
  // Already ISO YYYY-MM-DD — return unchanged to avoid misinterpreting the parts
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  // Handles DD-MM-YYYY and DD/MM/YYYY input formats
  const separator = dateString.indexOf("-") !== -1 ? "-" : "/";
  const parts = dateString.split(separator);
  const day = parts[0].padStart(2, "0");
  const month = parts[1].padStart(2, "0");
  const year = parts[2];
  return `${year}-${month}-${day}`;
}

function areDatesMatching(dateStr1, dateStr2, condition) {
  const parsedDate2 = formatDateToYYYYMMDD(dateStr2);
  const date1 = new Date(dateStr1);
  const date2 = new Date(parsedDate2);
  if (condition === "==") return date1.getTime() === date2.getTime();
  if (condition === ">=") return date1.getTime() >= date2.getTime();
  if (condition === "<=") return date1.getTime() <= date2.getTime();
  if (condition === ">") return date1.getTime() > date2.getTime();
  if (condition === "<") return date1.getTime() < date2.getTime();
}
// ─────────────────────────────────────────────────────────────────────────────

// ── Custom react-select menu list components ─────────────────────────────────
// Defined at module level so React always sees the same component type —
// previously inside useMemo([ModalIsOpen]) which caused the dropdown to remount
// whenever ModalIsOpen changed. ModalIsOpen is now passed via selectProps as
// modal_is_open (CustomMenuList) / ms_modal_is_open (MultiSelectMenuList).
function CustomMenuList(props) {
  const { children, selectProps } = props;
  const { name, form_id, selectedapitag, handleAddMasterData, is_transaction, modal_is_open } = selectProps;
  return (
    <div className="select-menu-list-wrap">
      <components.MenuList {...props}>
        {children}
      </components.MenuList>
      {!modal_is_open && is_transaction && (
        <div
          className="select-add-new-fixed"
          onClick={(e) => {
            e.stopPropagation();
            handleAddMasterData(name, form_id, selectedapitag);
          }}
        >
          <div className="select-add-new-inner">
            <SVGIcon name="AddplusIcon" stroke="#FFFFFF" />
            <span>Add New</span>
          </div>
        </div>
      )}
    </div>
  );
}

// MenuList for MultiselectCheckboxes — uses a plain scrollable div to avoid
// react-select version mismatch (library uses v2, app uses v5).
function MultiSelectMenuList(props) {
  const { children, maxHeight, selectProps } = props;
  const { ms_name, ms_form_id, ms_selectedapitag, ms_handleAddMasterData, ms_is_transaction, ms_modal_is_open } = selectProps;
  return (
    <div>
      <div
        className="ms-menu-list-scroll"
        style={{ "--ms-menu-max-height": `${maxHeight || 300}px` }}
      >
        {children}
      </div>
      {!ms_modal_is_open && ms_is_transaction && (
        <div
          className="select-add-new-fixed"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            ms_handleAddMasterData(ms_name, ms_form_id, ms_selectedapitag);
          }}
        >
          <div className="select-add-new-inner">
            <SVGIcon name="AddplusIcon" stroke="#FFFFFF" />
            <span>Add New</span>
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

// Module-level component — stable identity across renders; previously defined
// inside SubmissionDynamicform which caused every group row to remount on each
// parent re-render (React treats inline component definitions as new types).
const GroupActionButtons = ({ showDelete, showAdd, onDelete, onAdd, viewOnly = false }) => (
  <div className="group-action-buttons">
    {showDelete && !viewOnly && (
      <IconButton
        variant="text"
        title="Delete"
        aria-label="Delete"
        onClick={onDelete}
      >
        <DeleteOutlineOutlinedIcon fontSize="small" />
      </IconButton>
    )}
    {showAdd && !viewOnly && (
      <IconButton
        variant="text"
        title="Add new"
        aria-label="Add new"
        onClick={onAdd}
        className="form_submission_formgroup_add_new_btn"
      >
        <AddCircleOutlineIcon fontSize="small" /> Add new
      </IconButton>
    )}
  </div>
);

// These flags are passed as read-only props to FormFieldRenderer and never
// change during a session. Defining them here avoids two useState slots and
// prevents React from scheduling re-renders when they are "set".
const SHOW_ONLY_IN_EDIT = false;
const DISABLE_ONLY_IN_EDIT = false;

// BTN_SX lives in formSubmissionStyles.js — imported below with ActionButtons.

const SubmissionDynamicform = ({
  ModalIsOpen,
  DynamicFormId,
  routeParams,
  setIsModelPopup,
  popup,
  setpopup,
  RefreshSelect,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: { errors, isDirty },
    reset,
    trigger, watch
  } = useForm();

  const [formArray, setFormArray] = useState([]);
  // Always-current ref used by hooks that need formArray without adding it to
  // useCallback deps (which would recreate callbacks on every keypress).
  const formArrayRef = useRef(formArray);
  // Names of fields the user changed since the current (sub)form loaded.
  // Load-time code (the api_rules replay in dynamicFormData, getEditData's
  // deferred radio pass) and handleAPIChanged write whole arrays built from
  // snapshots taken before the click; without this, those writes put the old
  // value back (e.g. a Radio with api_rules snapping back to its saved option).
  const userEditedFieldsRef = useRef(new Set());
  // Replace each field in `next` with its `prev` entry when the user edited it,
  // so a stale snapshot write can't revert the user's change.
  const keepUserEdits = (prev, next) => {
    const edited = userEditedFieldsRef.current;
    if (edited.size === 0) return next;
    const prevByName = new Map(prev.map(f => [f.name, f]));
    return next.map(f => (edited.has(f.name) && prevByName.has(f.name) ? prevByName.get(f.name) : f));
  };

  // Stable handler wrappers — delegates to _impl.current so memo(FormFieldRenderer)
  // sees constant prop references and skips re-renders on unrelated parent state changes.
  const _impl = useRef({});
  const s_fieldChanged = useCallback((...a) => _impl.current.fieldChanged(...a), []);
  const s_fieldSelectChanged = useCallback((...a) => _impl.current.fieldSelectChanged(...a), []);
  const s_fieldMultiSelectChanged = useCallback((...a) => _impl.current.fieldMultiSelectChanged(...a), []);
  const s_fieldChangedDate = useCallback((...a) => _impl.current.fieldChangedDate(...a), []);
  const s_fieldChangedTime = useCallback((...a) => _impl.current.fieldChangedTime(...a), []);
  const s_fieldChangedGeo = useCallback((...a) => _impl.current.fieldChangedGeo(...a), []);
  const s_handleAPIChanged = useCallback((...a) => _impl.current.handleAPIChanged(...a), []);
  const s_triggerMultiPatchRules = useCallback((...a) => _impl.current.triggerMultiPatchRules(...a), []);
  const s_triggerFormgroupMultiPatch = useCallback((...a) => _impl.current.triggerFormgroupMultiPatch(...a), []);
  const s_applyFaceIdentifyPatch = useCallback((...a) => _impl.current.applyFaceIdentifyPatch(...a), []);
  const s_applyFaceCaptureEnroll = useCallback((...a) => _impl.current.applyFaceCaptureEnroll(...a), []);
  const s_applyReceiptOcrPatch = useCallback((...a) => _impl.current.applyReceiptOcrPatch(...a), []);
  const s_handleFormGroupAPITrigger = useCallback((...a) => _impl.current.handleFormGroupAPITrigger(...a), []);
  const s_handleGrouping = useCallback((...a) => _impl.current.handleGrouping(...a), []);
  const s_ControlFormGroupRadio = useCallback((...a) => _impl.current.ControlFormGroupRadio(...a), []);
  const s_downloadUploadFile = useCallback((...a) => _impl.current.downloadUploadFile(...a), []);
  const s_handleBarcodeScanChange = useCallback((...a) => _impl.current.handleBarcodeScanChange(...a), []);
  const s_handleButtonAPIChanged = useCallback((...a) => _impl.current.handleButtonAPIChanged(...a), []);
  const s_handlenavigatebutton = useCallback((...a) => _impl.current.handlenavigatebutton(...a), []);
  const s_handleAutoPopulate = useCallback((...a) => _impl.current.handleAutoPopulate(...a), []);
  const s_customAVTgstFunction = useCallback((...a) => _impl.current.customAVTgstFunction(...a), []);
  const s_numberFormatting = useCallback((...a) => _impl.current.numberFormatting(...a), []);
  const s_handleKeyDown = useCallback((...a) => _impl.current.handleKeyDown(...a), []);
  const s_handleGridAccordionToggle = useCallback((...a) => _impl.current.handleGridAccordionToggle(...a), []);
  const s_toggle = useCallback((...a) => _impl.current.toggle(...a), []);

  const [isFormEdited, setIsFormEdited] = useState(false);
  // firstApprovalData has no setter and is always null; kept as a plain constant
  // so it doesn't consume a useState slot on every render.
  const firstApprovalData = null;
  const navigateTo = useNavigate();
  const dropdownRef = useRef(null);
  const [subData, setSubData] = useState(null);
  const [name_id, setName] = useState("");
  const [displayName, setDisplayNameState] = useState("");
  // Mirrors displayName synchronously. Async flows (dynamicFormData's API
  // replay on subform load) run inside the previous render's closure, where
  // `displayName` still names the subform being left — syncing controls back
  // by that stale name overwrote the wrong subform (e.g. Medication controls
  // landing in subform 0). Write-backs into allsubFormArray read this ref.
  const displayNameRef = useRef("");
  const setDisplayName = useCallback((name) => {
    displayNameRef.current = name;
    setDisplayNameState(name);
  }, []);
  const [formDisplayName, setFormDisplayName] = useState("");
  const [created, setCreated] = useState("");
  const [selectedForm, setSelectedForm] = useState('');
  const [formList, setFormList] = useState([]);
  const [showNext, setShowNext] = useState(true);
  const [showPrevious, setShowPrevious] = useState(false);
  const [order, setOrder] = useState("");
  const [allsubFormArray, setAllsubformData] = useState([]);
  const [mainarray, setMainArray] = useState([]);
  const [selectedImages, setSelectedImages] = useState({});
  const [selectedSignature, setSelectedSignature] = useState({});
  const [selectedAudio, setSelectedAudio] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState({});
  // ReceiptScanner: resolved image src (S3 URL) for a receipt saved on this
  // submission, keyed by field name — only populated when the field's "Save
  // Scanned Image (S3)" option is on, since that's the only case an S3 key
  // ever landed in item.value.base64 (see processControls' ReceiptScanner
  // branch and applyReceiptOcrPatch below).
  const [selectedReceipt, setSelectedReceipt] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [tempMapValue, setTempMapValue] = useState("");
  // UPIPayment field — the one active payment attempt, if any. Own isolated
  // state, not reusing tempMapValue/useModal (Map's dedicated toggle pair).
  const [upiPaymentModal, setUpiPaymentModal] = useState(null);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const controlref = useRef();
  const isSubmittingRef = useRef(false);
  const { setIsGlobalLoading } = useLoading();
  const { setIsSoftLoading } = useSoftLoading();
  const [selectedImage, setSelectedImage] = useState(null);
  const [signaturePopupUrl, setSignaturePopupUrl] = useState(null);
  const [form, setForm] = useState("");
  const gstBlurTriggeredRef = useRef(false);
  const userData = useMemo(() => decryptData(Cookies.get("userdetails")), []);
  const [submit_button, setSubmitButton] = useState("Save");
  const [next_button, setNextButton] = useState("Next");
  const [buttonArray, setButtonArray] = useState([]);
  const [errorList, setErrorList] = useState([]);
  const [submittedBy, setSubmittedBy] = useState("");
  const [submissionUuid, setUuid] = useState("");
  const [isApproved, setApproved] = useState(false);
  const [isSecondApproved, setSecondApproved] = useState(false);
  const [isPrint, setIsprint] = useState(null);
  const [templatePdfId, setTemplatePdfId] = useState(null);
  const [showButtonShow, setShowButtonShow] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  // showOnlyInEdit / disableOnlyInEdit are module-level constants above — no useState needed.
  const [approveArray, setApproveBtnArray] = useState([]);
  const [rejectArray, setRejectBtnArray] = useState([]);
  // Six boolean modal flags consolidated into one object — reduces the number of
  // useState slots from 6 to 1 and groups conceptually related state together.
  const [modals, setModals] = useState({
    approve: false, reject: false,
    secondApprove: false, secondReject: false,
    delete: false, navigation: false,
  });
  // Aliases keep all existing JSX reads (showApproveModal, etc.) unchanged.
  const {
    approve: showApproveModal, reject: showRejectModal,
    secondApprove: showSecondApproveModal, secondReject: showSecondRejectModal,
    delete: showDeleteModal, navigation: showNavigationModal,
  } = modals;
  const openModal = useCallback((key) => setModals(prev => ({ ...prev, [key]: true })), []);
  const closeModal = useCallback((key) => setModals(prev => ({ ...prev, [key]: false })), []);
  const [dynamicFormId, setDynamicFormId] = useState("");
  const [masterDataSelectionName, setMasterDataSelectionName] = useState("");
  const [selectedApiTag, setSelectedApiTag] = useState("");
  const [showApprovePermission, setApprovePermission] = useState(false);
  const [showSecondApprovePermission, setSecondApprovePermission] = useState(false);
  const [refreshButtonClicked, setRefreshButtonClicked] = useState(false);
  const [isSaveAndNew, setIsSaveAndNew] = useState(false);
  const [saveAndNewButtonClicked, setSaveAndNewButtonClicked] = useState(false);
  const [subFormNewID, setSubFormNewID] = useState({}); // setting id after post in next
  const [subFormID, setSubFormID] = useState({}); // setting id of rendered subform
  const [showModal, setShowModal] = useState(false);
  const [pdfParams, setPdfParams] = useState({ form_id: '', uuid: '', type: '' });
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [temSubformData, setTemSubformData] = useState([]);
  const [parentFormRequired, setParentFormRequired] = useState(false);
  const [subFormSelect, setSubFormSelect] = useState(false);
  const [clickedSubform, setClickedSubform] = useState("");
  const [selectedSubformName, setSelectedSubformName] = useState("");
  const [navigationResponseData, setNavigationResponseData] = useState(null);
  const multiPatchCallCache = useRef(new Map()); // prevents duplicate multipatch api calls — useRef keeps the same Map across renders
  const permissionDataRef = useRef(""); // M-2: was `let` — re-init to "" each render, stale in async .then() callbacks
  const [isTitleOpen, setIsTitleOpen] = useState(false);
  const org_id = useMemo(() => decryptData(Cookies.get("org_id")), []);
  const gridComponentRefs = useRef({});
  const gstValue = watch("gst_no");
  const prevGstRef = useRef(gstValue);
  const baseURL = BASE_API_URL + IMAGE;
  const SignURL = BASE_API_URL + SIGNATURE;
  const AudioURL = BASE_API_URL + AUDIO;
  const hasValidatedOnceRef = useRef(false);

  // Derived values needed by hooks — must be declared before the hook calls.
  const isEditMode = routeParams?.action === "edit";

  // ─── Extracted hooks ────────────────────────────────────────────────────────
  // useFormCalculations owns all 12 calculation engines.
  // useMediaHandlers owns all image/signature/file handlers.
  // formArrayRef is already declared above (used by grid cache too).
  // Internal helpers (recalculate, reAggregationCalculate, equationSolver, etc.)
  // are used only within the hook's own chain — not needed by the component.
  const {
    onChangeCalculation,
    triggerGridSumCalculations,
    triggerTotalCalculation,
    triggerCountValueCalculation,
    triggerAggregationTypeCalculation,
    onChangeDateCalc,
    onChangeTime,
  } = useFormCalculations({ formArrayRef, setArray: setFormArray, getValues, setValue });

  const {
    onclickImage,
    handleImageSelect,
    handleMultiImageSelect,
    handleDeleteMultiImage,
    handleCropImageSelect,
    handleClearCropImage,
    onClickSignature,
    handleSignatureSelect,
    handleAudioCapture,
    handleClearAudio,
    handlePhotoCapture,
    handleClearPhoto,
    handleFileChange,
    clearFile,
    handleClick,
    validateFile,
  } = useMediaHandlers({
    formArrayRef,
    setArray: setFormArray,
    setSelectedImages,
    setSelectedSignature,
    setSelectedAudio,
    setSelectedPhoto,
    setIsFormEdited,
    clearErrors,
    trigger,
    baseURL,
    SignURL,
    isEditMode,
    dataUploadAuthAxios,
    setIsGlobalLoading,
  });
  // ─────────────────────────────────────────────────────────────────────────────

  const location = useLocation();
  const submitted_By = location?.state?.submittedBy;
  const actionID = location.state?.actionID;
  const navigateFrom = location?.state?.from;
  const dashboardActiveTab = location?.state?.dashboardActiveTab;
  const tableUuid = location?.state?.tableUuid;
  const activeGlobalFilters = location?.state?.activeGlobalFilters;
  const { isShowing, toggle } = useModal();

  const { t } = useTranslation();
  const translateLabel = useCallback((label) => {
    const translated = t(label);
    return translated === label ? label : translated;
  }, [t]);

  const togglePopup = useCallback((value, name) => {
    setTempMapValue("");
    const editID = routeParams?.id;
    const wrappedValue = (
      Array.isArray(value) &&
      Array.isArray(value[0]) &&
      value[0][0]?.length !== 2 // not already wrapped
    ) ? [value] : value;
    // NDVIAnalyzer only — Map fields never carry `.ndvi`/`.ndwi`/`.bsi`, so
    // this lookup is a harmless no-op (undefined) for Map and doesn't touch
    // Map's own behavior. `type` is passed too (not just `ndvi`) so the layer
    // switcher can show up for a brand-new NDVIAnalyzer field that hasn't
    // been saved/analyzed yet — gating on `ndvi` alone would hide it until
    // after the first save.
    const field = formArray.find((f) => f.name === name);
    setTempMapValue({
      value: wrappedValue,
      name,
      id: editID,
      ndvi: field?.ndvi,
      ndwi: field?.ndwi,
      bsi: field?.bsi,
      type: field?.type,
    });
    setIsOpen(prev => !prev);
    toggle();
  }, [routeParams?.id, toggle, formArray]);

  const toggleMap = () => {
    const storedMap = JSON.parse(localStorage.getItem("mapValues"));
    if (!storedMap) return;

    const localForm = { formArray };
    const temp = storedMap;

    for (let i = 0; i < localForm.formArray.length; i++) {
      if (
        localForm.formArray[i].type === "Map" &&
        localForm.formArray[i].name === temp.name
      ) {
        const isDeleted =
          temp.value === "" ||
          temp.value === null ||
          (Array.isArray(temp.value) && temp.value.length === 0);

        if (isDeleted) {
          // DELETE CASE (THIS WAS NEVER RUNNING BEFORE)
          localForm.formArray[i].value = "";
          localForm.formArray[i].area = "";

          setValue(localForm.formArray[i].name, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        } else {
          // SELECT CASE
          localForm.formArray[i].value = temp.value;
          localForm.formArray[i].area = temp.area || "";

          setValue(localForm.formArray[i].name, temp.value);
        }

        clearErrors(localForm.formArray[i].name);
        setTempMapValue("");

        // Recalculate dependent fields
        localForm.formArray.forEach(async (field) => {
          if (
            field.calc_rule?.some(rule =>
              rule.list?.includes(temp.name)
            )
          ) {
            await onChangeCalculation(field.calc_rule, localForm.formArray);
          }
        });

        localStorage.removeItem("mapValues");
      }
    }

    // Independent copy of the "Map" loop above, kept isolated from Map — handles
    // the NDVIAnalyzer field's own polygon capture (reuses the same modal/localStorage
    // handoff, but is its own code path so a Map change can never affect NDVI or vice versa).
    for (let i = 0; i < localForm.formArray.length; i++) {
      if (
        localForm.formArray[i].type === "NDVIAnalyzer" &&
        localForm.formArray[i].name === temp.name
      ) {
        const isDeleted =
          temp.value === "" ||
          temp.value === null ||
          (Array.isArray(temp.value) && temp.value.length === 0);

        if (isDeleted) {
          localForm.formArray[i].value = "";
          localForm.formArray[i].area = "";
          // Polygon is gone — any previously computed results are stale.
          delete localForm.formArray[i].ndvi;
          delete localForm.formArray[i].ndwi;
          delete localForm.formArray[i].bsi;

          setValue(localForm.formArray[i].name, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        } else {
          localForm.formArray[i].value = temp.value;
          localForm.formArray[i].area = temp.area || "";
          // Polygon changed shape — the old results no longer apply to it.
          delete localForm.formArray[i].ndvi;
          delete localForm.formArray[i].ndwi;
          delete localForm.formArray[i].bsi;
          // Water/Soil are on-demand — only carry them forward if the user
          // actually fetched them during this map session (saveModal sends
          // whatever was in indexResults at save time, null otherwise).
          if (temp.ndwi) localForm.formArray[i].ndwi = temp.ndwi;
          if (temp.bsi) localForm.formArray[i].bsi = temp.bsi;

          setValue(localForm.formArray[i].name, temp.value);
        }

        clearErrors(localForm.formArray[i].name);
        setTempMapValue("");

        localForm.formArray.forEach(async (field) => {
          if (
            field.calc_rule?.some(rule =>
              rule.list?.includes(temp.name)
            )
          ) {
            await onChangeCalculation(field.calc_rule, localForm.formArray);
          }
        });

        localStorage.removeItem("mapValues");
      }
    }

    // Independent copy of the "Map" loop above, for the Geolocation field's
    // single-point pin (reuses the same modal/localStorage handoff as
    // Map/NDVI, but its own code path since the value shape is a
    // {latitude, longitude} object rather than polygon coordinates).
    for (let i = 0; i < localForm.formArray.length; i++) {
      if (
        localForm.formArray[i].type === "Geolocation" &&
        localForm.formArray[i].name === temp.name
      ) {
        const isDeleted =
          !temp.value ||
          (temp.value.latitude === "" && temp.value.longitude === "");

        if (isDeleted) {
          localForm.formArray[i].value = { latitude: "", longitude: "" };
          setValue(localForm.formArray[i].name, { latitude: "", longitude: "" }, {
            shouldDirty: true,
            shouldValidate: true,
          });
        } else {
          localForm.formArray[i].value = temp.value;
          setValue(localForm.formArray[i].name, temp.value);
        }

        clearErrors(localForm.formArray[i].name);
        setTempMapValue("");

        localForm.formArray.forEach(async (field) => {
          if (
            field.calc_rule?.some(rule =>
              rule.list?.includes(temp.name)
            )
          ) {
            await onChangeCalculation(field.calc_rule, localForm.formArray);
          }
        });

        localStorage.removeItem("mapValues");
      }
    }

    setFormArray([...localForm.formArray]);
  };

  // UPIPayment field flow — order create, mock-simulate / real-Checkout
  // confirm, and commit-into-formArray. Own isolated handlers: doesn't reuse
  // togglePopup/toggleMap's localStorage handoff since UpiPaymentModal is a
  // plain React child (no mapbox instance to preserve across remounts).
  const applyUpiPaymentResult = useCallback((name, result) => {
    setFormArray((prev) => {
      const idx = prev.findIndex((f) => f.name === name);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], paymentResult: result };
      return next;
    });
    setValue(name, result.status === "paid" ? "paid" : "");
    clearErrors(name);
  }, [setValue, clearErrors]);

  const openUpiPayment = useCallback(async (name) => {
    const field = formArray.find((f) => f.name === name);
    if (!field) return;
    const amountField = formArray.find((f) => f.name === field.amountFieldName);
    const amount = Number(amountField?.value);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount in the linked field before paying.");
      return;
    }

    setUpiPaymentModal({
      name,
      label: field.paymentLabel || field.label,
      amount,
      currency: "INR",
      status: "creating",
    });

    try {
      const response = await authAxios.post("/api/v1/payments/order", {
        amount,
        currency: "INR",
        receipt: name,
      });
      const { orderId, providerOrderId, provider, mode, keyId } = response.data;
      setUpiPaymentModal((prev) => (prev && prev.name === name
        ? { ...prev, orderId, providerOrderId, provider, mode, keyId, status: "ready" }
        : prev));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start payment");
      setUpiPaymentModal(null);
    }
  }, [formArray, toast]);

  const pollUpiPaymentStatus = useCallback(async (name, orderId, mode) => {
    const maxAttempts = 20; // ~40s at 2s intervals — real webhook delivery is typically near-instant
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const response = await authAxios.get(`/api/v1/payments/order/${orderId}/status`);
        const { status, amount, providerPaymentId, failureReason } = response.data;
        if (status === "paid") {
          applyUpiPaymentResult(name, { status: "paid", amount, providerPaymentId, mode, provider: "razorpay", orderId });
          setUpiPaymentModal(null);
          return;
        }
        if (status === "failed") {
          setUpiPaymentModal((prev) => (prev ? { ...prev, status: "failed", error: failureReason || "Payment failed" } : prev));
          return;
        }
      } catch {
        // Transient network error while polling — just try again next tick.
      }
    }
    setUpiPaymentModal((prev) => (prev ? { ...prev, status: "failed", error: "Payment confirmation timed out. If money was deducted, contact support before retrying." } : prev));
  }, [applyUpiPaymentResult]);

  const handleUpiSimulate = useCallback(async (outcome) => {
    if (!upiPaymentModal) return;
    const { name, orderId, amount } = upiPaymentModal;
    setUpiPaymentModal((prev) => (prev ? { ...prev, status: "confirming" } : prev));
    try {
      const response = await authAxios.post(`/api/v1/payments/order/${orderId}/simulate`, { outcome });
      if (response.data.status === "paid") {
        applyUpiPaymentResult(name, { status: "paid", amount, mode: "mock", provider: "razorpay", orderId });
        setUpiPaymentModal(null);
      } else {
        setUpiPaymentModal((prev) => (prev ? { ...prev, status: "failed", error: "Simulated failure" } : prev));
      }
    } catch (err) {
      setUpiPaymentModal((prev) => (prev ? { ...prev, status: "failed", error: err?.response?.data?.message || "Simulation failed" } : prev));
    }
  }, [upiPaymentModal, applyUpiPaymentResult]);

  const handleUpiCheckoutSuccess = useCallback(() => {
    if (!upiPaymentModal) return;
    const { name, orderId, mode } = upiPaymentModal;
    setUpiPaymentModal((prev) => (prev ? { ...prev, status: "confirming" } : prev));
    pollUpiPaymentStatus(name, orderId, mode);
  }, [upiPaymentModal, pollUpiPaymentStatus]);

  const handleUpiCheckoutDismiss = useCallback((errorMsg) => {
    setUpiPaymentModal((prev) => {
      if (!prev) return prev;
      // User just closed Checkout without paying — quietly close, no error.
      if (!errorMsg) return null;
      return { ...prev, status: "failed", error: errorMsg };
    });
  }, []);

  const closeUpiPaymentModal = useCallback(() => setUpiPaymentModal(null), []);

  const updateSubformList = (array) => {
    const newArray = array.map(item => ({
      form_name: item.form_name,
      order: item.order,
      _id: item._id,
      isSubmitted: item?.isSubmitted,
    }));
    setFormList([...newArray]);
  }

  // CustomMenuList and MultiSelectMenuList are now module-level components above.
  // ModalIsOpen is passed via selectProps (modal_is_open / ms_modal_is_open).

  const HandleAddData = async (editID) => {
    setIsGlobalLoading(true);

    try {
      const response = await authAxios.get(`/api/v1/forms/getFormForSubmission/${editID}`);
      if (response.status === 200) {
        let response_data = response.data.data[0];
        permissionDataRef.current = response_data?.userPermission;
        if (permissionDataRef.current?.view && !permissionDataRef.current?.addEdit) {
          setViewOnly(true);
        }
        setForm(response_data._id);
        setDisplayName(response_data.subforms[0].form_name);
        setFormDisplayName(response_data.display_name || response_data.form_name || "");
        setAllsubformData(response_data.subforms);
        setShowNext(response_data.subforms.length > 1);
        if (response_data.subforms.length > 1) {
          setSubFormID(response_data?.subforms[0]?._id);
          setSelectedForm(response_data?.subforms[0]?._id);
          setSelectedSubformName(response_data?.subforms[0]?.form_name);
          const newArray = response_data?.subforms.map(item => ({
            form_name: item.form_name,
            order: item.order,
            _id: item._id,
            isSubmitted: item?.isSubmitted,
          }));

          const default_value = response_data.subforms[0]?.controls[0]?.defaultValue;
          if (org_id === AGROECO_ORG_ID && response_data._id === FDSV2_FORMID) {
            setFormList(applyAgroEcoSubformFilter(newArray, default_value));
          } else {
            setFormList(newArray);
          }
        }
        let loadedControls = response_data.subforms[0].controls;

        // Check if any field has isFieldLoadOnPageLoad = true
        const hasPageLoadTrigger = loadedControls.some(
          (f) => f.isFieldLoadOnPageLoad === true
        );

        if (hasPageLoadTrigger) {
          for (let field of loadedControls) {
            // Calculation field
            if (field.calc_rule && field.calc_rule.length > 0) {
              await onChangeCalculation(field.calc_rule, loadedControls);
            }

            // Sum field
            if (field.total_calc) {
              let sumRule = {
                total_calc: field.total_calc,
                total_binding_var: field.total_binding_var,
                total_calc_id: field.total_calc_id,
              };
              await triggerTotalCalculation(sumRule, loadedControls);
            }

            // Aggregation field
            if (field.aggregation_rules) {
              await triggerAggregationTypeCalculation(field.aggregation_rules, loadedControls);
            }

          }
        }
        let filterSelect = response_data.subforms[0].controls;
        const temSubform = structuredClone(response_data.subforms);
        setTemSubformData([...temSubform]);
        setOrder(response_data.subforms[0].order);
        setMainArray(response_data);
        setName(response_data._id);
        setSubData(structuredClone(response_data.subforms));
        // dynamicFormData hides the global loader itself after setFormArray,
        // so the form becomes visible without waiting for API-enrichment loops
        await dynamicFormData(filterSelect, response_data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
      if (error?.response?.data?.statusCode === 403) {
        navigateTo("/dashboard");
      }
    } finally {
      // Guarantee the global loader is always hidden once the load completes,
      // regardless of which enrichment branch ran or whether a helper threw
      // after re-showing the loader. Prevents the spinner from getting stuck.
      setIsGlobalLoading(false);
    }
  }

  const getEditData = async () => {
    userEditedFieldsRef.current = new Set();
    const editID = routeParams?.id;
    const _id = routeParams?.form_id;
    const action = routeParams?.action;
    if (action === "add" && !popup) {
      setIsGlobalLoading(true);
      if (editID) {
        HandleAddData(editID)
      }
    }
    if (action === "edit" && !popup) {
      setIsGlobalLoading(true);
      if (editID) {
        try {
          const [response] = await Promise.all([
            authAxios.get(`/api/v1/formSubmission/${editID}?form_id=${_id}`),
          ]);
          if (response.status === 200) {


            const normalizeControl = (control) => {
              if (control.type === "Container" && Array.isArray(control.controls)) {
                // Recurse into a Container's own children so an ApiField
                // living inside one gets its saved options snapshot restored
                // on edit exactly like a top-level ApiField does.
                return { ...control, controls: normalizeControls(control.controls) };
              }
              if (control.type === "ApiField" && control.sub_type === "select") {
                if (control.copiedSelectOptions && control.copiedSelectOptions.length > 0) {
                  return {
                    ...control,
                    options: control.copiedSelectOptions   // replace options
                  };
                }
              }

              if (control.type === "ApiField" && control.sub_type === "multiSelect") {
                if (control.copiedMultiSelectOptions && control.copiedMultiSelectOptions.length > 0) {
                  return {
                    ...control,
                    options: control.copiedMultiSelectOptions   // replace options
                  };
                }
              }
              return control;
            };

            const normalizeControls = (controls) => controls.map(normalizeControl);

            const normalizeSubforms = (subforms) => {
              return subforms.map(subform => ({
                ...subform,
                controls: normalizeControls(subform.controls)
              }));
            };

            // Usage after GET
            const normalizedData = {
              ...response.data,
              subforms: normalizeSubforms(response.data.subforms)
            };
            // `normalizedData` was previously computed and then discarded —
            // every downstream read in this function used the raw,
            // un-normalized `response.data`, so an ApiField's saved options
            // snapshot (`copiedSelectOptions`/`copiedMultiSelectOptions`)
            // never actually made it back into `.options` on edit, for ANY
            // ApiField (top-level or Container-nested). Reassigning
            // `response.data` here is the one-line fix that makes every
            // subsequent `response.data.*` access in this function see the
            // normalized version, without having to hunt down and rewrite
            // each individual read.
            response.data = normalizedData;
            // Radios come back as labels — hold option.value for every subform so all save paths send values
            radioLabelsToValues(response.data.subforms);
            setCreated(response.data.createdOn);
            setUuid(response.data.uuid);
            setDisplayName(response.data.subforms[0].form_name);
            setFormDisplayName(response.data.display_name || response.data.form_name || "");
            setSubmittedBy(response.data.submittedBy);
            setAllsubformData(response.data.subforms);
            setApproved(response.data.isApproved);
            setSecondApproved(response.data.isSecondApprove);
            setIsprint(response.data?.subforms[0]?.pdfId);
            setTemplatePdfId(response.data?.subforms[0]?.templatePdfId || null);
            permissionDataRef.current = response.data?.userPermission;
            setPdfParams({
              form_id: response.data?.form_id || response.data?._id,
              uuid: response.data?.uuid,
              type: ''
            });
            if (response.data.subforms.length === 1) {
              setShowNext(false);
            } else {
              setShowNext(true);
              let subFormData = {
                _id: response.data?._id,
                form_id: response.data?.form_id,
                uuid: response.data?.uuid,
              }
              setSubFormNewID(subFormData);
              setSubFormID(response.data?.subforms[0]?._id);
              setSelectedForm(response?.data?.subforms[0]?._id);
              setSelectedSubformName(response?.data?.subforms[0]?.form_name);
              const newArray = response?.data?.subforms.map(item => ({
                form_name: item.form_name,
                order: item.order,
                _id: item._id,
                isSubmitted: item?.isSubmitted,
              }));

              const projectValue = response?.data.subforms[0]?.controls[0]?.value;
              if (org_id === AGROECO_ORG_ID && response?.data?.form_id === FDSV2_FORMID) {
                setFormList(applyAgroEcoSubformFilter(newArray, projectValue));
              } else {
                setFormList(newArray);
              }
            }
            if (permissionDataRef.current?.secondApprove) {
              setSecondApprovePermission(true);
            }
            // Delete stays visible to anyone with delete permission before the
            // first approval. Once the form is first-approved, delete locks
            // for everyone UNLESS this form actually has a second-approval
            // stage (secondApprovalPermission) — in which case the second
            // approver may still delete it until they've second-approved it
            // too. Without that form-level check, a single-stage form's
            // isSecondApprove sits at -1 forever (that stage never runs), so
            // anyone holding secondApprove permission kept seeing Delete on
            // every approved form regardless of whether it even has a second
            // stage.
            const canDeleteAtThisStage = response.data.isApproved !== 1
              || (
                response.data.secondApprovalPermission === true
                && !!permissionDataRef.current?.secondApprove
                && response.data.isSecondApprove !== 1
              );
            if (permissionDataRef.current?.delete && canDeleteAtThisStage) {
              setShowDeleteButton(true);
            }
            if (permissionDataRef.current?.approve) {
              setApprovePermission(true);
            }
            if (!permissionDataRef.current?.edit) {
              setViewOnly(true);
            }

            if ('isApproved' in response.data && response.data.isApproved !== -1) {
              setShowButtonShow(true);
            }
            let filterSelect = response.data.subforms[0].controls;
            // ---------- Normalize Map field values for edit response ----------
            // Convert map values that are a single ring (array of points) into
            // the expected array-of-rings form: wrap [ [lng,lat], ... ] => [ [ [lng,lat], ... ] ]
            // Also handle cases where the value is a JSON string and compute area safely.
            if (Array.isArray(response.data?.subforms)) {
              response.data.subforms.forEach((subform) => {
                if (!Array.isArray(subform.controls)) return;

                subform.controls.forEach((control) => {
                  try {


                    // If it's a Map type, normalize shape
                    if (control.type === "Map" && control.value) {
                      // If value is an array and first element looks like a coordinate pair,
                      // it is the single-ring format: wrap it
                      if (
                        Array.isArray(control.value) &&
                        control.value.length > 0 &&
                        Array.isArray(control.value[0]) &&
                        typeof control.value[0][0] === "number"
                      ) {
                        control.value = [control.value]; // wrap into array-of-rings
                      }

                      try {
                        const ring = Array.isArray(control.value) ? control.value[0] : [];
                        if (Array.isArray(ring) && ring.length > 2) {
                          // turf expects polygon as [ [ [lng,lat], ... ] ]
                          const poly = turf.polygon(control.value);
                          const sqm = turf.area(poly); // square meters
                          const acres = Number(sqm / 4047).toFixed(3);
                          control.area = acres;
                        } else {
                          control.area = "";
                        }
                      } catch (errArea) {
                        // if turf fails, just clear area
                        control.area = "";
                      }
                    }
                    // Independent copy of the "Map" normalization above, kept isolated
                    // from Map — NDVIAnalyzer reuses the same polygon capture shape.
                    if (control.type === "NDVIAnalyzer" && control.value) {
                      if (
                        Array.isArray(control.value) &&
                        control.value.length > 0 &&
                        Array.isArray(control.value[0]) &&
                        typeof control.value[0][0] === "number"
                      ) {
                        control.value = [control.value]; // wrap into array-of-rings
                      }

                      try {
                        const ring = Array.isArray(control.value) ? control.value[0] : [];
                        if (Array.isArray(ring) && ring.length > 2) {
                          const poly = turf.polygon(control.value);
                          const sqm = turf.area(poly); // square meters
                          const acres = Number(sqm / 4047).toFixed(3);
                          control.area = acres;
                        } else {
                          control.area = "";
                        }
                      } catch (errArea) {
                        control.area = "";
                      }
                    }
                    // Normalize Geolocation string value
                    if (control.type === "Geolocation" && typeof control.value === "string") {
                      const match = control.value.match(/latitude\s*:\s*([\d.-]+)\s*,\s*longitude\s*:\s*([\d.-]+)/);
                      if (match) {
                        control.value = {
                          latitude: parseFloat(match[1]),
                          longitude: parseFloat(match[2])
                        };
                      } else {
                        control.value = { latitude: "", longitude: "" };
                      }
                    }

                  } catch (err) {
                    // don't break processing if anything unexpected occurs
                  }
                });
              });
            }

            const temSubform = structuredClone(response.data.subforms);
            setTemSubformData([...temSubform])
            // ========
            const controlData = [...filterSelect];
            buildGroupIndices(controlData); // P-11: extracted helper
            // ========

            const filteredData = controlData.filter(
              (item) => item.type === "submit_button"
            );
            if (filteredData.length > 0) {
              setSubmitButton(filteredData[0].label);
              setButtonArray(filteredData);
              setNextButton(filteredData[0].label);
            }
            const approveData = controlData.filter(
              (item) => item.type === "approve_button"
            );
            if (approveData.length > 0) {
              setApproveBtnArray(approveData);
            }
            const rejectData = controlData.filter(
              (item) => item.type === "reject_button"
            );
            if (rejectData.length > 0) {
              setRejectBtnArray(rejectData);
            }
            processControls(controlData, editID);
            processRules(controlData);
            // setFormArray(controlData);
            setFormArray(applyInitialGridVisibility(controlData));
            setGridVisibilityApplied(true);
            setOrder(response.data.subforms[0].order);
            setMainArray(response.data);
            setName(response.data.form_id);
            setIsGlobalLoading(false);
            setTimeout(() => {
              let updatedControls = [...controlData];
              const radioControls = updatedControls.filter(f => f.type === "Radio");

              radioControls.forEach(radio => {
                const selectedValue = getValues(radio.name);

                if (!selectedValue) return;

                // Find the selected label from radio.values
                const selectedOption = radio.values.find(
                  v =>
                    v.value.toString() === selectedValue.toString() ||
                    v.label.toLowerCase() === selectedValue.toString().toLowerCase()
                );
                if (!selectedOption) return;

                // Process form_group_show_rules
                if (radio.form_group_show_rules && radio.form_group_show_rules.length > 0) {
                  radio.form_group_show_rules.forEach(rule => {
                    const conditionMatched =
                      rule.r_condition === "==" &&
                      rule.r_value.toString().toLowerCase() ===
                      selectedOption.label.toString().toLowerCase();

                    // Find all fields belonging to the target form group
                    const targetGroupFields = updatedControls.filter(
                      c => c.group_id === rule.group_parent_id
                    );

                    // Show or hide based on the rule match
                    targetGroupFields.forEach(field => {
                      field.show = conditionMatched ? rule.r_show : false;
                    });
                  });
                }
              });
              // Re-evaluate grid show/hide rules after form values are loaded
              const fieldsWithGridRules = updatedControls.filter(
                field => field?.grid_show_hide_rule && field?.grid_show_hide_rule.length > 0
              );

              fieldsWithGridRules.forEach(field => {
                const currentValue = getValues(field.name);
                if (currentValue !== undefined && currentValue !== null) {
                  updatedControls = handleGridShowHide(field.name, currentValue, updatedControls);
                }
              });
              setFormArray(prev => keepUserEdits(prev, [...updatedControls]));
            }, 300);

            const clonedSubData = structuredClone(response.data.subforms);
            setSubData(clonedSubData); // Set subData here
            setIsFormEdited(false);
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
          setIsGlobalLoading(false);
          if (error?.response?.data?.statusCode === 403) {
            navigateTo("/dashboard");
          }
        }
      }
    }
    if (popup) {
      setIsGlobalLoading(true);
      if (DynamicFormId && !routeParams) {
        HandleAddData(DynamicFormId)
      }
    }
  };

  const dynamicFormData = async (filterSelect, responseData) => {
    userEditedFieldsRef.current = new Set();
     // Home Care - CNA: copy client name from first subform into Medication Management client_name
    const CNA_FORM_ID = "69520684b68b32a97d6128bf";
    if (mainarray?._id === CNA_FORM_ID || mainarray?.form_id === CNA_FORM_ID) {
      const clientCtrl = (allsubFormArray[0]?.controls || []).find(c => c.name === "client");
      const target = filterSelect.find(c => c.name === "client_name");
      if (target && clientCtrl?.value) {
        target.value = clientCtrl.value;
        setValue("client_name", clientCtrl.value);
      }
    }
    await processControls(filterSelect);
    await processRules(filterSelect);
    const submitButton = filterSelect.find(
      (item) => item.type === "submit_button"
    );
    if (submitButton) {
      setSubmitButton(submitButton.label);
      setButtonArray([submitButton]);
      setNextButton(submitButton.label);
    }

    // Show the form immediately after synchronous setup — do not wait for
    // parseURLParams (which can make slow API calls for formgroup_api_bool fields)
    setFormArray(applyInitialGridVisibility(filterSelect));
    setGridVisibilityApplied(true);
    setIsGlobalLoading(false);

    // URL params + kanban prefill run in background; form updates silently after
    const routeNavigationResponseData = location.state?.navigationResponseData || navigationResponseData;
    await parseURLParams(filterSelect, responseData, routeNavigationResponseData);

    // Pre-fill a field when coming from the Kanban Board "Add New" column button
    const prefillField = location.state?.prefillField;
    const prefillValue = location.state?.prefillValue;
    if (prefillField && prefillValue) {
      const ctrl = filterSelect.find(
        c => c.name === prefillField || c.label === prefillField
      );
      if (ctrl) {
        ctrl.value = prefillValue;
        setValue(ctrl.name, prefillValue);
      }
    }

    // Re-apply form array so URL-param and kanban prefill values are visible
    const queryString = window.location.href.split("?")[1];
    const apiRulesHandledDuringQueryPrefill = Boolean(queryString && actionID && !ModalIsOpen && !routeNavigationResponseData);
    setFormArray(prev => keepUserEdits(prev, applyInitialGridVisibility(filterSelect)));
    try {
      if (!apiRulesHandledDuringQueryPrefill) {
        // Flattened for ITERATION (and as handleAPIChanged's target-matching
        // array) only — same-referenced items, so patching a Container child
        // here still mutates the real nested object inside container.controls.
        // Without this, a Container's own api_bool fields (e.g. customer_name
        // driving an ApiField sibling like api_select) are invisible to this
        // replay loop, so their dependent field's options never get
        // refetched on edit-load: the saved value survives (it's never
        // cleared), but with no matching option object the dropdown renders
        // blank even though the value is correct in the submitted payload.
        const flattenedForApiReplay = filterSelect.flatMap((f) =>
          f.type === "Container" && Array.isArray(f.controls) ? [f, ...f.controls] : [f]
        );
        for (const fld of flattenedForApiReplay) {
          const hasValue = fld !== undefined && fld.value !== undefined && fld.value !== null && !(typeof fld.value === 'string' && fld.value.trim() === '');
          if (fld?.api_bool && hasValue) {
            try {
              await handleAPIChanged(fld.name, fld.value, fld.api_rules, flattenedForApiReplay, true);
            } catch (err) {
              toast.error(err?.response?.data?.message || "Failed to load field data. Please refresh.", { autoClose: 3000 });
            }
          }
        }
      }

      const initialMultiPatchKeys = new Set();
      for (const fld of filterSelect) {
        const hasValue = fld !== undefined && fld.value !== undefined && fld.value !== null && !(typeof fld.value === 'string' && fld.value.trim() === '');
        const hasMultiPatchRule = filterSelect.some(ctrl =>
          ctrl.multiPatchRules?.some(rule => rule.patchParameter?.includes(fld.name))
        );
        const triggerKey = `${fld.name}:${JSON.stringify(fld.value)}`;
        if (hasValue && hasMultiPatchRule && !initialMultiPatchKeys.has(triggerKey)) {
          initialMultiPatchKeys.add(triggerKey);
          try {
            await triggerMultiPatchRules(fld.name, fld.value, filterSelect, true);
          } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to apply field rules. Please refresh.", { autoClose: 3000 });
          }
        }
      }
      // The form is already interactive while the replay above runs, so keep
      // anything the user changed meanwhile.
      setFormArray(prev => keepUserEdits(prev, applyInitialGridVisibility(filterSelect)));

      // Trigger grid autopopulate for grids whose form-field params are pre-filled
      // (handles dashboard / action-table navigation where params arrive before onBlur fires)
      try {
        const gridComponents = filterSelect.filter(f =>
          f.type === "GridComponent" &&
          f.gridApiEnabled &&
          Array.isArray(f.gridApi) &&
          f.gridApi.length > 0
        );
        for (const gridField of gridComponents) {
          const apiRule = gridField.gridApi[0];
          const { apiEndpoint, apiParams } = apiRule;
          if (!apiEndpoint || !Array.isArray(apiParams) || apiParams.length === 0) continue;

          const queryParams = {};
          let allFilled = true;
          for (const param of apiParams) {
            const f = filterSelect.find(x => x.name === param);
            const value = f?.value;
            if (value === undefined || value === null || value === '') { allFilled = false; break; }
            queryParams[param] = value;
          }

          if (allFilled) {
            try {
              const apiUrl = normalizeApiPath(apiEndpoint);
              const queryString = serializeParamsRFC3986(queryParams);
              const fullUrl = queryString ? `${apiUrl}?${queryString}` : apiUrl;
              const response = await authAxios.get(fullUrl);
              if (response.status === 200 && response.data) {
                setFormArray(prevArray =>
                  prevArray.map(f =>
                    f.name === gridField.name
                      ? { ...f, gridRows: response?.data?.data || [] }
                      : f
                  )
                );
              }
            } catch (err) {
              toast.error(err?.response?.data?.message || "Failed to load grid data. Please refresh.", { autoClose: 3000 });
            }
          }
        }
      } catch (err) {
        toast.error("Failed to initialize grid data. Please refresh.", { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load form data. Please refresh.", { autoClose: 3000 });
    }
  };

  const parseURLParams = async (filterSelect, responseData, navigationResponseData) => {
    const currentUrl = window.location.href;
    const queryString = currentUrl.split("?")[1];

    // --- Navigation-response branch: patch navigationResponseData into filterSelect ---
    if (navigationResponseData) {
      const data = navigationResponseData?.Data || navigationResponseData;

      // STEP-A: Patch top-level non-array fields (unchanged)
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) return;
        const match = filterSelect.find(
          obj =>
            obj.name?.toLowerCase() === key.toLowerCase() ||
            obj.label?.toLowerCase() === key.toLowerCase()
        );
        if (match) {
          match.value = value;
          setValue(match.name, value);
        }
      });

      // STEP-B: Process all formgroups
      Object.entries(data).forEach(([groupKey, groupData]) => {
        if (!Array.isArray(groupData) || groupData.length === 0) return;

        const formGroupName = groupKey
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .trim()
          .toLowerCase();

        const formGroupId = filterSelect.find(f => {
          const normalizedGroupName = f.group_name?.replace(/\s+/g, "").toLowerCase();
          const normalizedKey = groupKey.replace(/\s+/g, "").toLowerCase();
          return normalizedGroupName === normalizedKey;
        })?.group_id;

        if (!formGroupId) {
          return;
        }

        // collect base fields for this group
        const baseFields = filterSelect.filter(f => f.group_id === formGroupId);
        const existingSets = [...new Set(baseFields.map(f => f.group_parent_count || 0))];
        const existingCount = existingSets.length;
        const requiredCount = groupData.length;

        // find insertion point
        const lastGroupFieldIndex = filterSelect.reduce(
          (maxIndex, field, i) => (field.group_id === formGroupId ? i : maxIndex),
          -1
        );

        // CLONE missing sets if required
        if (requiredCount > existingCount) {
          const setsToAdd = requiredCount - existingCount;

          // find last numeric suffix used (0 if none)
          for (let i = 1; i <= setsToAdd; i++) {
            const newSetNumber = existingCount + i;

            const groupUUID = crypto.randomUUID();

            const newSet = baseFields.map((f, index, arr) => {
              const isFirst = index === 0;
              const isLast = index === arr.length - 1;

              return {
                ...f,
                id: `${groupUUID}_${index}`,
                name: `${f.name}_${newSetNumber}`,
                value: "",
                group_parent_count: newSetNumber,
                group_parent: false,
                group_first: isFirst,
                group_last: isLast,
                add_new: false,
                addFgArrayIndex: newSetNumber,
                FGsubmissionIndex: isFirst ? newSetNumber + 1 : undefined,
              };
            });

            filterSelect.splice(lastGroupFieldIndex + 1, 0, ...newSet);

          }
        }

        // PATCH values into the group fields
        groupData.forEach((itemSet, setIndex) => {
          Object.entries(itemSet).forEach(([fieldName, fieldValue]) => {
            const cleanFieldName = fieldName.replace(/_\d+$/, "");
            const matches = filterSelect.filter(
              f => f.group_id === formGroupId && f.name.startsWith(cleanFieldName)
            );

            const targetField = matches[setIndex];
            if (!targetField) {
              return;
            }

            // handle selects / multiselects
            let finalValue = fieldValue;
            if (targetField.type === "Select" || targetField.type === "MultiSelect") {
              if (fieldValue !== null && fieldValue !== undefined) {
                fieldValue = String(fieldValue);
              }
              const options = targetField.options?.length ? targetField.options : targetField.filterArray || [];

              if (Array.isArray(fieldValue) && targetField.type === "MultiSelect") {
                finalValue = fieldValue.map(val => {
                  const match = options.find(
                    opt =>
                      String(opt.value).toLowerCase() === String(val).toLowerCase() ||
                      String(opt.label).toLowerCase() === String(val).toLowerCase()
                  );
                  return (match && match.value) || val;
                }).filter(Boolean);
              } else {
                const match = options.find(
                  opt =>
                    String(opt.value).toLowerCase() === String(fieldValue).toLowerCase() ||
                    String(opt.label).toLowerCase() === String(fieldValue).toLowerCase()
                );
                if (match) finalValue = match.value;
              }
            }

            // set the value (preserve if already set earlier)
            targetField.value = finalValue;
            setValue(targetField.name, finalValue);
          });
        });

        // STEP: Ensure only the last set of *this group* has add_new = true (last child's last field)
        // STEP: Ensure only the last set of *this group* has add_new = true (last child's last field)
        const allGroupFields = filterSelect
          .filter(f => f.group_id === formGroupId)
          .sort((a, b) => (a.group_parent_count || 0) - (b.group_parent_count || 0));
        const lastSetNumber = Math.max(...allGroupFields.map(f => f.group_parent_count || 0));
        const newSetNumber = lastSetNumber + 1;

        let numbering = 1;
        // normalize parent_count
        allGroupFields.forEach(f => {
          if (f.group_first) {
            f.group_array_index = numbering;      // UI uses this
            f.FGsubmissionIndex = numbering;      // Your logic uses this
            numbering++;
          }
        });

        // FIX: Sort group fields in correct order before flagging add_new
        allGroupFields.sort((a, b) => {
          if (a.group_parent_count !== b.group_parent_count) {
            return a.group_parent_count - b.group_parent_count;
          }
          return (a.group_order || a.order || 0) - (b.group_order || b.order || 0);
        });

        // Replace the unsorted portion of filterSelect with sorted fields
        const nonGroup = filterSelect.filter(f => f.group_id !== formGroupId);
        filterSelect.length = 0;
        filterSelect.push(...nonGroup, ...allGroupFields);

        const maxParentCount = Math.max(...allGroupFields.map(f => f.group_parent_count || 0));
        // reset add_new first
        allGroupFields.forEach(f => (f.add_new = false));

        // find last set fields, then mark the last field (group_last true) within it as add_new
        const lastSetFields = allGroupFields.filter(f => f.group_parent_count === maxParentCount);
        if (lastSetFields.length > 0) {
          // ensure consistent ordering (by group_order or order)
          lastSetFields.sort((a, b) => (a.group_order || a.order || 0) - (b.group_order || b.order || 0));
          const lastField = lastSetFields[lastSetFields.length - 1];
          if (lastField) lastField.add_new = true;
        }
      });
      return; // end navigation branch
    }


    // STEP 2: Normal URL Query Params
    if (!queryString) return;
    const params = new URLSearchParams(queryString);
    const dataArray = Array.from(params.entries()).map(([name, value]) => ({ name, value }));

    dataArray.forEach(({ name, value }) => {
      const matchingObject = filterSelect.find((obj) => obj.name === name);
      if (matchingObject) {
        if (matchingObject.type === "FileUpload") {
          matchingObject.value = value;
          const queryFileName = value?.slice(value?.indexOf("-") + 1);
          matchingObject.fileName = queryFileName;
          setValue(matchingObject.name, value);
        } else {
          matchingObject.value = value;
          setValue(matchingObject.name, value);
        }
      }
    });

    // 2B: Existing API field rules + form group rules
    if (actionID && !ModalIsOpen) {
      let updatedFilterSelect = [...filterSelect];
      // Flattened for ITERATION and target-matching only — same-referenced
      // items, so mutating a match here still mutates the real nested object
      // inside a Container's own `.controls`. Without this, a Container's
      // api_bool field (e.g. customer_name driving an ApiField sibling like
      // api_select) is invisible to this replay, so the sibling's options
      // never get refetched here either — mirrors the same fix already
      // applied to the initial-load replay loop above (dynamicFormData).
      const flattenedForApiReplay = updatedFilterSelect.flatMap((f) =>
        f.type === "Container" && Array.isArray(f.controls) ? [f, ...f.controls] : [f]
      );
      for (const item of flattenedForApiReplay) {  // P-7: was forEach(async) — promises were silently dropped
        // ---- API FIELD RULES ----
        if (item?.api_bool && item?.api_rules?.length > 0) {
          let tempApiRules = item.api_rules;
          for (let n = 0; n < tempApiRules.length; n++) {
            let main_url = resolveRuleUrl(tempApiRules[n]);
            if (item?.value) {
              if (tempApiRules[n].fieldwithAt.length > 0) {
                for (let i = 0; i < tempApiRules[n].fieldwithAt.length; i++) {
                  const value = getValues(tempApiRules[n].fieldwithAt[i]);
                  main_url = main_url.replace("@" + tempApiRules[n].fieldwithAt[i], value);
                }

                try {
                  const api_result = await apiFieldAxios.get(main_url);
                  if (api_result.data.statusCode === 200) {
                    const { type, options, value } = api_result.data;
                    if (type === "select" || type === "AdvancedSelect") {
                      const targetField = flattenedForApiReplay.find(
                        (field) =>
                          field.name === tempApiRules[n].binding_variable &&
                          (field.sub_type === "select" || field.type === "AdvancedSelect")
                      );
                      if (targetField) {
                        const filteredOptions = options.filter((o) => o.value !== null);
                        targetField.options = filteredOptions;
                        targetField.filterArray = filteredOptions;
                        if (value) {
                          targetField.value = value;
                          setValue(tempApiRules[n].binding_variable, value);
                        }
                      }
                      setFormArray(applyInitialGridVisibility(updatedFilterSelect));
                    } else {
                      setValue(tempApiRules[n].binding_variable, value);
                      const targetField = flattenedForApiReplay.find(
                        (field) => field.name === tempApiRules[n].binding_variable
                      );
                      if (targetField) {
                        targetField.value = value;
                      }
                      setFormArray(applyInitialGridVisibility(updatedFilterSelect));
                    }
                  } else {
                    toast.error("Failed to fetch data, Please contact admin.");
                  }
                } catch (error) {
                  toast.error(error.message || "Failed to fetch data.");
                }
              }
            }
          }
        }

        // ---- FORM GROUP RULES ----
        if (routeParams?.id !== "662213fe246b384abbfde506") {
          if (item?.formgroup_api_bool === true && item?.formgroup_api_rules.length > 0 && item.value) {
            let controlArray = [];
            let controlSecondArray = {};
            let parent_id = "";
            let last_index = "";
            let rules = item?.formgroup_api_rules;

            try {
              // Declared outside the loop so the value from the last iteration
              // is accessible in the subsequent for-i loop below.
              let parent_child_data;
              for (let n = 0; n < rules.length; n++) {
                const main_url = resolveRuleUrl(rules[n]);
                parent_child_data = await apiFieldAxios.get(BASE_API_URL + "/api/v1/formGroup/" + rules[n].group_id + "?submissionDuplicate=true");
                if (item.value && rules[n].fieldwithAt) {
                  const new_url = main_url.replace(`@${rules[n].fieldwithAt}`, getValues(rules[n].fieldwithAt));
                  try {
                    const api_result = await apiFieldAxios.get(new_url);
                    if (api_result.data.statusCode === 200) {
                      controlSecondArray = api_result.data.formGroupValues;
                    }
                  } catch (error) {
                    toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
                  }
                }
              }

              for (let i = 0; i < controlSecondArray.length - 1; i++) {
                const parent_data = structuredClone(parent_child_data.data.data.controls);
                if (parent_data.length > 0) {
                  parent_id = parent_data[0].group_id;
                }
                // Sync api_rules from the current form's template row into parent_data so that
                // outside-group params (e.g. sale_order_number) are present in fieldwithAt/url
                // before stateUpdate applies per-row suffixing.  Same pattern as handleGrouping.
                const templateFields = updatedFilterSelect.filter(f => f?.group_id === parent_id && !f?.id);
                for (const tmplField of templateFields) {
                  const pd = parent_data.find(f => f.name === tmplField.name);
                  if (pd) {
                    pd.api_rules = structuredClone(tmplField.api_rules);
                  }
                }
                const new_data = updatedFilterSelect.filter((data) => data.group_parent == true && data.group_id === parent_id);
                const last_finder = updatedFilterSelect.filter((data) => data.group_last === true && data.group_id === parent_id);
                const last_index_array = last_finder.slice(-1)[0];
                let name = last_index_array.name;
                last_index = updatedFilterSelect.findIndex((x) => x.name === name);
                if (new_data.length > 0) {
                  new_data[0].group_parent_count = new_data[0].group_parent_count + 1;
                  new_data[0].group_array_index = new_data[0].group_array_index ? new_data[0].group_array_index + 1 : 2;
                }
                const tempArray = await stateUpdate(parent_data, new_data);
                controlArray.push(tempArray);
              }
              const updatedArray = controlArray.map((item, index) => {
                if (index === controlArray.length - 1) {
                  return item;
                } else {
                  if (item && item.length > 0) {
                    item[item.length - 1] = {
                      ...item[item.length - 1],
                      add_new: false,
                    };
                  }
                  return item;
                }
              });
              const combinedArray = updatedArray.reduce((result, currentArray) => [...result, ...currentArray], []);
              const indexToMerge = last_index + 1;
              const newArray = [
                ...updatedFilterSelect.slice(0, indexToMerge),
                ...combinedArray,
                ...updatedFilterSelect.slice(indexToMerge),
              ];
              let hide_parent_show = newArray.filter((data) => data.parent_last === true && data.group_id === parent_id);
              if (hide_parent_show.length > 0) {
                hide_parent_show[0].add_new = false;
              }
              for (let i = 0; i < controlSecondArray.length; i++) {
                let tempVar = controlSecondArray[i];
                const keysToCheck = Object.keys(tempVar);
                updateValuesInArray(keysToCheck, newArray, tempVar);
              }
              if (newArray.length > 0) {
                for (let i = 0; i < newArray.length; i++) {
                  if (
                    newArray[i].type === "Text" ||
                    newArray[i].type === "Select" ||
                    newArray[i].type === "Time" ||
                    newArray[i].type === "Date"
                  ) {
                    if (newArray[i].api_bool && newArray[i].value !== '' && (newArray[i].group_id !== undefined && newArray[i].group_id !== '')) {
                      await handleAPIChanged(newArray[i].name, newArray[i].value, newArray[i].api_rules, newArray);
                    }
                  } else if (newArray[i].type === "Number") {
                    await onChangeCalculation(newArray[i]?.calc_rule, newArray);
                    if (newArray[i]?.sumRule) {
                      await triggerTotalCalculation(newArray[i]?.sumRule, newArray);
                    }
                    if (newArray[i]?.aggregationRule) {
                      await triggerAggregationTypeCalculation(newArray[i]?.aggregationRule, newArray);
                    }
                  }
                }
              }
              updatedFilterSelect = newArray;
              setFormArray([...newArray]); // This should ensure filterSelect is updated
              if (responseData) {
                const updatedSubforms = [...responseData.subforms];
                updatedSubforms[0].controls = updatedFilterSelect;
                setAllsubformData(updatedSubforms);
              }
            } catch (error) {
              toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
            }
          }
        }
      }
    }
  };

  const applyFormGroupShowRules = (formArray, getValues, setValue) => {
    const radioFields = formArray.filter(f => f.type === "Radio" && f.form_group_show_rules?.length > 0);

    radioFields.forEach(radio => {
      const selectedValue = getValues(radio.name);

      if (!selectedValue) return;

      // Find the selected option
      const selectedOption = radio.values.find(
        v => String(v.value) === String(selectedValue) ||
          String(v.label).toLowerCase() === String(selectedValue).toLowerCase()
      );

      if (!selectedOption || !radio.form_group_show_rules) return;

      // Apply each rule
      radio.form_group_show_rules.forEach(rule => {
        const conditionMatched =
          rule.r_condition === "==" &&
          String(rule.r_value).toLowerCase() === String(selectedOption.label).toLowerCase();

        // Find all fields in the target group
        const targetGroupFields = formArray.filter(
          c => c.group_id === rule.group_parent_id
        );

        // Update show property for all fields in the group
        targetGroupFields.forEach(field => {
          field.show = conditionMatched ? rule.r_show : false;

          // If hiding and field has a value, clear it
          if (!field.show && field.value) {
            field.value = "";
            setValue(field.name, "");
          }
        });
      });
    });

    return formArray;
  };

  // function for updating basic controls
  const processControls = async (filterSelect, editID) => {

    // Flattened for ITERATION only — `filterSelect` itself (and every
    // Container's own `.controls`) is left structurally untouched, so this
    // never duplicates a field in the eventual `formArray`. Every branch
    // below mutates `item` in place, so running it against a Container's
    // children too (previously invisible to this loop entirely — a
    // Container's own `type` never matched any branch here) gives them the
    // exact same init a top-level field already gets: Select/ApiField master
    // data options+value coercion, Checkbox reconstruction, Number
    // formatting, image/audio URL resolution, etc.
    const flattenedFilterSelect = filterSelect.flatMap((f) =>
      f.type === "Container" && Array.isArray(f.controls) ? [f, ...f.controls] : [f]
    );

    flattenedFilterSelect.forEach((item) => {
      if (!editID) {
        if (
          (item?.value === "" || item?.value === undefined || item?.value === null) &&
          item?.defaultValue !== undefined &&
          item?.defaultValue !== null &&
          item?.defaultValue !== ""
        ) {
          item.value = item.defaultValue;
          setValue(item.name, item.defaultValue);
        } else {
          item.value = item.value ?? "";
          setValue(item.name, item.value);
        }
      } else {
        item.value = item.value !== undefined && item.value !== null ? item.value : "";
        setValue(item.name, item.value);
      }
      if ((item.type === "Signature" || item.type === "Image") && item.value === "N/A") {
        item.value = "";
        setValue(item.name, item.value);
      }

      if (item.type === "Number" && item.value !== "") {
        item.value = item.value.toString();

        if (item.hasOwnProperty("isDecimalPoint") && item.hasOwnProperty("isThousandSeparator")) {
          if (item.isThousandSeparator && item.isDecimalPoint) {
            const safeValue = typeof item.value === "string" ? item.value : String(item.value || "");
            const numberValue = parseFloat(safeValue.replace(/,/g, ''));
            if (!isNaN(numberValue)) {
              const formattedValue = numberValue.toLocaleString(undefined, { minimumFractionDigits: item.hasOwnProperty("decimalValue") ? item.decimalValue : 2, });
              item.value = formattedValue;
              setValue(item.name, formattedValue);
            }
          } else if (item.isThousandSeparator) {
            const numberValue = parseFloat(item.value.replace(/,/g, ''));
            if (!isNaN(numberValue)) {
              const formattedValue = numberValue.toLocaleString(undefined, {
                minimumFractionDigits: 0,
              });
              item.value = formattedValue;
              setValue(item.name, formattedValue);
            }
          } else if (item.isDecimalPoint) {
            setValue(item.name, item.value);
          } else {
            setValue(item.name, item.value);
          }
        }
      }

      if (item.type === "Map") {
        // Ensure coordinates are always an array
        if (!Array.isArray(item.value)) {
          item.value = [];
        }
        setValue(item.name, item.value);
        // Calculate area if polygon exists
        try {
          if (Array.isArray(item.value) && item.value.length > 0) {
            const polygon = turf.polygon(item.value);
            const sqm = turf.area(polygon);
            const acres = Number(sqm / 4047).toFixed(3);
            item.area = acres;
          } else {
            item.area = "";
          }
        } catch (err) {
          item.area = "";
        }
      }
      // Independent copy of the "Map" block above, kept isolated from Map.
      if (item.type === "NDVIAnalyzer") {
        if (!Array.isArray(item.value)) {
          item.value = [];
        }
        setValue(item.name, item.value);
        try {
          if (Array.isArray(item.value) && item.value.length > 0) {
            const polygon = turf.polygon(item.value);
            const sqm = turf.area(polygon);
            const acres = Number(sqm / 4047).toFixed(3);
            item.area = acres;
          } else {
            item.area = "";
          }
        } catch (err) {
          item.area = "";
        }
      }
      if (item.type === "Checkbox" && item.value) {
        item.value = item.value.toString();
        const selectedValues = item.value.split(",").map(v => v.trim());

        item.checkvalues = item.checkvalues.map(option => ({
          ...option,
          checked: selectedValues.includes(option.label)
        }));
        setValue(item.name, selectedValues);
      }
      if (item.type === "Select") {
        item.value = item.value.toString();
      }
      if (item.type === "Select" && Array.isArray(item.options)) {
        item.options = item.options.map(opt => ({
          ...opt,
          value: String(opt.value) // convert number → string
        }));
        // Also ensure current selected value is string
        if (item.value !== "" && item.value !== undefined) {
          item.value = String(item.value);
          setValue(item.name, item.value);
        }
      }

      if (item.type === "CalculatedField" && item.value) {
        item.value = String(item.value);
      }

      if (item.type === "Map") {
        // Ensure coordinates are always an array
        if (!Array.isArray(item.value)) {
          item.value = [];
        }
        setValue(item.name, item.value);
      }
      // Independent copy of the "Map" block above, kept isolated from Map.
      if (item.type === "NDVIAnalyzer") {
        if (!Array.isArray(item.value)) {
          item.value = [];
        }
        setValue(item.name, item.value);
      }

      if (item.type === "Radio") {
        let selectedValue = item.value;
        // If empty, use defaultValue
        if (!selectedValue && item.defaultValue) {
          selectedValue = item.defaultValue;
        }

        // Normalize: If the value is a label instead of number
        const matchedOption = item.values.find(
          opt =>
            opt.value.toString() === selectedValue?.toString() ||
            opt.label.toLowerCase() === selectedValue?.toString().toLowerCase()
        );
        if (matchedOption) {
          item.value = matchedOption.value;
          setValue(item.name, matchedOption.value);
        } else {
          item.value = "";
          setValue(item.name, "");
        }
      }

      if (item.type === "FileUpload" && item.value) {
        const rawFile = item.value;
        item.originalValue = rawFile;
        const FileName = rawFile.includes('-')
          ? rawFile.split('-').slice(1).join('-').trim()
          : rawFile;
        item.fileName = FileName;
        setValue(item.name, rawFile);
      }

      if (item.type === "MultiSelect" || (item.type === "ApiField" && item.sub_type === "multiSelect")) {
        item.filterArray = item.options;
        setValue(item.name, item.value || []);
      }

      if (item.type === "AdvancedSelect") {
        if (Array.isArray(item.options) && item.options.length > 0) {
          item.filterArray = item.options;
        }
        if (item.value !== undefined && item.value !== null && item.value !== "") {
          item.value = String(item.value);
          setValue(item.name, String(item.value));
        } else if (item.validators?.required?.value === true && item.filterArray?.length === 1) {
          // Required field with only one option available — auto-select it
          item.value = String(item.filterArray[0].value);
          setValue(item.name, item.value);
        } else {
          setValue(item.name, "");
        }
      }

      if (item.type === "Select" || item.type === "ApiField") {
        if (item.type === "Select" || (item.type === "ApiField" && item.sub_type === "select")) {
          item.filterArray = item.options;
          if (item.value !== "" && item.value !== undefined) {
            // The server's save-time optimization (formSubmissionOptimization,
            // formSubmission.js) replaces a Select/ApiField-select's stored
            // `value` with its OPTION LABEL, not the raw value — so on
            // reload here, `item.value` is actually a label ("Banana"), not
            // the value ("banana") the options list is keyed by. Matching by
            // value only silently fails whenever label !== value (exactly
            // the case for an ApiField whose display label differs from its
            // raw option value — a plain master-data Select rarely notices
            // this because its label and value are often set equal). Falling
            // back to a label match resolves it back to the canonical value.
            const matchedOption = item.options.find(opt => opt.value === item.value)
              || item.options.find(opt => opt.label === item.value);
            if (matchedOption) {
              item.value = matchedOption.value;
              setValue(item.name, matchedOption.value);
            } else {
              setValue(item.name, "");
            }
          } else if (item.defaultValue) {
            setValue(item.name, item.defaultValue);
          } else if (item.validators?.required?.value === true && item.filterArray?.length === 1) {
            // Required field with only one option available — auto-select it
            item.value = item.filterArray[0].value;
            setValue(item.name, item.value);
          } else {
            setValue(item.name, "");
          }
        }
        if (item.type === "ApiField" && !item.hasOwnProperty("sub_type")) {
          item.sub_type = "text";
        }
        // Force ApiField text value to always be a string
        if (item.type === "ApiField" && item.sub_type === "text") {
          if (item.value !== undefined && item.value !== null) {
            item.value = String(item.value);   // Convert to string
            setValue(item.name, String(item.value));
          } else {
            item.value = "";
            setValue(item.name, "");
          }
        }
      }

      item.hidden ??= false;
      item.isFutureDateDisable ??= false;
      item.isPastDateDisable ??= false;

      if (
        item?.isCurrentDate &&
        item.type === "Date" &&
        item.defaultValue === "" &&
        item.value === ""
      ) {
        item.value = formatDateYMD(new Date());
        setValue(item.name, formatDateYMD(new Date()));
      }

      if (
        item?.isCurrentTime &&
        item.type === "Time" &&
        item.defaultValue === "" &&
        item.value === ""
      ) {
        item.value = nowHHmm();
        setValue(item.name, nowHHmm());
      }

      if (
        item?.type === "CalculatedField" ||
        item?.type === "SumField" ||
        item?.type === "AggregationField"
      ) {
        item.value = item.value.toString();
        if (item?.dummyAggregationRules?.aggregationType === "CountValue") {
          setValue(item?.name, Number(item.value));
        }
        else if (item?.dummycalcrules?.fieldTypes === "time") {
          setValue(item?.name, item.value);
        }

        else {
          const numberValue = parseFloat(item?.value?.replace(/,/g, "")); // Convert the string to a number
          if (!isNaN(numberValue)) {
            const decimalPlaces = item.hasOwnProperty("decimalValue") ? item.decimalValue : 2; // Use item.decimalValue
            const formattedValue = numberValue.toLocaleString(undefined, {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces, // Ensure it doesn't exceed the desired decimal places
            });
            item.value = formattedValue;
            setValue(item?.name, formattedValue);
          }
        }
      }

      const isEditMode = routeParams?.action !== "add" || saveAndNewButtonClicked;
      if (item?.type === "Image" || item?.type === "Signature") {
        const fieldName = item?.name;
        const base64Value = item?.value?.base64;
        const hasValue = item?.value !== "";

        if (hasValue) {
          if (isEditMode && base64Value?.length <= 36) {
            // reference URL branch
            const prefixUrl = (item.type === "Image") ? baseURL : SignURL;
            (item.type === "Image" ? setSelectedImages : setSelectedSignature)(prev => ({
              ...prev,
              [fieldName]: prefixUrl + base64Value
            }));
          } else {
            // full base64 data URI branch
            (item.type === "Image" ? setSelectedImages : setSelectedSignature)(prev => ({
              ...prev,
              [fieldName]: `data:image/jpeg;base64,${base64Value}`
            }));
          }
        }
      }
      if (item?.type === "MultiImage") {

        const fieldName = item?.name;
        const imageArray = item?.value;

        if (Array.isArray(imageArray) && imageArray.length > 0) {
          const images = imageArray.map((img) => {
            // handle both structures
            const base64Value =
              img?.base64?.base64 || img?.base64;
            if (!base64Value) return null;
            if (isEditMode && base64Value?.length <= 36) {
              // UUID reference
              return baseURL + base64Value;
            } else {
              // actual base64
              return `data:image/jpeg;base64,${base64Value}`;
            }
          }).filter(Boolean);

          setSelectedImages((prev) => ({
            ...prev,
            [fieldName]: images
          }));
        }
      }
      if (item?.type === "CropHealthAnalyzer") {
        const fieldName = item?.name;
        // Only ever one image now, but tolerate legacy array-valued
        // submissions saved before this field became single-image.
        const entry = Array.isArray(item?.value) ? item.value[0] : item?.value;
        const base64Value = entry?.base64?.base64 || entry?.base64;

        if (base64Value) {
          const src = (isEditMode && base64Value.length <= 36)
            ? baseURL + base64Value
            : `data:image/jpeg;base64,${base64Value}`;
          setSelectedImages((prev) => ({ ...prev, [fieldName]: src }));
        }
      }
      if (item?.type === "AudioCapture") {
        const fieldName = item?.name;
        const base64Value = item?.value?.base64;
        const mimeType = item?.value?.mimeType || "audio/webm";

        if (base64Value) {
          const src = (isEditMode && base64Value.length <= 36 && !base64Value.startsWith("data:"))
            ? `${AudioURL}${base64Value}?type=${encodeURIComponent(mimeType)}`
            : `data:${mimeType};base64,${base64Value}`;
          setSelectedAudio((prev) => ({ ...prev, [fieldName]: src }));
        }
      }
      if (item?.type === "PhotoCapture") {
        const fieldName = item?.name;
        const base64Value = item?.value?.base64;

        if (base64Value) {
          const src = (isEditMode && base64Value.length <= 36 && !base64Value.startsWith("data:"))
            ? baseURL + base64Value
            : `data:image/jpeg;base64,${base64Value}`;
          setSelectedPhoto((prev) => ({ ...prev, [fieldName]: src }));
        }
      }
      if (item?.type === "ReceiptScanner") {
        const fieldName = item?.name;
        const base64Value = item?.value?.base64;

        if (base64Value) {
          const src = (isEditMode && base64Value.length <= 36 && !base64Value.startsWith("data:"))
            ? baseURL + base64Value
            : `data:image/jpeg;base64,${base64Value}`;
          setSelectedReceipt((prev) => ({ ...prev, [fieldName]: src }));
        }
      }
      // NDVIAnalyzer's rendered image(s) get offloaded to an S3 key at save
      // time (formSubmissionOptimization) — resolve each back to a full URL
      // here, mutating in place since FormFieldRenderer reads them directly
      // off the control (`field.ndvi.image`/`.ndwi.image`/`.bsi.image`), not
      // through selectedImages state. A short key (<=36 chars, no "data:"
      // prefix) means it's been uploaded; anything else (a full data URI)
      // hasn't been yet — e.g. the mock path, which is never uploaded — and
      // is left untouched. ndwi/bsi are optional (only present if the user
      // fetched them on-demand before saving).
      if (item?.type === "NDVIAnalyzer") {
        ["ndvi", "ndwi", "bsi"].forEach((key) => {
          const rawImage = item?.[key]?.image;
          if (isEditMode && typeof rawImage === "string" && rawImage.length <= 36 && !rawImage.startsWith("data:")) {
            item[key].image = baseURL + rawImage;
          }
        });
      }

      if (item.total_calc) {
        item.sumRule = {
          total_calc: item.total_calc,
          total_binding_var: item.total_binding_var,
          total_calc_id: item.total_calc_id,
        };
      }

      // -----Aggregation-----
      if (item.aggregation_flag) {
        item.aggregationRule = item.aggregation_rules;
        // item.aggregationRule.forEach((rule) => {rule.group_id = item.group_id;});
      }

      if (item?.dummyAggregationRules?.aggregationType === "CountValue") {
        triggerCountValueCalculation(filterSelect)
      }
      // ---------------------
    });

    applyFormGroupShowRules(filterSelect, getValues, setValue);
    // Callers (dynamicFormData, getEditData) call setFormArray with
    // applyInitialGridVisibility immediately after — no intermediate render needed.
  };

  // function for updating basic rules on initial time
  const processRules = async (filterSelect) => {
    // Same container-flatten as processControls above — a show/hide rule
    // owned by (or targeting, via r_field/parent_field) a field nested inside
    // a Container was previously invisible on both sides of every
    // `filterSelect.find(...)` lookup below, since a flat `filterSelect`
    // never descended into `.controls`. Purely a local iteration/lookup
    // source — `filterSelect` itself is never restructured or written back.
    filterSelect = filterSelect.flatMap((f) =>
      f.type === "Container" && Array.isArray(f.controls) ? [f, ...f.controls] : [f]
    );
    filterSelect.forEach((item) => {
      if (item.rules !== undefined) {
        if (item.rules.length > 0) {
          if (item.type === "Number" || item.type === "Text" || item.type === "Email" || item.type === "phoneNumber") {
            const tempRules = item.rules;
            tempRules.forEach((rule) => {
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItem = filterSelect.find((itemInArray) => itemInArray.name === rule.r_field);
                let itemValue = '';
                if (item.type === "Number") {
                  itemValue = removeThousandSeparator(item.value);
                  if (targetItem) {
                    const show = (rule.r_condition === "==" && itemValue !== '' &&
                      Number(itemValue) === Number(rule.r_value)) ||
                      (rule.r_condition === ">=" && itemValue !== '' &&
                        Number(itemValue) >= Number(rule.r_value)) ||
                      (rule.r_condition === "<=" && itemValue !== '' &&
                        Number(itemValue) <= Number(rule.r_value)) ||
                      (rule.r_condition === ">" && itemValue !== '' &&
                        Number(itemValue) > Number(rule.r_value)) ||
                      (rule.r_condition === "<" && itemValue !== '' &&
                        Number(itemValue) < Number(rule.r_value));
                    targetItem.value = targetItem.value ? targetItem.value : "";
                    targetItem.show = rule.r_show ? show : !show;
                  }
                } else {
                  itemValue = item.value;
                  if (targetItem) {
                    const show = (rule.r_condition === "==" && itemValue !== '' &&
                      itemValue === rule.r_value) ||
                      (rule.r_condition === ">=" && itemValue !== '' &&
                        itemValue >= rule.r_value) ||
                      (rule.r_condition === "<=" && itemValue !== '' &&
                        itemValue <= rule.r_value) ||
                      (rule.r_condition === ">" && itemValue !== '' &&
                        itemValue > rule.r_value) ||
                      (rule.r_condition === "<" && itemValue !== '' &&
                        itemValue < rule.r_value);
                    targetItem.value = targetItem.value ? targetItem.value : "";
                    targetItem.show = rule.r_show ? show : !show;
                  }
                }
              }
            });
          }

          if (item.type === "Select") {

            // FIX 1: Always convert item.value to string
            item.value = item.value !== undefined && item.value !== null
              ? String(item.value)
              : "";

            // FIX 2: Ensure options exist
            if (!Array.isArray(item.options)) {
              item.options = [];
            }
            // FIX 3: Safe .find()
            const tempVar = Array.isArray(item.options)
              ? item.options.find((itemInArray) =>
                itemInArray &&
                itemInArray.value !== null &&
                itemInArray.value !== undefined &&
                String(itemInArray.value) === String(item.value)
              )
              : null;

            const tempRules = item.rules;

            tempRules.forEach((rule) => {
              if (rule.parent_field !== undefined) {
                const parentItem = filterSelect.find(f => f.name === rule.parent_field);

                if (item.value !== "") {
                  parentItem.value = parentItem.value || "";
                  parentItem.filterArray = parentItem.options.filter(
                    opt => opt.filtervalue === item.value
                  );
                } else {
                  parentItem.value = parentItem.value || "";
                  parentItem.filterArray = parentItem.options;
                }
                if (!parentItem.value && parentItem.validators?.required?.value === true && parentItem.filterArray?.length === 1) {
                  // Required field with only one option available — auto-select it
                  parentItem.value = String(parentItem.filterArray[0].value);
                  setValue(parentItem.name, parentItem.value);
                }
              }

              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItem = filterSelect.find(f => f.name === rule.r_field);

                if (targetItem) {
                  const show =
                    rule.r_condition === "==" &&
                    tempVar &&
                    tempVar.label === rule.r_value;

                  targetItem.value = targetItem.value || "";
                  targetItem.show = rule.r_show ? show : !show;
                }
              }
            });
          }

          // ApiField show rules configuration
          if (item?.type === "ApiField") {
            const tempRules = item.rules;
            const itemValue =
              item.value !== undefined && item.value !== null
                ? String(item.value)
                : "";

            tempRules.forEach((rule) => {
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItem = filterSelect.find(
                  (f) => f.name === rule.r_field
                );

                if (targetItem) {
                  let show = false;

                  // Handle different conditions
                  if (itemValue !== "") {
                    if (rule.r_condition === "==") {
                      show = itemValue === String(rule.r_value);
                    } else if (rule.r_condition === "!=") {
                      show = itemValue !== String(rule.r_value);
                    } else if (rule.r_condition === ">") {
                      show = parseFloat(itemValue) > parseFloat(rule.r_value);
                    } else if (rule.r_condition === "<") {
                      show = parseFloat(itemValue) < parseFloat(rule.r_value);
                    } else if (rule.r_condition === ">=") {
                      show = parseFloat(itemValue) >= parseFloat(rule.r_value);
                    } else if (rule.r_condition === "<=") {
                      show = parseFloat(itemValue) <= parseFloat(rule.r_value);
                    }
                  }

                  targetItem.value = targetItem.value || "";
                  targetItem.show = rule.r_show ? show : !show;
                }
              }
            });
          }

          if (item?.type === "ApiField" && item?.sub_type === "select") {
            const tempRules = item.rules;
            const tempVar = item.options.find((itemInArray) => {
              // Check if itemInArray and itemInArray.value are not null or undefined
              if (itemInArray && itemInArray.value !== null && itemInArray.value !== undefined) {
                // If the value is not null or undefined, then perform the comparison
                return itemInArray.value.toString() === item.value;
              } else {
                // If the value is null or undefined, skip this item in the array
                return false;
              }
            });

            tempRules.forEach((rule) => {
              if (rule.parent_field !== undefined) {
                const parentItem = filterSelect.find((itemInArray) => itemInArray.name === rule.parent_field);
                if (item.value !== "") {
                  parentItem.value = parentItem.value ? parentItem.value : "";
                  parentItem.filterArray = parentItem.options.filter((itemInArray) => itemInArray.filtervalue === item.value);
                } else {
                  parentItem.value = parentItem.value ? parentItem.value : "";
                  parentItem.filterArray = parentItem.options;
                }
                if (!parentItem.value && parentItem.validators?.required?.value === true && parentItem.filterArray?.length === 1) {
                  // Required field with only one option available — auto-select it
                  parentItem.value = String(parentItem.filterArray[0].value);
                  setValue(parentItem.name, parentItem.value);
                }
              }
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItem = filterSelect.find((itemInArray) => itemInArray.name === rule.r_field);
                if (targetItem) {
                  const show = rule.r_condition === "==" && tempVar && tempVar.label === rule.r_value;
                  targetItem.value = targetItem.value ? targetItem.value : "";
                  targetItem.show = rule.r_show ? show : !show;
                }
              }
            });
          }
          if (item.type === "Radio") {
            const tempRules = item.rules;
            const tempField = item.values.find((itemInArray) => itemInArray.value === item.value);
            tempRules.forEach((rule) => {
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItemRadio = filterSelect.find((itemInArray) => itemInArray.name === rule.r_field);
                if (targetItemRadio) {
                  if (rule.r_condition === "==" && tempField && tempField.label === rule.r_value) {
                    targetItemRadio.show = rule.r_show;
                  }
                  if (rule.r_condition === ">" && tempField && tempField.label > rule.r_value) {
                    targetItemRadio.show = rule.r_show;
                  }
                  if (rule.r_condition === "<" && tempField && tempField.label < rule.r_value) {
                    targetItemRadio.show = rule.r_show;
                  }
                  if (rule.r_condition === "<=" && tempField && tempField.label <= rule.r_value) {
                    targetItemRadio.show = rule.r_show;
                  }
                  if (rule.r_condition === ">=" && tempField && tempField.label >= rule.r_value) {
                    targetItemRadio.show = rule.r_show;
                  }
                }
              }
            });
          }

          if (item.type === "Date") {
            const tempRules = item.rules;
            tempRules.forEach((rule) => {
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItemDate = filterSelect.find((itemInArray) => itemInArray.name === rule.r_field);
                let dateMatch = areDatesMatching(item.value, rule.r_value, rule.r_condition);
                if (targetItemDate && dateMatch) {
                  targetItemDate.show = rule.r_show;
                }
              }
            });
          }
          if (item.type === "Time") {
            const tempRules = item.rules;
            tempRules.forEach((rule) => {
              if (rule.r_field !== undefined && rule.field !== undefined) {
                const targetItemTime = filterSelect.find((itemInArray) => itemInArray.name === rule.r_field);
                let timeMatch = compareTimes(item.value, rule.r_value);
                const shouldShow = item.value !== "" &&
                  ((rule.r_condition === "==" && timeMatch === 0) ||
                    (rule.r_condition === "<" && timeMatch === -1) ||
                    (rule.r_condition === ">" && timeMatch === 1) ||
                    (rule.r_condition === "<=" && timeMatch <= 0) ||
                    (rule.r_condition === ">=" && timeMatch >= 0));
                if (targetItemTime && shouldShow) {
                  targetItemTime.show = rule.r_show;
                }
              }
            });
          }
        }
      }
    });
  }

  const navigateAfterSave = (type, actionID, name_id) => {
    if (isSaveAndNew && routeParams?.action === "edit") {
      setShowPrevious(false);
      setIsSaveAndNew(false);
      setPdfParams({ form_id: '', uuid: '', type: '' });
      setSubmittedBy("");
      setIsprint(null);
      setTemplatePdfId(null);
      const currentUrl = location.pathname.split('/').filter(part => part !== '');
      const modifiedUrl = `/${currentUrl[0]}/add/${currentUrl[3]}`;
      navigateTo(modifiedUrl);
      setShowDeleteButton(false);
      setTemSubformData([]);
      reset();
      HandleAddData(currentUrl[3]);
    } else if (isSaveAndNew && routeParams?.action === "add") {
      reset();
      setShowDeleteButton(false);
      setTemSubformData([]);
      setShowPrevious(false);
      setIsSaveAndNew(false);
      setSubFormNewID({});
      setSubmittedBy("");
      getEditData()
    } else {
      if (type === 'new') {
        if (actionID && actionID !== "Dashboard") {
          navigateTo(`/actiontable_list/${actionID}`);
        }
        else if (navigateFrom === 'dashboard') {
          navigateTo("/dashboard", { state: { dashboardActiveTab } });
        }
        else {
          navigateTo(`/formsub/${name_id}`);
        }
      } else {
        const value = allsubFormArray[0]?.controls[0]?.value;
        const fieldId = allsubFormArray[0]?.controls[0]?.name;

        if (org_id === AGROECO_ORG_ID && fieldId === 'project') {
          const newArray = allsubFormArray.map(item => ({
            form_name: item.form_name, order: item.order,
            _id: item._id, isSubmitted: item?.isSubmitted,
          }));
          setFormList(applyAgroEcoSubformFilter(newArray, value));
        } else {
          updateSubformList(allsubFormArray);
        }
      }
    }
  };

  const submitSubFormData = async (type, subformID, geoLocation) => {
    let controls = [];
    if (allsubFormArray.length !== 0) {
      controls = allsubFormArray.filter(item => item._id === subformID);
      subData.forEach(item => {
        if (item._id === subformID) {
          item.controls = controls[0]?.controls;
          item.isSubmitted = 1;
        }
      });
    }
    const payload = {
      description: mainarray.description,
      isFormGroup: mainarray.isFormGroup,
      subforms: subData,
      form_name: mainarray.form_name,
      project_id: mainarray.project_id,
      survey_id: mainarray.survey_id,
      uuid: Object.keys(subFormNewID).length === 0 ? crypto.randomUUID() : subFormNewID?.uuid,
      submittedBy: userData,
      form_id: Object.keys(subFormNewID).length === 0 ? mainarray._id : subFormNewID?.form_id,
      isReportbuilderTemplate: mainarray.isReportbuilderTemplate ?? false
    };
    if (geoLocation) {
      payload.savedLocation = geoLocation;
    }
    // Carry forward the existing approval state — this PUT fires on every
    // "Next" click while paging through a multi-subform submission, and
    // omitting these let the backend reset/lose approval status on saves
    // that happen before the final submit.
    if (Object.keys(subFormNewID).length !== 0) {
      payload.isApproved = mainarray?.isApproved ?? -1;
      payload.approvalPermission = mainarray?.approvalPermission ?? false;
      payload.secondApprovalPermission = mainarray?.secondApprovalPermission ?? false;
      if (mainarray?.secondApprovalPermission === true)
        payload.isSecondApprove = mainarray?.isSecondApprove ?? -1;
    }
    // Single-pass normalization — all field-type coercions merged into one loop (P-6)
    if (Array.isArray(payload.subforms)) {
      payload.subforms.forEach(subform => {
        if (Array.isArray(subform.controls)) {
          // Flattened for ITERATION only — `subform.controls` itself (and
          // every Container's own `.controls`) stays structurally untouched,
          // so nothing here gets duplicated in the payload. Every branch
          // below mutates `control` in place; without this, a Container's
          // own `type` never matched any of these type-specific coercions
          // (ApiField options snapshot, Number/Radio/CalculatedField
          // stringification, Checkbox sync), so a field living inside a
          // Container reached the server with raw, un-coerced values —
          // exactly the class of bug the old Container-Checkbox-only patch
          // below this loop was a narrow, one-off fix for. Same pattern as
          // processControls/processRules in dynamicFormData above.
          const flattenedControls = subform.controls.flatMap((c) =>
            c.type === "Container" && Array.isArray(c.controls) ? [c, ...c.controls] : [c]
          );
          flattenedControls.forEach(control => {
            // ApiField options snapshot
            if (control.type === "ApiField" && control.sub_type === "multiSelect" && Array.isArray(control.options)) {
              control.copiedMultiSelectOptions = [...control.options];
            }
            if (control.type === "ApiField" && control.sub_type === "select" && Array.isArray(control.options)) {
              control.copiedSelectOptions = [...control.options];
            }
            if (control.type === "ApiField" && control.sub_type === "text") {
              control.value = String(control.value || '');
            }

            // Explicitly convert number values to string for number fields
            if (control.type === 'Number' && typeof control.value === 'number') {
              control.value = control.value.toString();
            }

            // Ensure radio button value is numeric (mapped to option.value)
            if (control.type === "Radio" && Array.isArray(control.values)) {
              const matched = control.values.find(opt =>
                opt.value === control.value || opt.label === control.value
              );
              // Only update if match found AND values array exists
              if (matched) {
                control.value = matched.value;
              }
            }
            if (control.type === "Radio" && control.value !== undefined) {
              control.value = String(control.value);
            }

            // Coerce calculated/aggregation field values to string
            if (
              (control.type === "CalculatedField" ||
                control.type === "SumField" ||
                control.type === "AggregationField") &&
              control.value !== undefined
            ) {
              control.value = String(control.value);
            }

            // CHECKBOX SYNC: Ensure checked values are correct
            if (control.type === "Checkbox" && Array.isArray(control.checkvalues)) {
              // Step 1: Get selected values from control.value (could be array or string)
              let selectedValues = [];
              if (Array.isArray(control.value)) {
                selectedValues = control.value.map(String);
              } else if (control.value && typeof control.value === 'string') {
                // Handle comma-separated or single value
                selectedValues = control.value.split(',').map(v => v.trim()).filter(v => v);
              } else if (control.value) {
                selectedValues = [String(control.value)];
              }

              // Step 2: If no selectedValues from control.value, extract from already-checked items
              if (selectedValues.length === 0) {
                selectedValues = control.checkvalues
                  .filter(opt => opt.checked === true)
                  .map(opt => String(opt.label) || String(opt.value));
              }

              // Step 3: Sync all checkvalues with the correct checked state
              control.checkvalues.forEach(opt => {
                const optLabel = String(opt.label || '');
                const optValue = String(opt.value || '');

                opt.checked =
                  selectedValues.includes(optLabel) ||
                  selectedValues.includes(optValue) ||
                  selectedValues.some(val => val === optLabel || val === optValue);
              });

              // Step 4: Rebuild control.value from checked items to ensure consistency
              control.value = control.checkvalues
                .filter(opt => opt.checked === true)
                .map(opt => opt.label)
                .join(',');
            }
          });
        }
      });
    }

    setIsLoadingBtn(true);
    setIsGlobalLoading(true);
    try {
      const id = routeParams.id
      const response = Object.keys(subFormNewID).length === 0
        ? await authAxios.post(`/api/v1/formSubmission`, payload)
        : await authAxios.put(`/api/v1/formSubmission/${id}`, payload);
      if (response.status === 200) {
        setIsGlobalLoading(false);
        setIsLoadingBtn(false); isSubmittingRef.current = false;
        if (mainarray.isReportbuilderTemplate && response?.data?.data?.template_id) {
          // Report builder flow — show popup
          setPdfParams({
            form_id: Object.keys(subFormNewID).length === 0 ? mainarray._id : subFormNewID?.form_id,
            uuid: response?.data?.data?.uuid,
            type: type,
            isReportBuilder: true,
            template_id: response.data.data.template_id,
            submission_id: response?.data?.data?._id
          });
          setShowModal(true);
        } else if (!ModalIsOpen && buttonArray && buttonArray[0]?.isPdfDownload && buttonArray[0].pdf_rules?.pdfdownloadapi !== "" && response?.data?.data?.uuid) {
          // Existing Java PDF flow — UNCHANGED
          setPdfParams({
            form_id: controls[0]._id,
            uuid: response?.data?.data?.uuid,
            type: type
          });
          setShowModal(true);
        } else {
          navigateAfterSave(type, actionID, name_id);
        }
        if (Object.keys(subFormNewID).length === 0) {
          setSubFormNewID(response.data?.data);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
      setIsLoadingBtn(false); isSubmittingRef.current = false;
      setIsGlobalLoading(false);
    }
  };

  const validateRequiredGrids = () => {
    const missing = formArray.find(
      f =>
        f.type === "GridComponent" &&
        f.tableRequired === true &&
        f.show !== false && // skip validation if grid is hidden
        (!Array.isArray(f.gridRows) || f.gridRows.length === 0)
    );
    if (missing) {
      toast.info(`Please add at least one row in "${missing.label}".`);
      return false;
    }
    return true;
  };

  // function for next form if multiple form exists
  const handleFormNext = async (parameter, type = 'normal') => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    // Commit and validate all grids before proceeding
    try {
      await commitAllGrids();
    } catch (e) {
      toast.error(e.message || "Grid validation failed");
      return;
    }
    if (!validateRequiredGrids()) return;
    reset();
    // apiBlocked: true = pre-flight API returned an error → stop the submit flow.
    // false (default) = no pre-flight gate, or gate passed → allow continuation.
    let apiBlocked = false;
    setErrorList([]);
    const lastObject = formArray;
    const button = buttonArray[0];

    if (button && resolveRuleUrl(button.api_rules)) {
      try {
        setIsGlobalLoading(true);
        let response;
        const resolvedButtonUrl = resolveRuleUrl(button.api_rules);
        if (button.api_rules?.isPreFullBody) {
          //post with full body
          // Inline-sync the current subform's formArray into the snapshot so the
          // pre-flight body reflects what the user actually typed, not stale init values.
          const syncedSubforms = allsubFormArray.map(s =>
            s._id === subFormID ? { ...s, controls: formArray } : { ...s }
          );
          const payload = {
            form_id: mainarray._id,
            form_name: mainarray.form_name,
            description: mainarray.description,
            isFormGroup: mainarray.isFormGroup,
            project_id: mainarray.project_id,
            survey_id: mainarray.survey_id,
            submittedBy: userData,
            subforms: syncedSubforms,
          }
          response = await authAxios.post(resolvedButtonUrl, payload);
        } else if (button.api_rules?.parameters?.length > 0) {
          //PUT with selected parametrs only
          const tempFilterControl = lastObject.filter((item) =>
            button.api_rules.parameters.includes(item.name)
          );
          const controlObj = {
            form_id: mainarray._id,
            controls: tempFilterControl,
          }
          response = await authAxios.put(resolvedButtonUrl, controlObj);
        }
        if (response.status === 200) {
          setIsGlobalLoading(false);
          apiBlocked = false;
        }
      }
      catch (error) {
        toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 2000 });
        setIsGlobalLoading(false);
        setErrorList(error?.response?.data?.errors);
        apiBlocked = true;
      }
    }
    if (!apiBlocked) {
      if (allsubFormArray.length !== 0) {
        for (let i = 0; i < allsubFormArray.length; i++) {
          if (allsubFormArray[i]._id === subFormID) {
            allsubFormArray[i].controls = formArray;
            allsubFormArray[i].isSubmitted = 1;
          }
        }
      }
      setAllsubformData([...allsubFormArray]);
      if (parameter === "Save") {
        try {
          await submitSubFormData("new", subFormID);
        } catch (error) {
          toast.error(error?.response?.data?.message || "Failed to save sub-form. Please try again.", { autoClose: 5000 });
        }
      } else {
        try {
          await submitSubFormData("edit", subFormID);
          setOrder("");
          setShowPrevious(true);
          if (order !== allsubFormArray.length) {
            let tempValue
            if (type === 'subNavigation') {
              tempValue = allsubFormArray.filter(
                (itemInArray) => itemInArray._id === clickedSubform
              );
            }
            else {
              tempValue = allsubFormArray.filter(
                (itemInArray) => itemInArray.order === order + 1
              );
            }
            setSubFormID(tempValue[0]?._id);
            setSelectedForm(tempValue[0]?._id);
            setSelectedSubformName(tempValue[0]?.form_name);
            let filterSelect = tempValue[0].controls;
            // ========
            const controlData = [...filterSelect];
            buildGroupIndices(controlData); // P-11: extracted helper
            // ========

            triggerCountValueCalculation(controlData);
            // Set before dynamicFormData — its API replay syncs controls back by displayNameRef
            setDisplayName(tempValue[0].form_name);
            await dynamicFormData(controlData);
            if (tempValue[0].order + 1 === allsubFormArray.length) {
              setShowNext(false);
            }
            setOrder(tempValue[0].order);
            setIsprint(tempValue[0]?.pdfId ? tempValue[0]?.pdfId : null);
            setTemplatePdfId(tempValue[0]?.templatePdfId || null);
            const filteredData = controlData.filter(
              (item) => item.type === "submit_button"
            );
            if (filteredData.length > 0) {
              setSubmitButton(filteredData[0].label);
              setNextButton(filteredData[0].label);
              setButtonArray(filteredData);
            }
            setFormArray(applyInitialGridVisibility(controlData)); // P-13: removed preceding setFormArray([]) — avoids blank-render flash
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || "Failed to navigate to next form. Please try again.", { autoClose: 5000 });
        }
      }
    }
  };

  // function for previous form if multiple form exists
  const handleFormPrevious = () => {
    setErrorList([]);
    if (allsubFormArray.length !== 0) {
      for (let i = 0; i < allsubFormArray.length; i++) {
        if (allsubFormArray[i]._id === subFormID) {
          allsubFormArray[i].controls = formArray;
        }
      }
    }

    setShowNext(true);
    if (order !== 0) {
      let tempValue = allsubFormArray.filter(
        (itemInArray) => itemInArray.order === order - 1
      );
      setDisplayName(tempValue[0].form_name);
      setSubFormID(tempValue[0]?._id);
      setSelectedForm(tempValue[0]?._id);
      setSelectedSubformName(tempValue[0]?.form_name);
      let filterSelect = tempValue[0].controls;
      // ========
      const controlData = [...filterSelect];
      buildGroupIndices(controlData); // P-11: extracted helper
      // ----count-----
      triggerCountValueCalculation(controlData)
      setOrder(tempValue[0].order);
      setIsprint(tempValue[0]?.pdfId ? tempValue[0]?.pdfId : null);
      setTemplatePdfId(tempValue[0]?.templatePdfId || null);
      if (order - 1 === 0) {
        setShowPrevious(false);
      }
      const filteredData = controlData.filter(
        (item) => item.type === "submit_button"
      );
      if (filteredData.length > 0) {
        setSubmitButton(filteredData[0].label);
        setNextButton(filteredData[0].label);
        setButtonArray(filteredData);
      }
      setFormArray(applyInitialGridVisibility(controlData)); // P-13: removed preceding setFormArray([]) — avoids blank-render flash
    }
    setAllsubformData([...allsubFormArray]);
  };

  //Checkbox field change
  const fieldChangedCheckbox = useCallback((name, fieldId, value) => {
    userEditedFieldsRef.current.add(name);
    // Container children live one level below formArray's own entries — same
    // gap fieldChanged had (see its own comment).
    if (!formArray.some((f) => f.name === name && f.type !== "submit_button")) {
      const found = findFieldByNameAnywhere(formArray, name);
      // Same submit_button exclusion the top-level branch below applies via
      // its own `f.type !== "submit_button"` findIndex filter — without it,
      // a Checkbox name that happens to collide with a submit_button's name
      // (button names live in a separate array, never cross-checked against
      // field names) would still resolve here (findFieldByNameAnywhere has
      // no type filter) and get a spurious `checkvalues` written onto it.
      if (!found || found.field.type === "submit_button") return;
      const updatedChild = {
        ...found.field,
        checkvalues: (found.field.checkvalues ?? []).map((cv) =>
          cv.value === fieldId ? { ...cv, checked: value } : cv
        ),
      };
      let nextArray = setAtPath(formArray, found.path, updatedChild);

      // Evaluate the child's own rules, mirroring the top-level path's ==/!=
      // checked-label logic below.
      if (updatedChild.rules?.length > 0) {
        const checkedLabels = new Set(
          updatedChild.checkvalues.filter((cv) => cv.checked).map((cv) => cv.label)
        );
        for (const rule of updatedChild.rules) {
          if (rule.r_condition !== "==" && rule.r_condition !== "!=") continue;
          const targetFound = findFieldByNameAnywhere(nextArray, rule.r_field);
          if (!targetFound) continue;
          const isChecked = checkedLabels.has(rule.r_value);
          const conditionMet = rule.r_condition === "==" ? isChecked : !isChecked;
          const tgt = targetFound.field;
          if (conditionMet) {
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, show: rule.r_show });
          } else {
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, value: "", show: !rule.r_show });
            setValue(tgt.name, "");
          }
        }
      }

      setFormArray(nextArray);
      return nextArray;
    }

    const updatedArray = [...formArray];

    const idx = updatedArray.findIndex(
      (f) => f.name === name && f.type !== "submit_button"
    );
    if (idx === -1) return;

    // Replace with a shallow clone so the original state object is not mutated
    updatedArray[idx] = {
      ...updatedArray[idx],
      checkvalues: (updatedArray[idx].checkvalues ?? []).map((cv) =>
        cv.value === fieldId ? { ...cv, checked: value } : cv
      ),
    };

    const item = updatedArray[idx];

    if (item.rules?.length > 0) {
      // Build once: O(1) target-field lookup per rule instead of O(n) find()
      const fieldByName = new Map(updatedArray.map((f) => [f.name, f]));
      // Build once: O(1) checked-label lookup per rule instead of O(m) some()
      const checkedLabels = new Set(
        item.checkvalues.filter((cv) => cv.checked).map((cv) => cv.label)
      );

      item.rules.forEach((rule) => {
        if (rule.r_condition !== "==" && rule.r_condition !== "!=") return;

        const targetField = fieldByName.get(rule.r_field);
        if (!targetField) return;

        const isChecked = checkedLabels.has(rule.r_value);
        const conditionMet = rule.r_condition === "==" ? isChecked : !isChecked;

        if (conditionMet) {
          targetField.show = rule.r_show;
        } else {
          targetField.value = "";
          setValue(targetField.name, "");
          targetField.show = !rule.r_show;
        }
      });
    }

    setFormArray(updatedArray);
    // Returned so callers that chain handleAPIChanged can pass the updated
    // array instead of their stale render-time formArray.
    return updatedArray;
  }, [formArray, setValue]);

  //  Re-evaluate ApiField visibility rules after value changes
  const reEvaluateApiFieldVisibility = (formArray) => {
    const updatedArray = [...formArray];
    // For each ApiField in the form, check if it has visibility rules and re-evaluate them
    updatedArray.forEach((apiField) => {
      if (apiField?.type === "ApiField" && apiField?.rules && apiField.rules.length > 0) {
        const itemValue = apiField.value !== undefined && apiField.value !== null ? String(apiField.value) : "";
        // Process each rule for this ApiField
        apiField.rules.forEach((rule) => {
          if (rule.r_field !== undefined && rule.field !== undefined) {
            const targetField = updatedArray.find((f) => f.name === rule.r_field);

            if (targetField) {
              let show = false;

              // Handle different conditions
              if (itemValue !== "") {
                if (rule.r_condition === "==") {
                  show = itemValue === String(rule.r_value);
                } else if (rule.r_condition === "!=") {
                  show = itemValue !== String(rule.r_value);
                } else if (rule.r_condition === ">") {
                  show = parseFloat(itemValue) > parseFloat(rule.r_value);
                } else if (rule.r_condition === "<") {
                  show = parseFloat(itemValue) < parseFloat(rule.r_value);
                } else if (rule.r_condition === ">=") {
                  show = parseFloat(itemValue) >= parseFloat(rule.r_value);
                } else if (rule.r_condition === "<=") {
                  show = parseFloat(itemValue) <= parseFloat(rule.r_value);
                }
              }
              targetField.show = rule.r_show ? show : !show;
            }
          }
        });
      }
    });
    return updatedArray;
  };

  // Select field select
  const fieldSelectChanged = async (fieldId, value, formArray) => {

    if (org_id === AGROECO_ORG_ID && allsubFormArray.length > 1 && fieldId === 'project') {
      const newArray = allsubFormArray.map(item => ({
        form_name: item.form_name, order: item.order,
        _id: item._id, isSubmitted: item?.isSubmitted,
      }));
      setFormList(applyAgroEcoSubformFilter(newArray, value));
    }
    const list = { formArray };
    // One Map built upfront replaces the previous O(n) for-loop + O(n) filter +
    // a second O(n) Map build partway through the rules block. Also indexes
    // every Container's own children by name (same object references,
    // mutated in place below same as any top-level entry) — this is the
    // ONLY change needed to make a nested field work as either the changed
    // field itself (`targetItem`) or a rule's target (`rule.r_field`) /
    // parent (`rule.parent_field`) below, since all three read from this
    // same map. (Grid show/hide further down still only looks at the top
    // level — a narrower, separately-scoped gap left as-is for this pass.)
    const localMap = buildFieldIndexMap(list.formArray);
    clearErrors(fieldId);
    let temp = "";
    let targetItem = localMap.get(fieldId);
    if (targetItem) {
      if (targetItem.type === "Select" && value !== undefined) {
        targetItem.value = value;
        targetItem.defaultValue = '';
        setValue(targetItem.name, value);
      } else if (targetItem.type === "ApiField" && targetItem?.sub_type === "select" && value !== undefined) {
        targetItem.value = value;
        setValue(targetItem.name, value);
      } else if (targetItem.type === "AdvancedSelect" && value !== undefined) {
        targetItem.value = value;
        setValue(targetItem.name, value);
      } else {
        targetItem.value = "";
        setValue(targetItem.name, "");
      }
    }
    // targetItem is the same object as tempArray[0] was — no extra filter needed.
    let tempRules = {};
    temp = (targetItem?.options || []).filter((itemInArray) => itemInArray.value !== null && itemInArray.value.toString() === targetItem?.value);
    if (targetItem?.rules?.length > 0) {
      tempRules = targetItem.rules;
    }

    if (tempRules.length > 0) {
      const formArrayMap = localMap; // reuse — no second O(n) build
      tempRules.forEach((rule) => {
        if (rule.r_field && rule.field) {
          let targetField = formArrayMap.get(rule.r_field);
          if (targetField) {
            let matched = targetItem?.value !== "" && temp[0]?.label === rule.r_value;
            let shouldShow = (rule.r_condition === "==") ? rule.r_show : !rule.r_show;
            targetField.value = targetField.defaultValue ? targetField.defaultValue : "";
            setValue(targetField.name, targetField.defaultValue ? targetField.defaultValue : "");
            targetField.show = matched ? shouldShow : !shouldShow;
          }
        }
        if (rule.parent_field) {
          let parentFieldItem = formArrayMap.get(rule.parent_field);
          if (parentFieldItem) {
            if (targetItem?.value !== "") {
              let arrayOptions = parentFieldItem.options.filter(itemInArray => itemInArray.filtervalue && itemInArray.filtervalue.toString() === targetItem?.value)
              let filtered = arrayOptions.filter(item => item.value === parentFieldItem.defaultValue);
              if (filtered.length > 0) {
                parentFieldItem.value = targetItem?.value !== "" ? parentFieldItem.defaultValue || "" : "";
                setValue(parentFieldItem.name, targetItem?.value !== "" ? parentFieldItem.defaultValue || "" : "");
              } else {
                parentFieldItem.value = "";
                setValue(parentFieldItem.name, "");
              }
              parentFieldItem.filterArray = parentFieldItem.options.filter(itemInArray =>
                itemInArray.filtervalue && itemInArray.filtervalue.toString() === targetItem?.value);
            } else {
              parentFieldItem.filterArray = parentFieldItem.options;
              parentFieldItem.value = targetItem?.value !== "" ? parentFieldItem.defaultValue || "" : "";
              setValue(parentFieldItem.name, targetItem?.value !== "" ? parentFieldItem.defaultValue || "" : "");
            }
          }
        }
      });
    }

    // Handle grid show/hide rules
    const selectedValue = typeof value === 'object' ? value?.value : value;
    // Capture which grids were visible BEFORE show/hide evaluation
    const prevVisibleGrids = new Set(
      list.formArray
        .filter(f => f.type === "GridComponent" && f.show === true)
        .map(f => f.name)
    );
    list.formArray = handleGridShowHide(fieldId, selectedValue, list.formArray);
    // Find grids that were visible before AND are still visible after (value changed but grid stays)
    const changedField = list.formArray.find(f => f.name === fieldId);
    const ruleGridNames = new Set(
      (changedField?.grid_show_hide_rule || []).map(r => r.gridName)
    );
    const gridsToRefresh = list.formArray.filter(
      f => f.type === "GridComponent" && f.show === true && prevVisibleGrids.has(f.name) && ruleGridNames.has(f.name)
    );

    list.formArray = [...list.formArray];
    setFormArray(list.formArray);
    // Refresh grids that stayed visible (user switched between two matching condition values)
    if (gridsToRefresh.length > 0) {
      setTimeout(() => {
        gridsToRefresh.forEach(g => {
          gridComponentRefs.current[g.name]?.resetGridToInitial?.();
        });
      }, 0);
    }
    // Reset newly visible grids (was hidden → now shown)
    const newlyVisibleGrids = list.formArray.filter(
      f => f.type === "GridComponent" && f.show === true && !prevVisibleGrids.has(f.name) && ruleGridNames.has(f.name)
    );
    if (newlyVisibleGrids.length > 0) {
      setTimeout(() => {
        const firstNew = newlyVisibleGrids[0];
        const idx = list.formArray.indexOf(firstNew);
        setOpenGridId(`grid-${firstNew.name || firstNew._id || idx}`);
        setTimeout(() => {
          newlyVisibleGrids.forEach(g => {
            gridComponentRefs.current[g.name]?.resetGridToInitial?.();
          });
        }, 150);
      }, 0);
    }
  };

  // MultiSelect field select
  const fieldMultiSelectChanged = (name, formArray, selectedOptions) => {
    const list = { formArray };
    // One Map built upfront replaces the previous O(n) for-loop + O(n) filter +
    // a second O(n) Map build partway through the rules block. Also indexes
    // every Container's own children by name (same object references,
    // mutated in place below same as any top-level entry) — this is the
    // ONLY change needed to make a nested field work as either the changed
    // field itself (`msItem`) or a rule's `parent_field` below, since both
    // read from this same map.
    const localMap = buildFieldIndexMap(list.formArray);
    clearErrors(name);
    let msItem = localMap.get(name);
    if (msItem && (msItem.type === "MultiSelect" || msItem.type === "ApiField")) {
      const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
      msItem.value = selectedValues;
      msItem.defaultValue = [];
      setValue(msItem.name, selectedValues);
    }
    // msItem is the same object as tempArray[0] was — no extra filter needed.
    let tempRules = {};
    if (msItem?.rules?.length > 0) {
      tempRules = msItem.rules;
    }
    if (tempRules.length > 0) {
      const formArrayMap = localMap; // reuse — no second O(n) build
      tempRules.forEach((rule) => {
        if (rule.parent_field) {
          let parentFieldItem = formArrayMap.get(rule?.parent_field);
          if (parentFieldItem) {
            if (msItem?.value.length !== 0) {
              let arrayOptions = parentFieldItem.options.filter(itemInArray =>
                itemInArray.filtervalue && msItem?.value.includes(itemInArray.filtervalue.toString()));
              let validValues = arrayOptions.map(item => item.value);
              let updatedValue = parentFieldItem.value.filter(val => validValues.includes(val));
              parentFieldItem.filterArray = parentFieldItem.options.filter(itemInArray =>
                itemInArray.filtervalue && msItem?.value.includes(itemInArray.filtervalue.toString()));
              parentFieldItem.value = updatedValue;
              setValue(parentFieldItem.name, updatedValue);
            } else {
              parentFieldItem.filterArray = parentFieldItem.options;
              parentFieldItem.value = msItem?.value.length > 0 ? parentFieldItem.defaultValue || [] : [];
              setValue(parentFieldItem.name, msItem?.value.length > 0 ? parentFieldItem.defaultValue || [] : []);
            }
          }
        }
      });
    }
    list.formArray = [...list.formArray];
    setFormArray(list.formArray);
  }

  // onChangeDateCalc, equationDateSolver, onChangeTime, equationTimeSolver
  // provided by useFormCalculations hook above.

  // Date field change
  // Accepts an optional `value` param so callers can pass the event value directly
  // instead of having this function read from the DOM.
  const fieldChangedDate = async (name, id, value) => {
    // O(1) lookup replaces the previous O(n) for-loop + O(n) filter —
    // formArrayMap indexes Container children too, so this already resolves
    // a nested field the same as a top-level one.
    let item = formArrayMap.get(name);
    let dateValue = "";
    if (item) {
      dateValue = value !== undefined ? value : (document.getElementById(name)?.value ?? "");
      item.value = dateValue;
      item.defaultValue = "";
      setValue(item.name, dateValue);
      trigger(item.name);
    }
    // O(1) target lookup per rule replaces the previous O(n²) nested for-loops.
    const tempRules = item?.rules?.length > 0 ? item.rules : [];
    for (const rule of tempRules) {
      if (rule.r_field !== undefined && rule.field !== undefined) {
        const targetItem = formArrayMap.get(rule.r_field);
        if (targetItem) {
          const dateMatch = areDatesMatching(item.value, rule.r_value, rule.r_condition);
          if (item.value !== "" && dateMatch === true) {
            const dv = targetItem.defaultValue || "";
            targetItem.value = dv;
            setValue(targetItem.name, dv);
            targetItem.show = rule.r_show;
          } else {
            targetItem.value = "";
            setValue(targetItem.name, "");
            targetItem.show = !rule.r_show;
          }
        }
      }
    }
    setFormArray([...formArray]);
    await handleGridApiTrigger(name, dateValue);
  };

  // Time field change
  // Accepts an optional `value` param so callers can pass the event value directly
  // instead of having this function read from the DOM.
  const fieldChangedTime = (name, id, value) => {
    // O(1) lookup replaces the previous O(n) find + O(n) filter —
    // formArrayMap indexes Container children too, so this already resolves
    // a nested field the same as a top-level one.
    let item = formArrayMap.get(name);
    if (!item) return;
    item.value = value !== undefined ? value : (document.getElementById(name)?.value ?? "");
    item.defaultValue = "";

    const { rules } = item;
    if (rules.length > 0) {
      rules.forEach((rule) => {
        const { r_field, r_condition, r_value, r_show } = rule;
        if (r_field && r_condition && r_value) {
          // O(1) lookup replaces the previous O(n) filter per rule.
          const matchingItem = formArrayMap.get(r_field);
          if (matchingItem) {
            const TimeMatch = compareTimes(item.value, r_value);
            const shouldShow =
              item.value !== "" &&
              ((r_condition === "==" && TimeMatch === 0) ||
                (r_condition === "<" && TimeMatch === -1) ||
                (r_condition === ">" && TimeMatch === 1) ||
                (r_condition === "<=" && TimeMatch <= 0) ||
                (r_condition === ">=" && TimeMatch >= 0));
            matchingItem.value = "";
            if (shouldShow) {
              matchingItem.show = r_show;
            } else {
              matchingItem.value = "";
              setValue(matchingItem.name, "");
              matchingItem.show = !r_show;
            }
          }
        }
      });
    }
    setFormArray([...formArray]);
  };

  // Function to compare two times — delegates to dateUtils
  const compareTimes = compareTimes_util;

  // number formatting on blur
  const numberFormatting = async (name, value) => {
    clearErrors(name);
    // O(1) lookup replaces the previous O(n) find.
    const element = formArrayMap.get(name);
    if (!element || element.type !== "Number") return;

    if (element) {
      if (element.hasOwnProperty("decimalValue")) {
        let decimalValue = element?.hasOwnProperty("decimalValue") ? element.decimalValue : 2;
        let formattedValue = value.replace(/[^0-9.-]/g, ""); // Remove non-numeric characters
        // Check for formatting options
        if (
          formattedValue !== null &&
          formattedValue !== "" &&
          formattedValue !== undefined
        ) {
          if (element.isThousandSeparator && element.isDecimalPoint) {
            // Format with both decimal point and thousand separator
            formattedValue = parseFloat(formattedValue).toLocaleString("en-US", {
              style: "decimal",
              minimumFractionDigits: decimalValue || null, // You can adjust this based on your requirements
              maximumFractionDigits: decimalValue || null, // You can adjust this based on your requirements
            });
          } else if (element.isThousandSeparator) {
            // Format with thousand separator, without decimal point
            formattedValue = formattedValue.replace(/\.\d+/g, ""); // Remove decimal part
            formattedValue = formattedValue.replace(
              /(\d)(?=(\d{3})+(?!\d))/g,
              "$1,"
            ); // Add thousand separator
          } else if (element.isDecimalPoint) {
            // Format with decimal point, without thousand separator
            formattedValue = formattedValue.replace(/,/g, ""); // Remove thousand separator
            formattedValue = parseFloat(formattedValue).toFixed(decimalValue || null); // Add ".00"
          }
        }
        element.value = formattedValue;
        setValue(name, formattedValue);
      }
    }
  };

  const handleGridShowHide = (fieldId, value, formArray) => {
    // One Map built once — O(n) — replaces the O(n) changedField find plus the
    // O(n) getFieldValue find that fires on every rule for every GridComponent.
    const fieldMap = new Map(formArray.map(f => [f.name, f]));
    const changedField = fieldMap.get(fieldId); // O(1)
    if (!changedField?.grid_show_hide_rule?.length) {
      return formArray;
    }
    return formArray.map(field => {
      if (field.type !== "GridComponent") return field;
      const gridRule = changedField.grid_show_hide_rule.find(r => r.gridName === field.name);
      if (!gridRule?.rules?.length) return field;
      // Helper to get field value for comparison
      const getFieldValue = (fieldName) => {
        const targetField = fieldMap.get(fieldName); // O(1) instead of O(n) find
        if (!targetField) return "";

        let fieldValue = targetField.value;

        if (targetField.type === "Select" && Array.isArray(targetField.options)) {
          const selectedOption = targetField.options.find(opt => String(opt.value) === String(fieldValue));
          fieldValue = selectedOption?.label || fieldValue;
        } else if (targetField.type === "Number") {
          fieldValue = String(fieldValue).replace(/,/g, '');
        } else if (targetField.type === "Radio") {
          const selectedOption = targetField.values?.find(v => v.value === Number(fieldValue));
          fieldValue = selectedOption?.label || fieldValue;
        }
        return fieldValue;
      };

      // Evaluate single condition
      const evaluateCondition = (rule) => {
        const currentValue = String(getFieldValue(rule.fieldName));
        const ruleValue = String(rule.value);

        switch (rule.condition) {
          case "===": case "==": return currentValue === ruleValue;
          case "!==": case "!=": return currentValue !== ruleValue;
          case ">": return Number(currentValue) > Number(ruleValue);
          case "<": return Number(currentValue) < Number(ruleValue);
          case ">=": return Number(currentValue) >= Number(ruleValue);
          case "<=": return Number(currentValue) <= Number(ruleValue);
          default: return false;
        }
      };

      // Evaluate all conditions with logical operators
      let finalCondition = evaluateCondition(gridRule.rules[0]);

      for (let i = 1; i < gridRule.rules.length; i++) {
        const prevRule = gridRule.rules[i - 1];
        const currentCondition = evaluateCondition(gridRule.rules[i]);

        if (prevRule.logicalOperator === "||") {
          finalCondition = finalCondition || currentCondition;
        } else {
          finalCondition = finalCondition && currentCondition;
        }
      }

      // Determine visibility based on action
      const shouldShow = gridRule.action === "show" ? finalCondition : !finalCondition;
      // Clear rows if hiding grid with data
      const hasData = Array.isArray(field.gridRows) && field.gridRows.length > 0;
      if (!shouldShow && hasData) {
        return { ...field, show: false, gridRows: [] };
      }
      return { ...field, show: shouldShow };
    });
  };

  const applyInitialGridVisibility = (formArray) => {
    let updatedArray = [...formArray];

    const fieldsWithGridRules = formArray.filter(
      field => field?.grid_show_hide_rule && field?.grid_show_hide_rule.length > 0
    );
    fieldsWithGridRules?.forEach(field => {
      updatedArray = handleGridShowHide(field.name, field.value, updatedArray);
    });
    return updatedArray;
  };

  // Text/Number/Radio/Textarea field change
  const fieldChanged = (fieldId, value) => {
    setIsFormEdited(true);
    userEditedFieldsRef.current.add(fieldId);
    clearErrors(fieldId);

    // Container children live one level below formArray's own entries, so
    // the flat indexByName lookup below never finds one — previously that
    // meant this whole function silently no-op'd for a container child
    // (the typed value still reached the saved submission via RHF's own
    // ref-based tracking on the uncontrolled input, but the formArray
    // mirror never updated and show/hide rules never re-evaluated). This
    // branch ONLY fires when fieldId isn't a top-level field at all, so it
    // changes nothing about the existing flat-array logic below — it just
    // adds a path for the case that used to do nothing.
    if (!formArray.some(f => f.name === fieldId)) {
      const found = findFieldByNameAnywhere(formArray, fieldId);
      if (!found) return;
      const updatedChild = { ...found.field, value };
      let nextArray = setAtPath(formArray, found.path, updatedChild);
      setValue(fieldId, value);

      // Evaluate the child's OWN show/hide rules against their targets
      // (top-level or nested — findFieldByNameAnywhere resolves either),
      // mirroring the exact condition logic the top-level path below uses.
      // A rule TARGETING a container child (i.e. some OTHER field's rule
      // pointing at this one) is still out of scope, same as it always
      // was for the top-level engine (it only ever evaluates the CHANGED
      // field's own outgoing rules, never scans every other field's rules
      // for ones that might target the field that just changed).
      const rules = updatedChild.rules?.length > 0 ? updatedChild.rules : [];
      if (updatedChild.type !== "Radio") {
        for (const rule of rules) {
          if (rule.r_field === undefined || rule.field === undefined) continue;
          const targetFound = findFieldByNameAnywhere(nextArray, rule.r_field);
          if (!targetFound) continue;
          const currentValue = updatedChild.type === "Number"
            ? removeThousandSeparator(updatedChild.value)
            : updatedChild.value;
          let conditionMet = false;
          switch (rule.r_condition) {
            case "==": conditionMet = currentValue !== "" && currentValue === rule.r_value; break;
            case ">=": conditionMet = currentValue !== "" && currentValue >= rule.r_value; break;
            case "<=": conditionMet = currentValue !== "" && currentValue <= rule.r_value; break;
            case ">": conditionMet = currentValue !== "" && Number(currentValue) > Number(rule.r_value); break;
            case "<": conditionMet = currentValue !== "" && Number(currentValue) < Number(rule.r_value); break;
            default: break;
          }
          const tgt = targetFound.field;
          if (conditionMet) {
            const newVal = tgt.defaultValue !== "" ? tgt.defaultValue : tgt.value;
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, show: rule.r_show, value: newVal });
            if (tgt.defaultValue !== "") setValue(tgt.name, tgt.defaultValue);
          } else {
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, value: "", show: !rule.r_show });
            setValue(tgt.name, "");
          }
        }
      } else {
        // Radio — only "==" condition is supported, same as the top-level path.
        const temp = (updatedChild.values || []).filter(v => v.value === updatedChild.value);
        for (const rule of rules) {
          if (rule.r_field === undefined || rule.field === undefined) continue;
          if (rule.r_condition !== "==") continue;
          const targetFound = findFieldByNameAnywhere(nextArray, rule.r_field);
          if (!targetFound) continue;
          const tgt = targetFound.field;
          if (updatedChild.value !== "" && temp[0]?.label === rule.r_value) {
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, show: rule.r_show, value: tgt.defaultValue });
          } else {
            nextArray = setAtPath(nextArray, targetFound.path, { ...tgt, value: "", show: !rule.r_show });
            setValue(tgt.name, "");
          }
        }
      }

      setFormArray(nextArray);
      return nextArray;
    }

    // One Map built once — O(n) single pass.
    // Provides O(1) index lookup for both the changed field and every rule target,
    // replacing the previous 7 nested O(n) for-loops that ran on every keystroke.
    const indexByName = new Map(formArray.map((f, i) => [f.name, i]));
    const list = { formArray: [...formArray] }; // shallow copy — mutations stay local

    // ── Step 1: Update the changed field's value (O(1) index lookup) ──────────
    const idx = indexByName.get(fieldId);
    if (idx === undefined) return;

    const item = list.formArray[idx];
    let updatedItem = item;

    if (item.type === "Radio") {
      updatedItem = { ...item, value: Number(value), defaultValue: "" };
      list.formArray[idx] = updatedItem;
    } else if (item.type === "Number") {
      let decimalPlaces = null;
      let regexPattern;
      if (item.isDecimalPoint && item.hasOwnProperty("decimalValue")) {
        decimalPlaces = item.decimalValue ? parseFloat(item.decimalValue, 10) : null;
        // Allow any decimal digits while typing; precision is enforced on blur by numberFormatting
        regexPattern = decimalPlaces !== null
          ? /^-?\d*\.?\d*$/
          : /^-?\d*$/;
      } else if (item.isDecimalPoint && !item.hasOwnProperty("decimalValue")) {
        regexPattern = /^-?\d*\.?\d*$/;
      } else {
        regexPattern = /^-?\d*$/;
      }
      const sanitizedNumber = value.replace(/[^0-9.-]/g, "");
      if (regexPattern.test(sanitizedNumber)) {
        if (item.isThousandSeparator === true) {
          const formattedNumber = sanitizedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          updatedItem = { ...item, value: formattedNumber, defaultValue: "" };
          list.formArray[idx] = updatedItem;
          setValue(fieldId, formattedNumber);
        } else {
          updatedItem = { ...item, value: sanitizedNumber, defaultValue: "" };
          list.formArray[idx] = updatedItem;
          setValue(fieldId, sanitizedNumber);
        }
      }
    } else {
      updatedItem = { ...item, value, defaultValue: "" };
      list.formArray[idx] = updatedItem;
      setValue(fieldId, value);
    }

    // ── Step 2: Apply visibility rules (O(1) target lookup per rule) ──────────
    // Helper: create a new immutable object for the target field at index j.
    const applyRuleToTarget = (j, rule, conditionMet) => {
      const tgt = list.formArray[j];
      if (conditionMet) {
        const newVal = tgt.defaultValue !== "" ? tgt.defaultValue : tgt.value;
        list.formArray[j] = { ...tgt, show: rule.r_show, value: newVal };
        if (tgt.defaultValue !== "") setValue(tgt.name, tgt.defaultValue);
      } else {
        list.formArray[j] = { ...tgt, value: "", show: !rule.r_show };
        setValue(tgt.name, "");
      }
    };

    const tempRules = updatedItem.rules?.length > 0 ? updatedItem.rules : [];

    if (updatedItem.type !== "Radio") {
      for (const rule of tempRules) {
        if (rule.r_field === undefined || rule.field === undefined) continue;
        const j = indexByName.get(rule.r_field); // O(1)
        if (j === undefined) continue;
        const currentValue = updatedItem.type === "Number"
          ? removeThousandSeparator(updatedItem.value)
          : updatedItem.value;
        let conditionMet = false;
        switch (rule.r_condition) {
          case "==": conditionMet = currentValue !== "" && currentValue === rule.r_value; break;
          case ">=": conditionMet = currentValue !== "" && currentValue >= rule.r_value; break;
          case "<=": conditionMet = currentValue !== "" && currentValue <= rule.r_value; break;
          case ">": conditionMet = currentValue !== "" && Number(currentValue) > Number(rule.r_value); break;
          case "<": conditionMet = currentValue !== "" && Number(currentValue) < Number(rule.r_value); break;
          default: break;
        }
        applyRuleToTarget(j, rule, conditionMet);
      }
    } else {
      // Radio — only "==" condition is supported
      const temp = updatedItem.values.filter(v => v.value === updatedItem.value);
      for (const rule of tempRules) {
        if (rule.r_field === undefined || rule.field === undefined) continue;
        if (rule.r_condition !== "==") continue;
        const j = indexByName.get(rule.r_field); // O(1)
        if (j === undefined) continue;
        const tgt = list.formArray[j];
        if (updatedItem.value !== "" && temp[0]?.label === rule.r_value) {
          list.formArray[j] = { ...tgt, show: rule.r_show, value: tgt.defaultValue };
        } else {
          list.formArray[j] = { ...tgt, value: "", show: !rule.r_show };
          setValue(tgt.name, "");
        }
      }
    }

    // ── Step 3: Grid show/hide ─────────────────────────────────────────────────
    const prevVisibleGrids = new Set(
      list.formArray
        .filter(f => f.type === "GridComponent" && f.show === true)
        .map(f => f.name)
    );
    list.formArray = handleGridShowHide(fieldId, value, list.formArray);
    setFormArray(list.formArray);

    // Re-open first visible grid after show/hide changes
    setTimeout(() => openFirstVisibleGrid(list.formArray), 0);

    // O(1) lookup for the changed field's grid rules
    const changedField = list.formArray[indexByName.get(fieldId)];
    const ruleGridNames = new Set(
      (changedField?.grid_show_hide_rule || []).map(r => r.gridName)
    );
    const gridsToRefresh = list.formArray.filter(
      f => f.type === "GridComponent" && f.show === true && prevVisibleGrids.has(f.name) && ruleGridNames.has(f.name)
    );
    if (gridsToRefresh.length > 0) {
      setTimeout(() => {
        gridsToRefresh.forEach(g => {
          gridComponentRefs.current[g.name]?.resetGridToInitial?.();
        });
      }, 0);
    }
    const newlyVisibleGrids = list.formArray.filter(
      f => f.type === "GridComponent" && f.show === true && !prevVisibleGrids.has(f.name) && ruleGridNames.has(f.name)
    );
    if (newlyVisibleGrids.length > 0) {
      setTimeout(() => {
        const firstNew = newlyVisibleGrids[0];
        const gridIdx = list.formArray.indexOf(firstNew);
        setOpenGridId(`grid-${firstNew.name || firstNew._id || gridIdx}`);
        setTimeout(() => {
          newlyVisibleGrids.forEach(g => {
            gridComponentRefs.current[g.name]?.resetGridToInitial?.();
          });
        }, 150);
      }, 0);
    }
    // Returned so callers that chain handleAPIChanged can pass the updated
    // array instead of their stale render-time formArray.
    return list.formArray;
  };

  const isMultiSelectParam = (paramName) => {
    const ctrl = formArray.find(c => c.name === paramName);

    return (
      ctrl?.type === "multiselect" ||
      ctrl?.controlType === "multiselect" ||
      Array.isArray(ctrl?.value)
    );
  };

  //normalize value only for multiselect
  const normalizeParamValue = (paramName, val) => {
    if (isMultiSelectParam(paramName)) {
      if (!Array.isArray(val)) return "";
      return val
        .map(v => v?.value)
        .filter(Boolean)
        .join(" ");
    }
    // SAFETY NET
    if (typeof val === "object") {
      return "";
    }
    return String(val);
  };

  const applyFaceCaptureEnroll = (faceField, enrolledValue) => {
    const faceFieldInArray = formArray.find(f => f.name === faceField.name);
    if (faceFieldInArray) {
      faceFieldInArray.value = enrolledValue;
    }
  };

  const applyFaceIdentifyPatch = (faceField, responseData) => {
    // Update FaceRecognition field's own value in both RHF and formArray
    const faceValue = { matched: responseData.matched, _code: responseData._code, confidence: responseData.confidence };
    setValue(faceField.name, faceValue, { shouldDirty: true, shouldValidate: true });
    const faceFieldInArray = formArray.find(f => f.name === faceField.name);
    if (faceFieldInArray) {
      faceFieldInArray.value = faceValue;
    }

    // Patch configured patch fields
    const patchFields = faceField.face_patch_fields || [];
    const data = responseData.data
    patchFields.forEach(fieldName => {
      if (data[fieldName] !== undefined) {
        const rawValue = data[fieldName];
        const value = typeof rawValue === 'number' ? String(rawValue) : rawValue;
        setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
        const field = formArray.find(f => f.name === fieldName);
        if (field) {
          field.value = value;
        }
      }
    });
  };

  // Receipt/bill OCR — same shape as applyFaceIdentifyPatch above. Applies the
  // extracted values to every configured ocr_field AND the scanner's own field
  // in a single pass: RHF setValue drives the (uncontrolled) inputs, the
  // in-place formArray mutation keeps the submit payload in sync, and one
  // setFormArray at the end triggers a single re-render.
  //
  // Must NOT loop fieldChanged() — each fieldChanged copies formArray from its
  // own stale closure and calls setFormArray, so a synchronous loop of them
  // clobbers itself and only the last field survives.
  //
  // A GridComponent ocr_field is different in shape from a scalar one: the
  // backend returns an array of line-item rows (keyed by column columnName,
  // see receiptOcrExtractor.js's grid prompt) instead of one value, and a
  // grid's data lives on formArray[i].gridRows directly — never through RHF's
  // setValue — same "parent sets field.gridRows, DynamicGridComponent ingests
  // it as an auto-populate" contract handleAutoPopulate uses elsewhere in this
  // file. Each row needs a fresh client-side id (MUI DataGrid's row identity);
  // the OCR response has none, so one is generated here with uuidv4(), same
  // generator DynamicGridComponent.js's handleAddNewRow uses.
  const applyReceiptOcrPatch = (scannerField, base64, data) => {
    const ocrFields = scannerField.ocr_fields || [];
    const appliedFields = [];

    ocrFields.forEach(fieldName => {
      if (data[fieldName] === undefined || data[fieldName] === null) return;
      const field = formArray.find(f => f.name === fieldName);

      if (field?.type === "GridComponent") {
        const extractedRows = Array.isArray(data[fieldName]) ? data[fieldName] : [];
        if (extractedRows.length === 0) return;
        const colData = field.columnData || [];
        const gridRows = extractedRows.map((row) => {
          const rowObj = { id: uuidv4() };
          colData.forEach((col) => {
            const cell = row[col.columnName];
            if (cell === undefined || cell === null || cell === "") return;
            rowObj[col.columnName] = typeof cell === "number" && col.columnType !== "Number" ? String(cell) : cell;
          });
          return rowObj;
        });
        field.gridRows = gridRows;
        appliedFields.push(fieldName);
        return;
      }

      const rawValue = data[fieldName];
      const value = typeof rawValue === 'number' ? String(rawValue) : rawValue;
      setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
      if (field) field.value = value;
      appliedFields.push(fieldName);
    });

    // "Save Scanned Image (S3)" (db_save, default on) gates whether the image
    // is kept at all — off means the receipt is used only to fill the
    // configured fields and the base64 never enters formArray/setValue, so it
    // is never submitted or persisted anywhere, not even inline.
    const scannerValue = scannerField.db_save === false
      ? { extraction: data, appliedFields }
      : { base64, extraction: data, appliedFields };
    setValue(scannerField.name, scannerValue, { shouldDirty: true });
    const scannerInArray = formArray.find(f => f.name === scannerField.name);
    if (scannerInArray) scannerInArray.value = scannerValue;

    setIsFormEdited(true);
    setFormArray([...formArray]);
    return appliedFields;
  };

  const triggerMultiPatchRules = async (fieldName, fieldValue, controlsOverride, isInitialLoad = false) => {

    if (!fieldName) return;

    const controlsSource = Array.isArray(controlsOverride) ? controlsOverride : formArray;
    const matchedRules = [];

    controlsSource.forEach(ctrl => {
      ctrl.multiPatchRules?.forEach(rule => {
        if (rule.patchParameter?.includes(fieldName)) {
          matchedRules.push(rule);
        }
      });
    });

    if (!matchedRules.length) return;
    if (!isInitialLoad) setIsSoftLoading(true);

    for (const rule of matchedRules) {
      // Collect all params
      const paramMap = {};
      let ready = true;
      rule.patchParameter.forEach(param => {
        const val = param === fieldName ? fieldValue : getValues(param);
        if (val === undefined || val === null || val === "") {
          ready = false;   // missing dependency
        }
        paramMap[param] = val;
      });

      //  DO NOT CALL API until all params exist
      if (!ready) {
        rule.patchFields.forEach(f => {
          setValue(f, "", {
            shouldDirty: true,
            shouldValidate: true
          });
        });

        const calcFieldsToClear = [];
        controlsSource.forEach(ctrl => {
          if (!ctrl.calc_rule || ctrl.calc_rule.length === 0) return;
          ctrl.calc_rule.forEach(cr => {
            if (
              Array.isArray(cr.list) &&
              cr.list.some(f => rule.patchFields.includes(f)) &&
              cr.binding_variable &&
              !calcFieldsToClear.includes(cr.binding_variable)
            ) {
              calcFieldsToClear.push(cr.binding_variable);
            }
          });
        });
        // Clear calc target fields
        calcFieldsToClear.forEach(f => {
          setValue(f, "", { shouldDirty: true, shouldValidate: true });
        });

        // Also collect api_rules binding_variable targets of patch fields
        const apiFieldsToClear = [];
        controlsSource.forEach(ctrl => {
          if (!rule.patchFields.includes(ctrl.name)) return;
          if (!Array.isArray(ctrl.api_rules) || ctrl.api_rules.length === 0) return;
          ctrl.api_rules.forEach(ar => {
            if (ar.binding_variable && !apiFieldsToClear.includes(ar.binding_variable)) {
              apiFieldsToClear.push(ar.binding_variable);
            }
          });
        });

        // Clear api binding variable fields
        apiFieldsToClear.forEach(f => {
          setValue(f, "", { shouldDirty: true, shouldValidate: true });
        });

        //cascade clear fields from patched field's own multiPatchRules
        const cascadeFieldsToClear = [];
        rule.patchFields.forEach(patchField => {
          const patchedCtrl = controlsSource.find(ctrl => ctrl.name === patchField);
          patchedCtrl?.multiPatchRules?.forEach(cascadeRule => {
            cascadeRule.patchFields.forEach(cf => {
              if (!cascadeFieldsToClear.includes(cf)) {
                cascadeFieldsToClear.push(cf);
                setValue(cf, "", { shouldDirty: true, shouldValidate: true });
              }
            });
          });
        });

        // Update local state — patch fields + calc fields + api fields cleared
        controlsSource.forEach(ctrl => {
          if (rule.patchFields.includes(ctrl.name)) ctrl.value = "";
          if (calcFieldsToClear.includes(ctrl.name)) ctrl.value = "";
          if (apiFieldsToClear.includes(ctrl.name)) ctrl.value = "";
          if (cascadeFieldsToClear.includes(ctrl.name)) ctrl.value = "";
        });

        setFormArray(prev =>
          prev.map(ctrl => {
            if (rule.patchFields.includes(ctrl.name)) return { ...ctrl, value: "" };
            if (calcFieldsToClear.includes(ctrl.name)) return { ...ctrl, value: "" };
            if (apiFieldsToClear.includes(ctrl.name)) return { ...ctrl, value: "" };
            if (cascadeFieldsToClear.includes(ctrl.name)) return { ...ctrl, value: "" };
            return ctrl;
          })
        );
        if (!isInitialLoad) setIsSoftLoading(false);
        return;
      }

      // Build URL
      let url = rule.multiPatchUrl;
      Object.entries(paramMap).forEach(([k, v]) => {
        const normalizedValue = normalizeParamValue(k, v);
        url = url.replace(
          `@${k}`,
          encodeURIComponent(normalizedValue)
        );
      });

      const callKey = `${url}`;
      //prevent duplicate api calls
      if (multiPatchCallCache.current.get(callKey)) {
        continue;
      }
      multiPatchCallCache.current.set(callKey, true);
      try {
        const res = await authAxios.get(`/api/v1/${url}`);
        if (res?.data?.statusCode !== 200) continue;
        const apiData = res.data.data || res.data.Data || res.data;
        const apiMap = apiData.values || apiData || {};

        // patch RHF — iterate apiMap keys, not just patchFields
        Object.keys(apiMap).forEach(f => {
          const targetField = controlsSource.find(ctrl => ctrl.name === f);
          if (!targetField) return;
          const patchedValue = apiMap[f];
          const finalValue = targetField?.type === "MultiSelect"
            ? (Array.isArray(patchedValue) ? patchedValue : [patchedValue]) : patchedValue;
          targetField.value = finalValue;
          setValue(f, finalValue, {
            shouldDirty: true,
            shouldValidate: true
          });
        });

        let updatedFormArray;
        setFormArray(prev => {
          updatedFormArray = prev.map(ctrl =>
            apiMap[ctrl.name] !== undefined
              ? { ...ctrl, value: apiMap[ctrl.name] }
              : ctrl
          );
          return updatedFormArray;
        });
        // updatedFormArray is captured synchronously from the setArray updater above —
        // no need to yield to the event loop before using it in the chain below.

        for (const f of Object.keys(apiMap)) {
          const targetField = (updatedFormArray?.length ? updatedFormArray : controlsSource).find(ctrl => ctrl.name === f);
          if (!targetField) continue;

          // Trigger calculation rules
          if (targetField.calc_rule && targetField.calc_rule.length > 0) {
            await onChangeCalculation(targetField.calc_rule, updatedFormArray);
          }

          // Trigger API rules
          if (targetField.api_bool && targetField.api_rules?.length > 0) {
            await handleAPIChanged(
              targetField.name,
              apiMap[f],
              targetField.api_rules,
              updatedFormArray,
              isInitialLoad
            );
          }
          await triggerMultiPatchRules(targetField.name, apiMap[f], updatedFormArray?.length ? updatedFormArray : controlsSource, isInitialLoad);
          // Trigger grid auto-populate if patched field is a grid parameter
          await handleGridApiTrigger(targetField.name, apiMap[f], isInitialLoad);
        }
      } catch (err) {
        rule.patchFields.forEach(f => {
          setValue(f, "", {
            shouldDirty: true,
            shouldValidate: true
          });
        });
        toast.error(err?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 2000 });
        // CLEAR LOCAL STATE
        setFormArray(prev =>
          prev.map(ctrl =>
            rule.patchFields.includes(ctrl.name)
              ? { ...ctrl, value: "" }
              : ctrl
          )
        );
      } finally {
        setTimeout(() => {
          multiPatchCallCache.current.delete(callKey);
        }, 300);
      }
    }
    if (!isInitialLoad) setIsSoftLoading(false);
  };

  // Extract base field (remove _number suffix)
  const getBaseFieldName = (name = "") =>
    name.replace(/_\d+$/, "");

  // Extract suffix (_1, _2, etc)
  const getFieldSuffix = (name = "") => {
    const match = name.match(/(_\d+)$/);
    return match ? match[1] : "";
  };

  // Convert API field -> formgroup field
  const withSuffix = (baseName, suffix) =>
    suffix ? `${baseName}${suffix}` : baseName;

  const triggerFormgroupMultiPatch = async (fieldName, fieldValue, isInitialLoad = false) => {

    if (!fieldName) return;

    const baseField = getBaseFieldName(fieldName);
    const suffix = getFieldSuffix(fieldName); // "", _1, _2
    const matchedRules = [];
    formArray.forEach(ctrl => {
      ctrl.formgroupMultipatchRules?.forEach(rule => {
        if (rule.patchParameter?.includes(baseField)) {
          matchedRules.push({ rule, ctrl });
        }
      });
    });

    if (!matchedRules.length) return;
    if (!isInitialLoad) setIsSoftLoading(true);
    for (const { rule } of matchedRules) {

      const paramMap = {};
      let ready = true;
      //  Build API params using BASE field names
      rule.patchParameter.forEach(param => {
        const paramWithSuffix = withSuffix(param, suffix);

        const val =
          paramWithSuffix === fieldName
            ? fieldValue
            : getValues(paramWithSuffix);

        if (val === undefined || val === null || val === "") {
          ready = false;
        }
        paramMap[param] = val; // API wants BASE names
      });

      if (!ready) {
        //CLEAR PATCHED FIELDS (suffix-aware)
        rule.patchFields.forEach(baseFieldName => {
          const targetField = withSuffix(baseFieldName, suffix);

          setValue(targetField, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        });

        //  Find calc TARGET fields (binding_variable) from patch field's calc_rule
        const calcFieldsToClear = [];
        formArray.forEach(ctrl => {
          const ctrlBase = getBaseFieldName(ctrl.name);
          const ctrlSuffix = getFieldSuffix(ctrl.name);
          if (ctrlSuffix !== suffix || !rule.patchFields.includes(ctrlBase)) return;

          if (Array.isArray(ctrl.calc_rule)) {
            ctrl.calc_rule.forEach(cr => {
              if (cr.binding_variable && !calcFieldsToClear.includes(cr.binding_variable)) {
                calcFieldsToClear.push(cr.binding_variable); // already fully suffixed
              }
            });
          }
        });

        //  Clear calc fields (suffix-aware)
        calcFieldsToClear.forEach(fullFieldName => {
          setValue(fullFieldName, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        });

        //  Build clearedFormArray — clear only this row's patch + calc fields
        // Also collect api_rules binding_variable targets of patch fields (suffix-aware)
        const apiFieldsToClear = [];
        formArray.forEach(ctrl => {
          const ctrlBase = getBaseFieldName(ctrl.name);
          const ctrlSuffix = getFieldSuffix(ctrl.name);
          if (ctrlSuffix !== suffix || !rule.patchFields.includes(ctrlBase)) return;
          if (!Array.isArray(ctrl.api_rules) || ctrl.api_rules.length === 0) return;
          ctrl.api_rules.forEach(ar => {
            if (ar.binding_variable && !apiFieldsToClear.includes(ar.binding_variable)) {
              // binding_variable is already suffixed on duplicated rows
              apiFieldsToClear.push(ar.binding_variable);
            }
          });
        });

        // Clear api binding variable fields
        apiFieldsToClear.forEach(f => {
          setValue(f, "", { shouldDirty: true, shouldValidate: true });
        });

        //  Build clearedFormArray — clear patch + calc + api fields
        const clearedFormArray = formArray.map(ctrl => {
          const ctrlBase = getBaseFieldName(ctrl.name);
          const ctrlSuffix = getFieldSuffix(ctrl.name);
          if (ctrlSuffix !== suffix) return ctrl;
          if (rule.patchFields.includes(ctrlBase)) return { ...ctrl, value: "" };
          if (calcFieldsToClear.includes(ctrl.name)) return { ...ctrl, value: "" };
          // api fields — match by full name (already suffixed)
          if (apiFieldsToClear.includes(ctrl.name)) return { ...ctrl, value: "" };
          return ctrl;
        });

        setFormArray(clearedFormArray);

        //  Recalculate sum + aggregation using remaining row values
        // Do NOT clear them — other rows still contribute
        for (const baseFieldName of rule.patchFields) {
          const sourceField = clearedFormArray.find(ctrl =>
            getBaseFieldName(ctrl.name) === baseFieldName &&
            getFieldSuffix(ctrl.name) === suffix
          );

          if (!sourceField) continue;

          if (sourceField.sumRule) {
            await triggerTotalCalculation(sourceField.sumRule, clearedFormArray);
          }

          if (sourceField.aggregationRule && sourceField.aggregationRule.length > 0) {
            await triggerAggregationTypeCalculation(sourceField.aggregationRule, clearedFormArray);
          }
        }
        //recursively cascade clear children of each cleared patchField
        for (const baseFieldName of rule.patchFields) {
          const suffixedName = withSuffix(baseFieldName, suffix);
          const clearedField = formArray.find(ctrl => ctrl.name === suffixedName);

          if (
            clearedField &&
            Array.isArray(clearedField.formgroupMultipatchRules) &&
            clearedField.formgroupMultipatchRules.length > 0
          ) {
            await triggerFormgroupMultiPatch(suffixedName, "", isInitialLoad);
          }
        }
        if (!isInitialLoad) setIsSoftLoading(false);
        continue;
      }
      // Build URL
      let url = rule.multiPatchUrl;
      Object.entries(paramMap).forEach(([k, v]) => {
        const normalizedValue = normalizeParamValue(k, v);
        url = url.replace(
          `@${k}`,
          encodeURIComponent(normalizedValue)
        );
      });

      const callKey = `${url}-${suffix}`;
      if (multiPatchCallCache.current.get(callKey)) continue;
      multiPatchCallCache.current.set(callKey, true);

      try {
        const res = await authAxios.get(`/api/v1/${url}`);
        if (res?.data?.statusCode !== 200) continue;

        const apiData = res.data.data || res.data.Data || res.data;
        const apiMap = apiData.values || apiData || {};

        //Patch RHF with SUFFIXED fields
        rule.patchFields.forEach(baseFieldName => {
          const targetField = withSuffix(baseFieldName, suffix);

          if (apiMap[baseFieldName] !== undefined) {
            const destinationField = formArray.find(ctrl => ctrl.name === baseFieldName);
            const patchedValue = apiMap[baseFieldName];
            const finalValue = destinationField?.type === "MultiSelect"
              ? (Array.isArray(patchedValue) ? patchedValue : [patchedValue]) : patchedValue;
            setValue(targetField, finalValue, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        });
        //Patch local formArray state
        setFormArray(prev =>
          prev.map(ctrl => {
            const ctrlBase = getBaseFieldName(ctrl.name);
            const ctrlSuffix = getFieldSuffix(ctrl.name);
            if (ctrlSuffix !== suffix) return ctrl;
            if (apiMap[ctrlBase] !== undefined) {
              return { ...ctrl, value: apiMap[ctrlBase] };
            }
            return ctrl;
          })
        );
        // Trigger calc_rule, sumRule, aggregationRule for each patched field
        const updatedFormArray = formArray.map(ctrl => {
          const ctrlBase = getBaseFieldName(ctrl.name);
          const ctrlSuffix = getFieldSuffix(ctrl.name);
          if (ctrlSuffix === suffix && apiMap[ctrlBase] !== undefined) {
            return { ...ctrl, value: apiMap[ctrlBase] };
          }
          return ctrl;
        });
        for (const baseFieldName of rule.patchFields) {
          if (apiMap[baseFieldName] === undefined) continue;
          const suffixedName = withSuffix(baseFieldName, suffix);
          const targetField = updatedFormArray.find(ctrl => ctrl.name === suffixedName);
          if (!targetField) continue;

          // Trigger calc_rule
          if (targetField.calc_rule && targetField.calc_rule.length > 0) {
            await onChangeCalculation(targetField.calc_rule, updatedFormArray);
          }
          //  Trigger sumRule (was missing — this is why sum field wasn't updating)
          if (targetField.sumRule) {
            await triggerTotalCalculation(targetField.sumRule, updatedFormArray);
          }
          // Trigger aggregationRule
          if (targetField.aggregationRule && targetField.aggregationRule.length > 0) {
            await triggerAggregationTypeCalculation(targetField.aggregationRule, updatedFormArray);
          }
          if (targetField.api_bool && targetField.api_rules?.length > 0) {
            // apiMap uses base names — so strip suffix when checking
            const alreadyPatchedByApi = targetField.api_rules.every(apiRule =>
              apiMap[getBaseFieldName(apiRule.binding_variable)] !== undefined
            );

            if (!alreadyPatchedByApi) {
              // fieldwithAt has base names — add suffix to get actual form field
              const allParamsFilled = targetField.api_rules.every(apiRule =>
                (apiRule.fieldwithAt || []).every(param => {
                  const val = getValues(withSuffix(getBaseFieldName(param), suffix));
                  return val !== undefined && val !== null && val !== "";
                })
              );
              if (allParamsFilled) {
                await handleAPIChanged(
                  targetField.name,
                  apiMap[baseFieldName],
                  targetField.api_rules,
                  updatedFormArray,
                  isInitialLoad
                );
              }
            }
          }
          await triggerFormgroupMultiPatch(targetField.name, apiMap[baseFieldName], isInitialLoad)
        }

      } catch (err) {
        rule.patchFields.forEach(baseFieldName => {
          const targetField = withSuffix(baseFieldName, suffix);

          setValue(targetField, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        });
        toast.error(err?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 2000 });
        setFormArray(prev =>
          prev.map(ctrl => {
            const ctrlBase = getBaseFieldName(ctrl.name);
            const ctrlSuffix = getFieldSuffix(ctrl.name);

            if (
              ctrlSuffix === suffix &&
              rule.patchFields.includes(ctrlBase)
            ) {
              return { ...ctrl, value: "" };
            }
            return ctrl;
          })
        );
      } finally {
        setTimeout(() => {
          multiPatchCallCache.current.delete(callKey);
        }, 300);
      }
    }
    if (!isInitialLoad) setIsSoftLoading(false);
  };

  //Geolocation field change
  const fieldChangedGeo = (fieldtype, fieldId, value) => {
    // Container children live one level below formArray's own entries — same
    // gap fieldChanged had (see its own comment). Without this the .map()
    // below would just pass every item through unchanged, silently no-op'ing.
    if (!formArray.some((f) => f.name === fieldId)) {
      setFormArray((prev) => prev.map((f) => {
        if (f.type !== "Container" || !Array.isArray(f.controls)) return f;
        const childIdx = f.controls.findIndex((c) => c.name === fieldId);
        if (childIdx === -1) return f;
        const child = f.controls[childIdx];
        const prevGeo = child.value?.longitude !== undefined
          ? child.value
          : { latitude: "", longitude: "" };
        const newGeo = { ...prevGeo, [fieldtype]: Number(value) };
        setValue(fieldId, newGeo);
        const nextControls = [...f.controls];
        nextControls[childIdx] = { ...child, value: newGeo };
        return { ...f, controls: nextControls };
      }));
      return;
    }
    const updatedArray = formArray.map(item => {
      if (item.name !== fieldId) return item;

      const prevGeo = item.value?.longitude !== undefined
        ? item.value
        : { latitude: "", longitude: "" };

      const newGeo = {
        ...prevGeo,
        [fieldtype]: Number(value),
      };

      setValue(fieldId, newGeo);
      return { ...item, value: newGeo };
    });
    setFormArray(updatedArray);
  };

  // api field change
  const handleAPIChanged = async (fieldName, value, rules, formArray, isInitialLoad = false) => {
    let notSatisfiedArray = [...formArray]; // Start with the full array
    // `formArray` can predate a user edit (e.g. the load-time replay runs while
    // the form is already interactive). When merging back into state, keep the
    // state entry for a user-edited field unless this call changed its value.
    const valuesAtStart = new Map(notSatisfiedArray.map(f => [f.name, f.value]));
    const keepStateEntry = (current, incoming) =>
      current !== incoming &&
      userEditedFieldsRef.current.has(incoming.name) &&
      incoming.value === valuesAtStart.get(incoming.name);
    let field = notSatisfiedArray.filter(item => Array.isArray(item?.relatedFormgroupId) && item?.relatedFormgroupId.length > 0 && item.name === fieldName);

    let varTest = notSatisfiedArray.filter((data) => data.name === fieldName);

    field.forEach((fld) => {
      if (fld?.relatedFormgroupId && fld.relatedFormgroupId.length > 0 && varTest[0]?.hasOutsideParamRelation === false) {
        let idCounts = fld.relatedFormgroupId; // array of IDs

        // reset related items
        notSatisfiedArray
          .filter((obj) => idCounts.includes(obj?.group_id) && !obj?.id)
          .forEach((item) => {
            const { name } = item;
            item.value = "";      // empty value
            setValue(name, "");   // reset field explicitly
          });

        if (varTest[0]?.formgroup_child !== true) {
          // remove child formgroups
          notSatisfiedArray = notSatisfiedArray.filter(
            (obj) => !(idCounts.includes(obj?.group_id) && obj?.formgroup_child === true)
          );

          // handle parent_last → set add_new
          notSatisfiedArray
            .filter((data) => data.parent_last === true && idCounts.includes(data?.group_id))
            .forEach((item) => (item.add_new = true));

          // reset counters for group_first
          notSatisfiedArray
            .filter((data) => data.group_first === true && idCounts.includes(data?.group_id))
            .forEach((item) => {
              item.group_parent_count = 0;
              item.group_array_index = 1;
            });
        }
      }
    });

    if (rules !== undefined) {
      if (rules.length > 0) {
        let tempRules = rules;
        let list = { notSatisfiedArray };
        list.notSatisfiedArray.forEach((item) => {
          const ruleMatch = tempRules.find(
            (rule) => rule.binding_variable === item.name
          );

          if (ruleMatch) {
            // reset current item — preserve AdvancedSelect value on initial load so saved submission value shows
            const keepValue = isInitialLoad && item.type === "AdvancedSelect";
            if (!keepValue) {
              item.value = "";
              setValue(ruleMatch.binding_variable, "");
            }
            item.options = [];
            item.filterArray = [];
            // check if this field has api_rules → reset its children too
            const childData = list.notSatisfiedArray.filter(
              (data) => data.name === ruleMatch.binding_variable
            );

            if (childData[0]?.api_rules?.length > 0) {
              childData[0].api_rules.forEach((childRule) => {
                const childField = list.notSatisfiedArray.find(
                  (f) => f.name === childRule.binding_variable
                );
                if (childField) {
                  childField.value = "";
                  childField.options = [];
                  childField.filterArray = [];
                  setValue(childRule.binding_variable, "");
                }
              });
            }
            //cascade clear multiPatch fields of the cleared field
            if (childData[0]?.multiPatchRules?.length > 0) {
              childData[0].multiPatchRules.forEach(multiPatchRule => {
                multiPatchRule.patchFields.forEach(patchField => {
                  const patchedItem = list.notSatisfiedArray.find(f => f.name === patchField);
                  if (patchedItem) {
                    patchedItem.value = "";
                    patchedItem.options = [];
                    patchedItem.filterArray = [];
                  }
                  setValue(patchField, "");
                });
              });
            }
          }
        });
        // When parent is cleared, child fields are cleared above but handleGridApiTrigger
        // is never called (it's inside the value !== "" block below). Grids that use
        // a cleared child field as an API param must also be cleared here.
        //
        // Bug 3 fix: walk api_rules + multiPatchRules transitively so that grids whose
        // parameter is a cascade descendant beyond direct tempRules (e.g. A → B → C
        // with C as the grid param) also reset when an ancestor is cleared. Without
        // this, handleGridApiTrigger only fires for tempRules' direct children.
        const collectClearedTransitive = (rules, visited) => {
          const out = [];
          (rules || []).forEach(rule => {
            const bindingVar = rule?.binding_variable;
            if (!bindingVar || visited.has(bindingVar)) return;
            visited.add(bindingVar);
            const item = list.notSatisfiedArray.find(f => f.name === bindingVar);
            if (!item) return;
            if (item.value === "" || item.value == null) out.push(bindingVar);
            if (Array.isArray(item.api_rules) && item.api_rules.length > 0) {
              out.push(...collectClearedTransitive(item.api_rules, visited));
            }
            if (Array.isArray(item.multiPatchRules)) {
              item.multiPatchRules.forEach(mp => {
                (mp.patchFields || []).forEach(pf => {
                  if (visited.has(pf)) return;
                  visited.add(pf);
                  const pi = list.notSatisfiedArray.find(f => f.name === pf);
                  if (pi && (pi.value === "" || pi.value == null)) out.push(pf);
                });
              });
            }
          });
          return out;
        };
        const clearedBindingVars = collectClearedTransitive(tempRules, new Set());
        for (const clearedVar of clearedBindingVars) {
          await handleGridApiTrigger(clearedVar, "");
        }

        // Reset any grid whose autopopulate / multi-value param is one of the
        // cleared variables. We MUTATE notSatisfiedArray in place so the
        // subsequent setArray call inside the for-loop's else branch (which
        // uses [...notSatisfiedArray]) preserves the cleared gridRows instead
        // of clobbering them with the pre-clear values. Triggering the grid's
        // own gridRows useEffect (CASE 1, empty parentRows) handles the rest —
        // without touching MUI's rowModesModel directly from the cascade.
        if (clearedBindingVars.length > 0) {
          const clearedSet = new Set(clearedBindingVars);
          const isParamCleared = (paramConfig) => {
            if (!paramConfig) return false;
            if (typeof paramConfig === "string") return clearedSet.has(paramConfig);
            if (paramConfig.type && paramConfig.type !== "formField") return false;
            return paramConfig.name && clearedSet.has(paramConfig.name);
          };
          (notSatisfiedArray || []).forEach((f) => {
            if (f?.type !== "GridComponent") return;
            const gridApiParams = Array.isArray(f.gridApi) && f.gridApi[0]?.apiParams;
            const mvParams = Array.isArray(f.multivalueApiRules) && f.multivalueApiRules[0]?.apiParams;
            const affected =
              (Array.isArray(gridApiParams) && gridApiParams.some(isParamCleared)) ||
              (Array.isArray(mvParams) && mvParams.some(isParamCleared));
            if (affected) {
              f.gridRows = [];
              f.gridRev = undefined;
            }
          });
        }
        for (let n = 0; n < tempRules.length; n++) {
          let main_url = resolveRuleUrl(tempRules[n]);
          if (
            (typeof value === "string" && value !== undefined && value !== null && value.trim() !== "") ||
            (Array.isArray(value) && value.length > 0)
          ) {
            if (tempRules[n].fieldwithAt.length > 0) {
              for (let i = 0; i < tempRules[n].fieldwithAt.length; i++) {
                let new_url = main_url;
                let value = getValues(tempRules[n].fieldwithAt[i]);
                let replace_url = new_url.replace(
                  "@" + tempRules[n].fieldwithAt[i],
                  value
                );
                main_url = replace_url;
              }
              const url = new URL(main_url);
 
// url.protocol = "http:";
// url.host = "localhost:3001";
 
// const newUrl = url.toString();
              if (!isInitialLoad) setIsSoftLoading(true);
              try {
                // const api_result = await apiFieldAxios.get(newUrl);
 const api_result = await apiFieldAxios.get(main_url);
                if (api_result.data.statusCode === 200) {
                  const { type, options, value } = api_result.data;
                  if (type === "select" || type === "multiSelect" || type === "AdvancedSelect") {
                    // buildFieldIndexMap indexes a Container's own children by
                    // name alongside every top-level field (same object
                    // references — a match here mutates the field wherever it
                    // actually lives), unlike a plain `.map()` over
                    // `list.notSatisfiedArray`, which only ever sees the flat
                    // top level and so silently no-ops when binding_variable
                    // is a Container-nested field. Same pattern already
                    // established in fieldSelectChanged above.
                    const boundField = buildFieldIndexMap(list.notSatisfiedArray).get(tempRules[n].binding_variable);
                    if (
                      boundField &&
                      (boundField.sub_type === "select" || boundField.sub_type === "multiSelect" || boundField.type === "AdvancedSelect")
                    ) {
                      const filteredOptions = options.filter(
                        (option) => option.value !== null
                      );
                      boundField.filterArray = filteredOptions;
                      boundField.options = filteredOptions;
                      boundField.value = value;
                      setValue(tempRules[n].binding_variable, value);
                      triggerFormgroupMultiPatch(tempRules[n].binding_variable, value)
                    }
                    const updatedFormArray = list.notSatisfiedArray;
                    //  Re-evaluate ApiField visibility rules after API field value is set
                    let evaluatedArray = reEvaluateApiFieldVisibility(updatedFormArray);
                    // Use functional update so a concurrent handleGridApiTrigger write isn't overwritten
                    setFormArray(prevArray => {
                      const _evalMap = new Map(evaluatedArray.map(f => [f.name, f]));
                      return prevArray.map(f => {
                        const u = _evalMap.get(f.name);
                        if (!u || keepStateEntry(f, u)) return f;
                        if (f.type === 'GridComponent') return { ...u, gridRows: f.gridRows, gridRev: f.gridRev };
                        return u;
                      });
                    });
                    // During initial load, run multipatch once after all API-prefilled values settle.
                    if (!isInitialLoad) {
                      await triggerMultiPatchRules(tempRules[n].binding_variable, value, evaluatedArray);
                    }
                    await triggerFormgroupMultiPatch(tempRules[n].binding_variable, value, isInitialLoad);
                    // Trigger grid auto-populate if patched field is a grid parameter
                    await handleGridApiTrigger(tempRules[n].binding_variable, value, isInitialLoad);
                    let api_binding_variable_rule = notSatisfiedArray.filter(
                      (data) => data.name === tempRules[n].binding_variable
                    );
                    if (api_binding_variable_rule[0]?.rules?.length > 0) {
                      // await fieldSelectChanged(api_binding_variable_rule[0]?.name, api_binding_variable_rule[0]?.value, updatedFormArray)
                      await fieldSelectChanged(api_binding_variable_rule[0]?.name, api_binding_variable_rule[0]?.value, evaluatedArray)
                    }

                    if (
                      api_binding_variable_rule[0]?.api_bool === true &&
                      api_binding_variable_rule[0]?.value !== ""
                    ) {
                      await handleAPIChanged(
                        api_binding_variable_rule[0]?.name,
                        api_binding_variable_rule[0]?.value,
                        api_binding_variable_rule[0]?.api_rules,
                        evaluatedArray,
                        isInitialLoad
                      );
                    }
                    const evaluatedMap = new Map(evaluatedArray.map(f => [f.name, f]));
                    setFormArray(prevArray => {
                      const merged = prevArray.map(f => {
                        const u = evaluatedMap.get(f.name);
                        if (!u || keepStateEntry(f, u)) return f;
                        if (f.type === 'GridComponent') {
                          return { ...u, gridRows: f.gridRows, gridRev: f.gridRev };
                        }
                        return u;
                      });
                      return applyInitialGridVisibility(merged);
                    });
                    if (allsubFormArray.length !== 0) {
                      for (let i = 0; i < allsubFormArray.length; i++) {
                        if (allsubFormArray[i].form_name === displayNameRef.current) {
                          allsubFormArray[i].controls = evaluatedArray;
                        }
                      }
                    }
                  }
                  else {
                    setValue(tempRules[n].binding_variable, value);
                    // Same Container-nested-field fix as the select/multiSelect
                    // branch above — see its comment.
                    const boundField = buildFieldIndexMap(list.notSatisfiedArray).get(tempRules[n].binding_variable);
                    if (boundField) {
                      boundField.value = value;
                    }
                    const updatedFormArray = list.notSatisfiedArray;
                    //  Re-evaluate ApiField visibility rules after API field value is set
                    let evaluatedArray = reEvaluateApiFieldVisibility(updatedFormArray);
                    // Use functional update so a concurrent handleGridApiTrigger write isn't overwritten
                    setFormArray(prevArray => {
                      const _evalMap = new Map(evaluatedArray.map(f => [f.name, f]));
                      return prevArray.map(f => {
                        const u = _evalMap.get(f.name);
                        if (!u || keepStateEntry(f, u)) return f;
                        if (f.type === 'GridComponent') return { ...u, gridRows: f.gridRows, gridRev: f.gridRev };
                        return u;
                      });
                    });
                    if (!isInitialLoad) {
                      await triggerMultiPatchRules(tempRules[n].binding_variable, value, evaluatedArray);
                    }
                    // Trigger grid auto-populate if patched field is a grid parameter
                    await handleGridApiTrigger(tempRules[n].binding_variable, value);
                    let api_binding_variable_rule = formArray.filter(
                      (data) => data.name === tempRules[n].binding_variable
                    );
                    if (
                      api_binding_variable_rule[0]?.api_bool === true &&
                      api_binding_variable_rule[0]?.value !== ""
                    ) {
                      await handleAPIChanged(
                        api_binding_variable_rule[0]?.name,
                        api_binding_variable_rule[0]?.value,
                        api_binding_variable_rule[0]?.api_rules,
                        evaluatedArray,
                        isInitialLoad
                      );
                    }
                    if (type === "number") {
                      if (api_binding_variable_rule[0]?.calc_rule.length > 0) {
                        await onChangeCalculation(api_binding_variable_rule[0].calc_rule, updatedFormArray);
                      }
                      if (api_binding_variable_rule[0]?.sumRule && api_binding_variable_rule[0]?.sumRule?.length > 0) {
                        await triggerTotalCalculation(api_binding_variable_rule[0]?.sumRule, updatedFormArray);
                      }
                      if (api_binding_variable_rule[0]?.aggregationRule && api_binding_variable_rule[0]?.aggregationRule?.length > 0) {
                        await triggerAggregationTypeCalculation(api_binding_variable_rule[0]?.aggregationRule, updatedFormArray);
                      }
                    }
                    //onBlur of api Number field
                    if (api_binding_variable_rule[0]?.calc_rule.length > 0) {
                      await onChangeCalculation(api_binding_variable_rule[0].calc_rule, updatedFormArray);
                    }
                    if (api_binding_variable_rule[0]?.total_calc) {
                      await triggerTotalCalculation(api_binding_variable_rule[0]?.sumRule, updatedFormArray);
                    }
                    if (api_binding_variable_rule[0]?.aggregation_flag === true) {
                      await triggerAggregationTypeCalculation(api_binding_variable_rule[0]?.aggregationRule, updatedFormArray);
                    }
                    const updatedMap = new Map(updatedFormArray.map(f => [f.name, f]));
                    setFormArray(prevArray => {
                      const merged = prevArray.map(f => {
                        const u = updatedMap.get(f.name);
                        if (!u || keepStateEntry(f, u)) return f;
                        if (f.type === 'GridComponent') {
                          return { ...u, gridRows: f.gridRows, gridRev: f.gridRev };
                        }
                        return u;
                      });
                      return applyInitialGridVisibility(merged);
                    });
                    if (allsubFormArray.length !== 0) {
                      for (let i = 0; i < allsubFormArray.length; i++) {
                        if (allsubFormArray[i].form_name === displayNameRef.current) {
                          allsubFormArray[i].controls = updatedFormArray;
                        }
                      }
                    }
                  }
                } else {

                  if (api_result.data.statusCode === 404) {
                    toast.error(api_result.data.message, {
                      autoClose: 5000,
                    });
                  } else {
                    toast.error("Something went wrong. Please contact the admin .", {
                      autoClose: 5000,
                    });
                  }
                }
              } catch (error) {
                toast.error(error.message, "Failed to fetch data. Please try again.", { autoClose: 5000 });
              } finally {
                if (!isInitialLoad) setIsSoftLoading(false);
              }
            }
          } else {
            notSatisfiedArray.forEach((item) => {
              const ruleMatch = tempRules.find(
                (rule) => rule.binding_variable === item.name
              );

              if (ruleMatch) {
                // reset current item
                item.value = "";
                item.options = [];
                item.filterArray = [];
                setValue(ruleMatch.binding_variable, "");
                // check if this field has api_rules → reset its children too
                const childData = list.notSatisfiedArray.filter(
                  (data) => data.name === ruleMatch.binding_variable
                );

                if (childData[0]?.api_rules?.length > 0) {
                  childData[0].api_rules.forEach((childRule) => {
                    const childField = list.notSatisfiedArray.find(
                      (f) => f.name === childRule.binding_variable
                    );
                    if (childField) {
                      childField.value = "";
                      childField.options = [];
                      childField.filterArray = [];
                      setValue(childRule.binding_variable, "");
                    }
                  });
                }
                if (childData[0]?.formgroupMultipatchRules?.length > 0) {
                  // Determine the suffix from the cleared field's name 
                  const suffix = getFieldSuffix(ruleMatch.binding_variable);

                  childData[0].formgroupMultipatchRules.forEach(mpRule => {
                    mpRule.patchFields.forEach(baseFieldName => {
                      const suffixedFieldName = withSuffix(baseFieldName, suffix);

                      const patchedItem = notSatisfiedArray.find(f => f.name === suffixedFieldName);
                      if (patchedItem) {
                        patchedItem.value = "";
                        patchedItem.options = [];
                        patchedItem.filterArray = [];
                      }
                      setValue(suffixedFieldName, "");

                      // Recursively cascade if this patched field also has multipatch rules
                      const suffixedField = notSatisfiedArray.find(f => f.name === suffixedFieldName);
                      if (suffixedField?.formgroupMultipatchRules?.length > 0) {
                        suffixedField.formgroupMultipatchRules.forEach(nestedRule => {
                          nestedRule.patchFields.forEach(nestedBase => {
                            const nestedSuffixed = withSuffix(nestedBase, suffix);
                            const nestedItem = notSatisfiedArray.find(f => f.name === nestedSuffixed);
                            if (nestedItem) {
                              nestedItem.value = "";
                              nestedItem.options = [];
                              nestedItem.filterArray = [];
                            }
                            setValue(nestedSuffixed, "");
                          });
                        });
                      }
                    });
                  });
                }
              }
            });
            setFormArray(applyInitialGridVisibility([...notSatisfiedArray]));
            if (allsubFormArray.length !== 0) {
              for (let i = 0; i < allsubFormArray.length; i++) {
                if (allsubFormArray[i].form_name === displayNameRef.current) {
                  allsubFormArray[i].controls = notSatisfiedArray;
                }
              }
            }
          }
        }
      }
    }
  };

  //  =========button conponent firld change======
  const handleButtonAPIChanged = async (name, buttonRules, formArray) => {
    let buttonparams = buttonRules.buttonParameters; // original param names like "otp"
    let main_url = resolveRuleUrl(buttonRules);

    if (buttonparams.length > 0) {
      let modifiedParams = [...buttonparams];

      if (buttonRules.binding_variable !== name) {
        const lastPart = name?.split("_").pop();
        modifiedParams = buttonparams.map(param => `${param}_${lastPart}`);
      }

      // Build full URL with all replacements before making one API call
      for (let i = 0; i < buttonparams.length; i++) {
        const originalParam = buttonparams[i];     // e.g., "otp"
        const modifiedParam = modifiedParams[i];   // e.g., "otp_1"
        const value = getValues(modifiedParam) ?? "";  // fallback to empty string if undefined/null

        main_url = main_url.replace("@" + originalParam, value);  // replace using original param
      }

      setIsGlobalLoading(true);
      try {
        const api_result = await authAxios.get(main_url);
        const { type, message } = api_result.data;
        if (api_result.data.statusCode === 200) {
          if (type === "sms") {
            showSuccessToast(message);
          }
        } else {
          if (type === "sms") {
            toast.error(message, { autoClose: 5000 });
          }
        }
      } catch (error) {
        const type = error?.response?.data?.type;
        const message = error?.response?.data?.message || "Failed to fetch data. Please try again.";
        if (type) {
          // existing flow
          if (type === "sms") {
            toast.error(message, { autoClose: 5000 });
          }
        } else {
          // type not available → show message directly
          toast.error(message, { autoClose: 5000 });
        }
      } finally {
        setIsGlobalLoading(false);
      }
    }
  };

  const handlenavigatebutton = async (navigation_data) => {
    const {
      navigationForm,
      url,
      type = "GET",
      navigationParameters,
    } = navigation_data;

    if (!navigationForm) {
      toast.error("Navigation form not configured.");
      return;
    }

    if (!url) {
      toast.error("Navigation API URL not configured.");
      return;
    }
    let main_url = resolveRuleUrl(navigation_data);
    // Replace @params
    if (navigationParameters?.length > 0) {
      for (const param of navigationParameters) {
        const value = getValues(param) ?? "";
        if (!value) {
          toast.error(`Please fill the required field: ${param}`);
          return;
        }
        main_url = main_url
          .replace(`@${param}`, value)
          .replace(`${param}=@`, `${param}=${value}`);
      }
    }

    setIsGlobalLoading(true);
    try {
      const api_result = await authAxios.get(main_url);
      const { statusCode, message } = api_result.data;

      // Handle response format
      const responseData =
        api_result.data?.data && typeof api_result.data.data === "object"
          ? api_result.data.data
          : api_result.data;

      if (statusCode === 200 && responseData) {
        // Store in local state
        setNavigationResponseData(responseData);
        // Navigate with response in router state
        navigateTo(`/formsubmission_new/add/${navigationForm}`, {
          state: { navigationResponseData: responseData },
        });
      }
    } catch (error) {
      toast.error(error.message || "Navigation API failed.");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleAutoPopulate = async (populate_field_config) => {
    if (!Array.isArray(populate_field_config) || populate_field_config.length === 0) {
      toast.error("Auto Populate not configured.");
      return;
    }

    for (const rule of populate_field_config) {
      const { apiEndpoint, parameters = [], patchFields = [] } = rule;

      if (!apiEndpoint) continue;

      const queryParams = {};
      let allFilled = true;

      for (const param of parameters) {
        const rhfValue = getValues(param);
        const formField = formArray.find(f => f.name === param);
        const formArrayValue = formField?.value;

        const value = (rhfValue !== undefined && rhfValue !== null && rhfValue !== "")
          ? rhfValue
          : formArrayValue;

        if (value === undefined || value === null || value === "") {
          allFilled = false;
          const fieldLabel = formArray.find(f => f.name === param)?.label || param;
          toast.error(`Please fill the required field: ${fieldLabel}`);
          break;
        }
        queryParams[param] = value;
      }

      if (!allFilled) return;

      const apiUrl = normalizeApiPath(apiEndpoint);
      const queryString = serializeParamsRFC3986(queryParams);
      const fullUrl = queryString ? `${apiUrl}?${queryString}` : apiUrl;

      try {
        setIsGlobalLoading(true);
        const response = await authAxios.get(fullUrl);

        if (response.status === 200 && response.data) {
          const responseData = response.data?.data || [];

          // Build updated formArray locally so triggerGridSumCalculations sees
          // the new gridRows immediately (setArray is async/batched by React).
          let updatedFormArray = formArray;

          for (const patch of patchFields) {
            const fieldName = typeof patch === "string" ? patch : patch?.fieldName;
            const targetType = typeof patch === "string" ? "grid" : (patch?.targetType || "grid");

            if (targetType === "grid") {
              const newRows = Array.isArray(responseData) ? responseData : [];
              updatedFormArray = updatedFormArray.map(field =>
                field.name === fieldName
                  ? {
                    ...field,
                    gridRows: newRows,
                    gridRev: (field.gridRev || 0) + 1,
                  }
                  : field
              );
              setFormArray(updatedFormArray);
            }
          }

          // Trigger calculated fields that depend on grid column sums
          for (const patch of patchFields) {
            const fieldName = typeof patch === "string" ? patch : patch?.fieldName;
            const targetType = typeof patch === "string" ? "grid" : (patch?.targetType || "grid");
            if (targetType === "grid") {
              await triggerGridSumCalculations(fieldName, updatedFormArray);
            }
          }
        } else {
          for (const patch of patchFields) {
            const fieldName = typeof patch === "string" ? patch : patch?.fieldName;
            setFormArray(prevArray =>
              prevArray.map(field =>
                field.name === fieldName
                  ? { ...field, gridRows: [], gridRev: (field.gridRev || 0) + 1 }
                  : field
              )
            );
          }
          toast.error(response.data?.message || "No data found.", { autoClose: 3000 });
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Auto Populate failed.",
          { autoClose: 3000 }
        );
      } finally {
        setIsGlobalLoading(false);
      }
    }
  };

  // ── Calculation and media functions provided by useFormCalculations and useMediaHandlers hooks above.

  // function for delete the sub formgroups
  const handleGroupingDelete = useCallback(async (id, group_id) => {
    // Single pass — builds both complementary arrays without scanning formArray twice
    const updatedArray = [];
    const deleteArray = [];
    for (const obj of formArray) {
      (obj.id === id ? deleteArray : updatedArray).push(obj);
    }

    const deleteDataLast = deleteArray.filter(data => data.total_calc === true);

    // Find the new last item in the group after deletion
    const lastGroup = updatedArray.filter(
      data => data.group_id == group_id && data.group_last === true
    );
    const last_index_array = lastGroup[lastGroup.length - 1];

    // Guard: last_index_array is undefined when all instances of the group are removed
    if (last_index_array) {
      const last_id = last_index_array.id;
      if (last_id != null) {
        const lastItem = updatedArray.find(
          data => data.group_last === true && data.id === last_id
        );
        if (lastItem) lastItem.add_new = true;
      } else {
        // parent_last is used to add or remove "add another"
        const parentLastItem = updatedArray.find(
          data => data.parent_last === true && data.group_id == group_id
        );
        if (parentLastItem) parentLastItem.add_new = true;
      }
    }

    // Decrement addFgArrayIndex and group_array_index for remaining group fields
    updatedArray.forEach(obj => {
      if (obj.addFgArrayIndex > deleteArray[0]?.addFgArrayIndex && obj.group_id === group_id) {
        obj.addFgArrayIndex -= 1;
      }
      if (obj.group_id === group_id && obj.group_first) {
        obj.group_array_index -= 1;
      }
    });

    // Sync allsubFormArray
    if (allsubFormArray.length > 0) {
      const subForm = allsubFormArray.find(f => f.form_name === displayNameRef.current);
      if (subForm) subForm.controls = updatedArray;
    }

    // Recalculate sum fields — awaited to prevent race with count/aggregation below
    for (const field of deleteDataLast) {
      if (field.sumRule !== undefined) {
        await triggerTotalCalculation(field.sumRule, updatedArray);
      }
    }

    await triggerCountValueCalculation(updatedArray);

    const aggregationFields = updatedArray.filter(f => f.aggregationRule?.length > 0);
    for (const field of aggregationFields) {
      await triggerAggregationTypeCalculation(field.aggregationRule, updatedArray);
    }

    // Single setArray after all calculations — avoids intermediate render
    setFormArray([...updatedArray]);
  }, [formArray, allsubFormArray, displayName, triggerTotalCalculation, triggerCountValueCalculation, triggerAggregationTypeCalculation]);


  // function for addind dynamic formgroups
  const handleFormGroupAPITrigger = async (name, value, rules) => {
    if (value && rules && rules.length > 0) {
      // removing the child form groups and resetting the values inside parent formgroup
      let notSatisfiedArray = [...formArray]; // Start with the full array

      for (let rule of rules) {
        // Modify items in the formArray based on the rule
        notSatisfiedArray.forEach((item) => {
          const { group_id, name, apiFLAG } = item;
          if (group_id === rule.group_id) {
            item.value = "";
            setValue(name, item.value);
            if (apiFLAG) {
              item.apiFLAG = false;
            }
          }
        });

        // Update notSatisfiedArray by filtering out the items that satisfy the rule
        notSatisfiedArray = notSatisfiedArray.filter(
          (obj) => !(obj?.group_id == rule.group_id && obj?.formgroup_child === true)
        );

        let hide_parent_show = notSatisfiedArray.filter((data) => data.parent_last === true && data?.group_id == rule.group_id);
        if (hide_parent_show.length > 0) {
          hide_parent_show[0].add_new = true;
        }

        let reset_count = notSatisfiedArray.filter((data) => data.group_first === true && data?.group_id == rule.group_id);
        if (reset_count.length > 0) {
          reset_count[0].group_parent_count = 0;
          reset_count[0].group_array_index = 1;
        }
      }
      try {
        setIsGlobalLoading(true);
        let newArray = notSatisfiedArray; // Initialize newArray outside the loop
        for (let n = 0; n < rules.length; n++) {
          let controlArray = [];
          let controlSecondArray = {};
          let parent_id = "";
          let last_index = "";
          const main_url = resolveRuleUrl(rules[n]);
          const parent_child_data = await apiFieldAxios.get(BASE_API_URL + "/api/v1/formGroup/" + rules[n].group_id + "?submissionDuplicate=true");

          if (value && rules[n].fieldwithAt) {
            let new_url =
              main_url.replace(
                `@${rules[n].fieldwithAt}`,
                encodeURIComponent(getValues(rules[n].fieldwithAt))
              );

            // append uuid only in edit mode
            if (routeParams?.action === "edit" && submissionUuid) {
              new_url += `&uuid=${encodeURIComponent(submissionUuid)}`;
            }

            const api_result = await apiFieldAxios.get(new_url);
            if (api_result.data.statusCode === 200) {
              controlSecondArray = api_result.data.formGroupValues;
              for (let i = 0; i < controlSecondArray.length - 1; i++) {
                const parent_data = structuredClone(parent_child_data.data.data.controls);
                if (parent_data.length > 0) {
                  parent_id = parent_data[0].group_id;
                }
                const templateFields = newArray.filter(f => f?.group_id === parent_id && !f?.id);
                for (const tmplField of templateFields) {
                  const pd = parent_data.find(f => f.name === tmplField.name);
                  if (pd) {
                    pd.api_rules = structuredClone(tmplField.api_rules);
                  }
                }
                const new_data = newArray.filter((data) => data.group_parent === true && data.group_id === parent_id);
                let last_finder = newArray.filter((data) => data.group_last === true && data.group_id === parent_id);
                let last_index_array = await last_finder.slice(-1)[0];
                let name = last_index_array.name;

                last_index = newArray.findIndex((x) => x.name === name);
                if (new_data.length > 0) {
                  new_data[0].group_parent_count = new_data[0].group_parent_count + 1;
                  new_data[0].group_array_index = new_data[0].group_array_index ? new_data[0].group_array_index + 1 : 2;
                }
                const tempArray = await stateUpdate(parent_data, new_data);
                controlArray.push(tempArray);
              }

              const updatedArray = controlArray.map((item, index) => {
                if (index === controlArray.length - 1) {
                  return item;
                } else {
                  if (item && item.length > 0) {
                    item[item.length - 1] = {
                      ...item[item.length - 1],
                      add_new: false, // or set add new value false except last one
                    };
                  }
                  return item;
                }
              });

              const combinedArray = updatedArray.reduce(
                (result, currentArray) => [...result, ...currentArray],
                []
              );

              const indexToMerge = last_index + 1;
              newArray = [
                ...newArray.slice(0, indexToMerge),
                ...combinedArray,
                ...newArray.slice(indexToMerge),
              ];

              let hide_parent_show = newArray.filter((data) => data.parent_last === true && data.group_id === parent_id);
              if (hide_parent_show.length > 0) {
                hide_parent_show[0].add_new = false;
              }
              setIsGlobalLoading(false);
              await bindControlArray(controlSecondArray, newArray, rules[n].group_id);
              await delay(0);
            } else if (api_result.data.statusCode === 400) {
              toast.error(api_result?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
              setIsGlobalLoading(false);
            }
          }
        }

      } catch (error) {
        toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
        setIsGlobalLoading(false)
      }
    } else {

      let notSatisfiedArray = [...formArray]; // Start with the full array
      for (let rule of rules) {
        // Modify items in the formArray based on the rule
        notSatisfiedArray.forEach((item) => {
          const { group_id, name, apiFLAG } = item;
          if (group_id === rule.group_id) {
            item.value = "";
            setValue(name, item.value);
            if (apiFLAG) {
              item.apiFLAG = false;
            }
          }
        });

        // Update notSatisfiedArray by filtering out the items that satisfy the rule
        notSatisfiedArray = notSatisfiedArray.filter(
          (obj) => !(obj?.group_id == rule.group_id && obj?.formgroup_child === true)
        );

        let hide_parent_show = notSatisfiedArray.filter((data) => data.parent_last === true && data?.group_id == rule.group_id);

        if (hide_parent_show.length > 0) {
          hide_parent_show[0].add_new = true;
        }

        let reset_count = notSatisfiedArray.filter((data) => data.group_first === true && data?.group_id == rule.group_id);
        if (reset_count.length > 0) {
          reset_count[0].group_parent_count = 0;
          reset_count[0].group_array_index = 1;
        }
      }
      setFormArray([...notSatisfiedArray]);
    }
  };

  const bindControlArray = async (controlSecondArray, newArray, targetGroupId = null) => {
    for (let i = 0; i < controlSecondArray.length; i++) {
      let tempVar = controlSecondArray[i];
      const keysToCheck = Object.keys(tempVar);
      updateValuesInArray(keysToCheck, newArray, tempVar);
    }

    if (newArray.length > 0) {
      for (let i = 0; i < newArray.length; i++) {
        if (
          newArray[i].type === "Text" ||
          newArray[i].type === "Select" ||
          newArray[i].type === "Time" ||
          newArray[i].type === "Date"
        ) {
          if (newArray[i].api_bool && newArray[i].value !== '' && (newArray[i].group_id !== undefined && newArray[i].group_id !== '') &&
            (targetGroupId === null || newArray[i].group_id === targetGroupId)) {
            await handleAPIChanged(newArray[i].name, newArray[i].value, newArray[i].api_rules, newArray);
          }
        } else if (newArray[i].type === "Number") {
          await onChangeCalculation(newArray[i]?.calc_rule, newArray);
          if (newArray[i]?.sumRule) {
            await triggerTotalCalculation(newArray[i]?.sumRule, newArray);
          }
          if (newArray[i]?.aggregationRule) {
            await triggerAggregationTypeCalculation(newArray[i]?.aggregationRule, newArray);
          }
        }
      }
    }
    setFormArray([...newArray]);
    if (allsubFormArray.length !== 0) {
      for (let i = 0; i < allsubFormArray.length; i++) {
        if (allsubFormArray[i].form_name === displayNameRef.current) {
          allsubFormArray[i].controls = newArray;
        }
      }
    }
  };

  // Function to update values in the array based on matching keys and values
  const updateValuesInArray = (keysToCheck, newArray, tempVar) => {
    newArray.forEach((obj) => {
      if (keysToCheck.includes(obj.name)) {
        if (obj.type === "FileUpload") {
          const rawFile = tempVar[obj.name]; // full string with timestamp
          const FileName = rawFile.split('-').slice(1).join('-').trim(); // trimmed
          obj.value = rawFile
          obj.fileName = FileName;       // keep trimmed for display
          obj.originalValue = rawFile;   // keep original for saving back if needed
          setValue(obj.name, rawFile);
        }

        else if (obj.type === "Map") {
          if (!obj.value || obj.value === "N/A" || obj.value.length === 0) {
            obj.value = "";
            obj.area = "";
            setValue(obj.name, "");
          } else {
            try {
              // Ensure nested format
              if (Array.isArray(obj.value) && Array.isArray(obj.value[0]) && typeof obj.value[0][0] === "number") {
                // Means it's [[lng, lat], [lng, lat], ...] → wrap it
                obj.value = [obj.value];
              }
              const coords = Array.isArray(obj.value[0]) ? obj.value[0] : obj.value;
              if (Array.isArray(coords) && coords.length > 2) {
                let area = 0;
                for (let i = 0; i < coords.length - 1; i++) {
                  const [x1, y1] = coords[i];
                  const [x2, y2] = coords[i + 1];
                  area += (x1 * y2 - x2 * y1);
                }
                area = Math.abs(area / 2.0);
                const acres = Number(area / 4047).toFixed(3);
                obj.area = acres;
              }
            } catch (err) {
              obj.area = "";
            }
            setValue(obj.name, obj.value);
          }
        }
        // Independent copy of the "Map" branch above, kept isolated from Map.
        else if (obj.type === "NDVIAnalyzer") {
          if (!obj.value || obj.value === "N/A" || obj.value.length === 0) {
            obj.value = "";
            obj.area = "";
            setValue(obj.name, "");
          } else {
            try {
              if (Array.isArray(obj.value) && Array.isArray(obj.value[0]) && typeof obj.value[0][0] === "number") {
                obj.value = [obj.value];
              }
              const coords = Array.isArray(obj.value[0]) ? obj.value[0] : obj.value;
              if (Array.isArray(coords) && coords.length > 2) {
                let area = 0;
                for (let i = 0; i < coords.length - 1; i++) {
                  const [x1, y1] = coords[i];
                  const [x2, y2] = coords[i + 1];
                  area += (x1 * y2 - x2 * y1);
                }
                area = Math.abs(area / 2.0);
                const acres = Number(area / 4047).toFixed(3);
                obj.area = acres;
              }
            } catch (err) {
              obj.area = "";
            }
            setValue(obj.name, obj.value);
          }
        }
        else if (obj.type === "Number" || obj.type === "CalculatedField") {
          // Always cast to string so it saves/loads as string
          obj.value = tempVar[obj.name] !== undefined ? String(tempVar[obj.name]) : "";
          setValue(obj.name, obj.value);
        }
        else if (obj.type === "Signature" || obj.type === "Image") {
          if (obj.value === "N/A") {
            obj.value = "";
          }
        }
        else {
          obj.value = tempVar[obj.name];
          setValue(obj.name, tempVar[obj.name]);
        }
      }
    });
  };

  const stateUpdate = async (parent_data, new_data) => {
    const uuid = crypto.randomUUID();
    const groupParentCount = new_data[0]?.group_parent_count || 0;
    const groupArrayIndex = new_data[0]?.group_array_index || 1;
    const tempArray = [];
    // Capture original field names before the loop mutates them with suffixes
    const groupFieldOriginalNames = parent_data.map(item => item.name);

    for (const currentData of parent_data) {
      currentData.name += `_${groupParentCount}`;
      currentData.group_name += `(${groupParentCount})`;
      currentData.addFgArrayIndex = groupArrayIndex;
      currentData.apiFLAG = true;
      currentData.formgroup_child = true;

      if (currentData.group_last) {
        currentData.add_new = true;
      }

      if (currentData.hasOwnProperty("isCurrentDate")) {
        if (
          currentData.type === "Date" &&
          currentData.isCurrentDate &&
          currentData.defaultValue === "" &&
          currentData.value === ""
        ) {
          currentData.value = formatDateYMD(new Date());
        }
      }

      if (currentData.defaultValue !== "") {
        currentData.value = currentData.defaultValue;
        setValue(currentData.name, currentData.defaultValue);
      }

      currentData.isFutureDateDisable ??= false;
      currentData.isPastDateDisable ??= false;
      currentData.hidden ??= false;

      if (
        currentData.isCurrentTime &&
        currentData.type === "Time" &&
        currentData.defaultValue === "" &&
        currentData.value === ""
      ) {
        currentData.value = nowHHmm();
        setValue(currentData.name, nowHHmm());
      }

      if (currentData.total_calc) {
        const tempSumRule = {
          total_calc: currentData.total_calc,
          total_binding_var: currentData.total_binding_var,
          total_calc_id: currentData.total_calc_id,
        };
        currentData.sumRule = tempSumRule;
      }
      // --------Aggregation ----------
      if (currentData.aggregation_flag) {
        currentData.aggregationRule = currentData.aggregation_rules;
      }
      // ------------------------------
      currentData.id = uuid;

      if (currentData.rules?.length > 0) {
        for (let j = 0; j < currentData.rules.length; j++) {
          const rule = currentData.rules[j];

          if (rule.r_field !== undefined) {
            rule.r_field += `_${groupParentCount}`;
            rule.field += `_${groupParentCount}`;
          } else if (rule.parent_field !== undefined) {
            rule.parent_field += `_${groupParentCount}`;
            rule.edit_field += `_${groupParentCount}`;
          }
        }
      }

      if (currentData.type === "Select" || currentData.type === "MultiSelect") {
        currentData.filterArray = currentData.options;
        setValue(currentData.name, currentData.value);
      }

      if (currentData.type === "FileUpload" && currentData.value !== undefined) {
        const FileName = (currentData.value).split('-').slice(1).join('-').trim();
        currentData.fileName = FileName;
        setValue(currentData.name, currentData.value);
      }

      if (currentData.type === "ApiField") {
        currentData.sub_type = currentData.sub_type || "text";

        if (currentData.sub_type === "select") {
          currentData.filterArray = currentData.options;
        }
      }

      if (currentData.calc_rule.length > 0) {
        for (let m = 0; m < currentData.calc_rule.length; m++) {
          const rules = currentData.calc_rule[m];
          let calc_equation = rules.expression;
          const list = rules.list;
          let new_equation = "";
          const temp_array = [];
          for (let j = 0; j < list.length; j++) {
            const regex = new RegExp("\\b" + list[j] + "\\b", "g");
            new_equation = calc_equation.replace(
              regex,
              list[j] + `_${groupParentCount}`
            );
            calc_equation = new_equation;
            temp_array.push(list[j] + `_${groupParentCount}`);
          }
          currentData.calc_rule[m].expression = calc_equation;
          currentData.calc_rule[m].binding_variable += `_${groupParentCount}`;
          currentData.calc_rule[m].list = temp_array;
        }
      }
      if (
        currentData.hasOwnProperty("api_rules") &&
        currentData.api_rules.length > 0
      ) {
        for (let t = 0; t < currentData.api_rules.length; t++) {
          currentData.api_rules[t].binding_variable = currentData.api_rules[t].binding_variable + '_' + new_data[0].group_parent_count;
          let api_list_all = currentData.api_rules[t].fieldwithAt;
          // Only suffix fields that belong to this formgroup; outside-group params (e.g. sale_order_number) must stay unsuffixed
          let api_list_inside = api_list_all.filter(item => groupFieldOriginalNames.includes(item));
          let api_temp_array = api_list_all.filter(item => !groupFieldOriginalNames.includes(item));
          let api_url = currentData.api_rules[t].url;
          for (let k = 0; k < api_list_inside.length; k++) {
            let replace_url = api_url.replace('@' + api_list_inside[k], '@' + api_list_inside[k] + '_' + groupParentCount);
            api_url = replace_url;
            api_temp_array.push(api_list_inside[k] + '_' + groupParentCount);
          }
          currentData.api_rules[t].fieldwithAt = api_temp_array;
          currentData.api_rules[t].url = api_url;
        }
      }
      currentData.group_parent = false;
      tempArray.push(currentData);
    }
    return tempArray;
  };

  // function for creating individual formgroups by clicking addnew
  const handleGrouping = async (group_id) => {
    setIsGlobalLoading(true);

    try {
      const parent_child_data = await apiFieldAxios.get(
        `${BASE_API_URL}/api/v1/formGroup/${group_id}?submissionDuplicate=true`
      );
      const parent_data = parent_child_data.data.data.controls;
      const parent_id = parent_data.length > 0 ? parent_data[0].group_id : "";

      // O(1) lookups — replaces repeated Array.includes() and find() calls
      const nameSet = new Set(parent_data.map(item => item.name));
      const parentDataByName = new Map(parent_data.map(f => [f.name, f]));

      // Single formArray pass — replaces 4 separate filter() scans
      const matchedField = [], lastGroup = [], parentGroup = [];
      for (const f of formArray) {
        if (f.group_id === group_id && !f.id) matchedField.push(f);
        if (f.group_last && f.group_id === parent_id) lastGroup.push(f);
        if (f.group_parent && f.group_id === parent_id) parentGroup.push(f);
      }

      // Sync api_rules from current form state into freshly fetched parent_data
      for (const mf of matchedField) {
        const apiField = parentDataByName.get(mf.name);
        if (apiField) apiField.api_rules = structuredClone(mf.api_rules);
      }

      // Guard: both a group-parent and a group-last field must exist to proceed
      if (parentGroup.length === 0 || lastGroup.length === 0) {
        throw new Error("Group configuration incomplete: missing parent or last marker");
      }

      const last_index_array = lastGroup[lastGroup.length - 1];
      const last_index = formArray.findIndex(x => x.name === last_index_array.name);
      const last_id = last_index_array.id;

      // Disable add_new on the current last item (a new last will take its place)
      if (last_id != null) {
        const lastItem = lastGroup.find(d => d.id === last_id);
        if (lastItem) lastItem.add_new = false;
      }

      // Increment counters on the group parent field
      const groupParent = parentGroup[0];
      groupParent.group_parent_count += 1;
      groupParent.group_array_index = groupParent.group_array_index
        ? groupParent.group_array_index + 1
        : 2;
      const count = groupParent.group_parent_count;
      const arrayIndex = groupParent.group_array_index;

      const uuid = crypto.randomUUID();
      const tempArray = [];

      for (let i = 0; i < parent_data.length; i++) {
        const field = parent_data[i]; // alias — eliminates ~30 repeated parent_data[i] accesses

        field.name = `${field.name}_${count}`;
        field.group_name = `${field.group_name}(${count})`;
        field.addFgArrayIndex = arrayIndex;
        field.formgroup_child = true;
        field.isNewlyAdded = true;

        if (field.group_last) field.add_new = true;

        // Value initialisation
        if (field.defaultValue !== "") {
          field.value = field.defaultValue;
          setValue(field.name, field.defaultValue);
        } else if (field.value !== "") {
          setValue(field.name, field.value);
        } else {
          setValue(field.name, "");
        }

        // Auto-fill current date
        if (
          field.type === "Date" &&
          field.isCurrentDate === true &&
          field.defaultValue === "" &&
          field.value === ""
        ) {
          const formatted_date = formatDateYMD(new Date());
          field.value = formatted_date;
          setValue(field.name, formatted_date);
        }

        // Default missing boolean flags to false (no-op when property already present)
        if (!field.hasOwnProperty("hidden")) field.hidden = false;
        if (!field.hasOwnProperty("isFutureDateDisable")) field.isFutureDateDisable = false;
        if (!field.hasOwnProperty("isPastDateDisable")) field.isPastDateDisable = false;

        // Auto-fill current time — call nowHHmm() once to avoid minute-boundary inconsistency
        if (
          field.isCurrentTime &&
          field.type === "Time" &&
          field.defaultValue === "" &&
          field.value === ""
        ) {
          const currentTime = nowHHmm();
          field.value = currentTime;
          setValue(field.name, currentTime);
        }

        // Sum rule
        if (field.total_calc) {
          field.sumRule = {
            total_calc: field.total_calc,
            total_binding_var: field.total_binding_var,
            total_calc_id: field.total_calc_id,
          };
        }

        // Aggregation rule
        if (field.aggregation_flag) {
          field.aggregationRule = field.aggregation_rules;
          field.aggregationRule.forEach(rule => { rule.group_id = field.group_id; });
        }

        field.id = uuid;

        // Suffix visibility rule field names with the group count
        if (field.rules?.length > 0) {
          for (let r = 0; r < field.rules.length; r++) {
            const rule = field.rules[r];
            if (rule.r_field !== undefined) {
              rule.r_field = `${rule.r_field}_${count}`;
              rule.field = `${rule.field}_${count}`;
            } else if (rule.parent_field !== undefined) {
              rule.parent_field = `${rule.parent_field}_${count}`;
              rule.edit_field = `${rule.edit_field}_${count}`;
            }
          }
        }

        // Select / MultiSelect
        if (field.type === "Select" || field.type === "MultiSelect") {
          field.filterArray = field.options;
          if (
            field.type === "Select" &&
            (field.value === undefined || field.value === null || field.value === "") &&
            field.validators?.required?.value === true &&
            field.filterArray?.length === 1
          ) {
            // Required field with only one option available — auto-select it
            // (mirrors the same-named check in processControls for initial form load).
            // api_rules for this value are fired later by triggerDefaultValueAPIs,
            // once the full row (and sibling fields it may reference) exists.
            field.value = String(field.filterArray[0].value);
          }
          setValue(field.name, field.value);
        }

        // ApiField sub_type default
        if (field.type === "ApiField") {
          if (field.hasOwnProperty("sub_type")) {
            if (field.sub_type === "select") field.filterArray = field.options;
          } else {
            field.sub_type = "text";
          }
        }

        // Suffix calc rule expressions, binding variables and field lists
        if (field.calc_rule.length > 0) {
          for (let m = 0; m < field.calc_rule.length; m++) {
            const cr = field.calc_rule[m];
            let calc_equation = cr.expression;
            const temp_array = [];
            for (let j = 0; j < cr.list.length; j++) {
              const regex = new RegExp(`\\b${cr.list[j]}\\b`, "g");
              calc_equation = calc_equation.replace(regex, `${cr.list[j]}_${count}`);
              temp_array.push(`${cr.list[j]}_${count}`);
            }
            cr.expression = calc_equation;
            cr.binding_variable = `${cr.binding_variable}_${count}`;
            cr.list = temp_array;
          }
        }

        // Suffix api_rule URLs and fieldwithAt references
        if (field.api_rules?.length > 0) {
          for (let t = 0; t < field.api_rules.length; t++) {
            const ar = field.api_rules[t];
            ar.binding_variable = `${ar.binding_variable}_${count}`;
            const fieldwithAt = ar.fieldwithAt ?? [];
            const groupFields = fieldwithAt.filter(item => nameSet.has(item));  // O(1) per item
            const outerFields = fieldwithAt.filter(item => !nameSet.has(item)); // O(1) per item
            let api_url = ar.url;
            for (const gf of groupFields) {
              api_url = api_url.replaceAll(`@${gf}`, `@${gf}_${count}`); // replaceAll fixes single-replace bug
              outerFields.push(`${gf}_${count}`);
            }
            ar.fieldwithAt = outerFields;
            ar.url = api_url;
          }
        }

        // Suffix barcode rule URLs
        if (field.barcode_rules?.length > 0) {
          for (let e = 0; e < field.barcode_rules.length; e++) {
            const br = field.barcode_rules[e];
            const newParam = `${br.field}_${count}`;
            const regex = new RegExp(`(${br.field}=)@${br.field}`, "g");
            br.url = br.url.replace(regex, `${newParam}=@${newParam}`);
          }
        }

        field.group_parent = false;
        tempArray.push(field);
      }

      processRules(tempArray);

      // Build updated array before setArray — removes fragile closure dependency
      const newArray = [...formArray];
      newArray.splice(last_index + 1, 0, ...tempArray);
      const hideParent = newArray.find(d => d.parent_last && d.group_id === parent_id);
      if (hideParent) hideParent.add_new = false;
      setFormArray(newArray);

      // Aggregation type count
      triggerCountValueCalculation(newArray);

      // Sum and aggregation calculations for newly added fields (sequential — share mutable state)
      for (const field of tempArray) {
        if (field.total_calc && field.sumRule) {
          await triggerTotalCalculation(field.sumRule, newArray);
        }
        if (field.aggregation_flag && field.aggregationRule?.length > 0) {
          await triggerAggregationTypeCalculation(field.aggregationRule, newArray);
        }
      }

      // Sync allsubFormArray
      if (allsubFormArray.length > 0) {
        const subForm = allsubFormArray.find(f => f.form_name === displayNameRef.current);
        if (subForm) subForm.controls.splice(last_index + 1, 0, ...tempArray);
      }

    } catch (error) {
      toast.error("Error occurred while generating formgroup");
    }
    setIsGlobalLoading(false);
  };

  // function for controling formgroup validation
  const ControlFormGroupRadio = async (value, rules) => {
    if (rules?.length > 0) {
      for (let rule of rules) {
        let controls = formArray.filter(data => {
          return data.group_id === rule.group_parent_id;
        });
        for (let i = 0; i < controls.length; i++) {
          // Single authoritative assignment — the earlier !rule.r_show line was
          // immediately overwritten and both beforeShow/beforeValue were unused.
          controls[i].show = rule.value == Number(value) ? rule.r_show : false;
        }
        setFormArray(prevArray => {

          // REMOVE FORM GROUP CHILDREN
          let notSatisfiedArray = prevArray.filter(
            (obj) => !(obj?.group_id === rule.group_parent_id && obj?.formgroup_child === true)
          );

          // CHECK FOR PARENT_LAST
          let hide_parent_show = prevArray.filter(
            (data) => data.parent_last === true && data?.group_id === rule.group_parent_id
          );

          if (hide_parent_show.length > 0) {
            hide_parent_show[0].add_new = true;
          }

          // RESET group counters
          let reset_count = prevArray.filter(
            (data) => data.group_first === true && data?.group_id === rule.group_parent_id
          );

          if (reset_count.length > 0) {
            reset_count[0].group_parent_count = 0;
            reset_count[0].group_array_index = 1;
          }
          return notSatisfiedArray;
        });
      }
    }
  };

  const refreshSelect = () => {
    handleRefreshMasterData(selectedApiTag, masterDataSelectionName);
    // If the object is found, update the key
  };

  const downloadUploadFile = async (value, customFileName) => {
    if (!value) {
      toast.error("No file reference available.", { autoClose: 2000 });
      return;
    }
    try {
      const response = await authAxios.get(
        `api/v1/formSubmission/downloadFromS3?key=${encodeURIComponent(value)}`,
        { responseType: "blob" }
      );
      if (response.status === 200) {
        // Extract filename and extension
        const fileName = value.split("/").pop();
        const fileExtension = fileName.split(".").pop().toLowerCase();
        // Detect MIME type
        let mimeType = "application/octet-stream";
        if (fileExtension === "pdf") mimeType = "application/pdf";
        else if (["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension))
          mimeType = `image/${fileExtension === "jpg" ? "jpeg" : fileExtension
            }`;
        else if (
          ["xls", "xlsx", "csv"].includes(fileExtension)
        )
          mimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        else if (["doc", "docx"].includes(fileExtension))
          mimeType =
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

        // Create blob
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        // Construct download filename (preserve extension)
        const downloadFileName = customFileName?.includes(fileExtension)
          ? customFileName
          : `${customFileName || fileName}.${fileExtension}`;

        // If Excel, Word, or unknown file type → Direct download only
        if (["xls", "xlsx", "csv", "doc", "docx"].includes(fileExtension)) {
          const link = document.createElement("a");
          link.href = url;
          link.download = downloadFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          // For PDF / Images → Preview + Download
          window.open(url, "_blank");
          const link = document.createElement("a");
          link.href = url;
          link.download = downloadFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        // Cleanup
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Apologies for the inconvenience. We are currently addressing an unexpected error.",
        { autoClose: 5000 }
      );
    }
  };

  const handleAddMasterData = useCallback(async (name, form_id, selectedapitag) => {
    setDynamicFormId(form_id);
    setIsModelPopup(true);
    setMasterDataSelectionName(name);
    setSelectedApiTag(selectedapitag);
  }, [setIsModelPopup]);

  const handleRefreshMasterData = useCallback(async (selectedapitag, name) => {
    setSelectedApiTag(selectedapitag);
    setRefreshButtonClicked(true);
    try {
      const response = await authAxios.get(
        `/api/v1/masterData/refreshMasterDataList/${selectedapitag}`
      );
      if (response.status === 200) {
        const responseData = Array.isArray(response.data) ? response.data : [];
        const filteredOptions = responseData.filter(option => option.value !== null);

        // Functional updater — uses latest state (prev), avoids stale closure over formArray
        setFormArray(prev => prev.map(field => {
          if (field.name !== name) return field;

          const dummy_rules = field?.dummyrules?.edit_field;
          let filtered_value;

          if (dummy_rules) {
            const rules_form = prev.find(data => data.name === dummy_rules); // find() — stops at first match
            filtered_value = rules_form?.value
              ? filteredOptions.filter(data => data.filtervalue == rules_form.value)
              : filteredOptions;
          } else {
            filtered_value = filteredOptions;
          }

          // Spread into new object — prevents mutating original state items
          return { ...field, filterArray: filtered_value, options: filteredOptions };
        }));
      } else {
        // Non-200 responses were previously silent — now surfaces the failure to the user
        toast.error("Failed to refresh master data. Please try again.", { autoClose: 5000 });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Apologies for the inconvenience. We are currently addressing an unexpected error.",
        { autoClose: 5000 }
      );
    } finally {
      // Always reset — previously setRefreshButtonClicked could be stuck on non-200 responses
      setRefreshButtonClicked(false);
      setIsGlobalLoading(false);
    }
  }, []);

  // barcode scan field change
  const handleBarcodeScanChange = async (name, value, rules, formArray) => {
    if (value !== "" && value !== undefined) {
      clearErrors(name);
      const list = { formArray };
      for (let i = 0; i < list.formArray.length; i++) {
        if (list.formArray[i].name === name) {
          list.formArray[i].value = value;
          setValue(name, value);
        }
      }
      if (rules !== undefined && rules.length > 0) {
        try {
          for (let n = 0; n < rules.length; n++) {
            let main_url = resolveRuleUrl(rules[n]);
            let new_url = main_url;
            let value = getValues(name);
            let replace_url = new_url.replace("@" + name, value);
            main_url = replace_url;
            setIsSoftLoading(true);
            const api_result = await apiFieldAxios.get(main_url);
            if (api_result.data.statusCode === 200) {
              if (api_result.data?.barCodeValues.length > 0) {
                let barCodeValues = api_result.data?.barCodeValues;
                // Iterate over each item in the array
                list.formArray.forEach(field => {
                  // Get all keys from the first object in barCodeValues
                  Object.keys(barCodeValues[0]).forEach(key => {
                    // Check if the current key matches the name of the field
                    if (field.name === key) {
                      // Patch the value from barCodeValues to the field's value
                      field.value = barCodeValues[0][key];
                      setValue(field.name, barCodeValues[0][key])
                    }
                  });
                });
                setFormArray([...list.formArray]);
              }
            } else {
              toast.error("Failed to fetch data. Please try again.", { autoClose: 5000, });
            }
            setIsSoftLoading(false);
          }
        } catch (error) {
          toast.error(error.message, "Failed to fetch data. Please try again.", {
            autoClose: 5000,
          });
        }
      }
    }
  }

  // Layout breakpoint as a boolean (was a raw windowWidth number). FormFieldRenderer
  // only ever checks `windowWidth < 1024`, so we track just that threshold via
  // matchMedia — it updates only when crossing 1024px, meaning resizing within a
  // breakpoint band triggers zero re-renders of this component or FormFieldRenderer.
  // 1023.98px (vs 1024) handles fractional device-pixel widths, matching < 1024.
  const isCompactLayout = useMediaQuery('(max-width: 1023.98px)');

  const prevLengthRef = useRef(null);

  useEffect(() => {
    if (!formArray || formArray.length === 0) return;

    const isNewRowAdded = prevLengthRef.current !== null && formArray.length > prevLengthRef.current;
    prevLengthRef.current = formArray.length;

    if (routeParams?.action === "edit" && !isNewRowAdded) return;

    const fieldsToProcess = routeParams?.action === "edit"
      ? formArray.filter(f =>
        f.formgroup_child === true &&
        f.isNewlyAdded === true // only freshly added rows
      )
      : formArray;

    let isMounted = true; // M-1: guard against state updates after unmount
    const triggerDefaultValueAPIs = async () => {
      for (const field of fieldsToProcess) {
        if (!isMounted) break; // component unmounted — stop processing

        const hasDefaultValue = field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== "";
        // A required Select with only one option gets auto-selected in handleGrouping
        // (no defaultValue involved) when a new form-group row is added — fire its
        // api_rules the same way defaultValue-driven fields do. Gated on isNewlyAdded
        // so this can never match an original/initial-load field (those already go
        // through the separate initial-load api_rules replay).
        const isAutoSelectedNewRow = !hasDefaultValue && field.isNewlyAdded === true &&
          field.type === "Select" && field.value !== undefined && field.value !== null && field.value !== "";

        if ((hasDefaultValue || isAutoSelectedNewRow) && field.api_bool === true && field.api_rules?.length > 0) {
          const valueToApply = hasDefaultValue ? field.defaultValue : field.value;
          setValue(field.name, valueToApply);

          await handleAPIChanged(
            field.name,
            String(valueToApply),
            field.api_rules,
            formArray
          );

          if (!isMounted) break;
          await triggerMultiPatchRules(field.name, String(valueToApply));
        }

        //  clear the flag after processing so it doesn't re-trigger
        field.isNewlyAdded = false;
      }
    };
    triggerDefaultValueAPIs();
    return () => { isMounted = false; };
  }, [formArray.length]);


  // ====phone number only allow=======
  const handlePhoneKeyDown = (event) => {
    // Allowed keys: numbers, space, +, -, (, ), and control keys such as backspace
    const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ' ', '+', '-', '(', ')', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];

    if (!validKeys.includes(event.key) && !event.ctrlKey) {
      // Prevent default if key is not valid and not a control combination (like ctrl+c)
      event.preventDefault();
    }
  };

  const [openGridId, setOpenGridId] = useState(null);
  const [hasInitializedGrid, setHasInitializedGrid] = useState(false);
  const [gridVisibilityApplied, setGridVisibilityApplied] = useState(false);
  const handleGridAccordionToggle = (panelId) => (_e, isExpanded) =>
    setOpenGridId(isExpanded ? panelId : null);

  // Helper function to open first visible grid
  const openFirstVisibleGrid = (formArrayData) => {
    if (!formArrayData || formArrayData.length === 0) return;

    const firstVisibleGrid = formArrayData.find(
      field => field.type === 'GridComponent' && field.show !== false
    );

    if (firstVisibleGrid) {
      const gridIndex = formArrayData.indexOf(firstVisibleGrid);
      const firstPanelId = `grid-${firstVisibleGrid.name || firstVisibleGrid._id || gridIndex}`;
      // Check if currently open grid is still visible
      const currentlyOpenGrid = formArrayData.find(field => {
        const currentIndex = formArrayData.indexOf(field);
        const currentPanelId = `grid-${field.name || field._id || currentIndex}`;
        return currentPanelId === openGridId && field.type === 'GridComponent' && field.show !== false;
      });
      // If current grid is still visible, keep it open. Otherwise, open first visible grid
      if (!currentlyOpenGrid) {
        setOpenGridId(firstPanelId);
      }
    } else {
      // No visible grids, close all
      setOpenGridId(null);
    }
  };

  useEffect(() => {
    // Only auto-open the first VISIBLE grid after grid visibility has been applied
    if (formArray && formArray.length > 0 && !hasInitializedGrid && gridVisibilityApplied) {
      openFirstVisibleGrid(formArray);
      setHasInitializedGrid(true);
    }
  }, [formArray, hasInitializedGrid, gridVisibilityApplied]);

  // ======grid auto populate==
  // normalize relative/absolute paths
  const normalizeApiPath = (p) => {
    if (!p) return "/api/v1/";
    if (/^https?:\/\//i.test(p)) return p;
    const clean = String(p).replace(/^\/+/, "");
    return clean.startsWith("api/v1/") ? `/${clean}` : `/api/v1/${clean}`;
  };

  // RFC3986 query serializer
  const serializeParamsRFC3986 = (params) => {
    const enc = (v) => encodeURIComponent(String(v));
    const out = [];

    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (item === undefined || item === null || item === "") return;
          out.push(`${enc(k)}=${enc(item)}`);
        });
      } else if (v instanceof Date) {
        out.push(`${enc(k)}=${enc(v.toISOString())}`);
      } else {
        out.push(`${enc(k)}=${enc(v)}`);
      }
    });

    return out.join("&");
  };
  // Refs for grid API mapping cache.
  // formArrayRef is declared at the top of the component (near state declarations)
  // so hooks that depend on it can be called at the correct position.
  const gridApiMappingsRef = useRef(null);
  const gridDefSignatureRef = useRef('');

  const getGridApiMappings = useCallback(() => {
    if (gridApiMappingsRef.current) {
      return gridApiMappingsRef.current;
    }

    // Build mapping: paramName -> [{ gridName, apiRule }]
    const fieldToGridMap = new Map();
    const gridComponents = formArrayRef.current.filter(f =>
      f.type === "GridComponent" &&
      f.gridApiEnabled &&
      Array.isArray(f.gridApi) &&
      f.gridApi.length > 0
    );

    gridComponents.forEach(gridField => {
      const apiRule = gridField.gridApi[0];
      if (apiRule.apiParams && Array.isArray(apiRule.apiParams)) {
        apiRule.apiParams.forEach(paramName => {
          if (!fieldToGridMap.has(paramName)) {
            fieldToGridMap.set(paramName, []);
          }
          fieldToGridMap.get(paramName).push({
            gridName: gridField.name,
            apiRule: apiRule
          });
        });
      }
    });
    gridApiMappingsRef.current = fieldToGridMap;
    return fieldToGridMap;
  }, []); // stable — reads current array through formArrayRef

  // O(1) field lookup by name — one Map built once per formArray state change,
  // shared by all handlers that read from state formArray. Replaces the repeated
  // O(n) find/filter calls inside fieldChangedDate, fieldChangedTime, and
  // numberFormatting with a single O(1) Map lookup each. Also indexes every
  // Container's own children by name (same object references, mutated in
  // place by those handlers same as any top-level entry) — a nested field
  // used to need its own bespoke "search every container" fallback wherever
  // this map came up empty; building it in here once means every reader of
  // this single map already resolves a nested field correctly.
  const formArrayMap = useMemo(() => buildFieldIndexMap(formArray), [formArray]);

  // Keep formArrayRef current and invalidate the cache only when grid field
  // definitions change (names / API endpoints), not on every value update.
  // Previously this useEffect had [formArray] and unconditionally cleared the
  // cache, so it was rebuilt from scratch on every keypress.
  useEffect(() => {
    formArrayRef.current = formArray;
    const sig = formArray
      .filter(f => f.type === 'GridComponent' && f.gridApiEnabled)
      .map(f => `${f.name}:${f.gridApi?.[0]?.apiEndpoint ?? ''}`)
      .join('|');
    if (sig !== gridDefSignatureRef.current) {
      gridApiMappingsRef.current = null;
      gridDefSignatureRef.current = sig;
    }
  }, [formArray]);

  // Single, optimized function to handle grid API calls
  const handleGridApiTrigger = useCallback(async (fieldName, fieldValue, isInitialLoad = false) => {
    const mappings = getGridApiMappings();

    // Quick exit if field doesn't affect any grid
    if (!mappings.has(fieldName)) {
      return;
    }

    const affectedGrids = mappings.get(fieldName);

    for (const { gridName, apiRule } of affectedGrids) {
      const { apiEndpoint, apiParams } = apiRule;

      // Check if all required params are filled
      const queryParams = {};
      let allFilled = true;

      for (const param of apiParams) {
        let value;
        if (param === fieldName) {
          value = fieldValue;
        } else {
          // Read through ref so this callback doesn't need formArray in its deps
          const field = formArrayRef.current.find(f => f.name === param);
          value = field?.value;
        }
        if (value === undefined || value === null || value === "") {
          allFilled = false;
          break;
        }
        queryParams[param] = value;
      }

      // Update grid based on parameter status
      if (allFilled) {
        // Call API
        const apiUrl = normalizeApiPath(apiEndpoint);
        const queryString = serializeParamsRFC3986(queryParams);
        const fullUrl = queryString ? `${apiUrl}?${queryString}` : apiUrl;

        try {
          if (!isInitialLoad) setIsSoftLoading(true);
          const response = await authAxios.get(fullUrl);

          if (response.status === 200 && response.data) {
            // Capture the updated array synchronously so we can trigger grid sum
            // calculations AFTER the setArray call — state updaters must be pure
            // (no side effects / setTimeout inside them).
            let snapshotForCalc;
            setFormArray(prevArray => {
              const updated = prevArray.map(field =>
                field.name === gridName
                  ? { ...field, gridRows: response?.data?.data || [], gridRev: (field.gridRev || 0) + 1 }
                  : field
              );
              snapshotForCalc = updated;
              return updated;
            });
            setTimeout(() => {
              if (snapshotForCalc) triggerGridSumCalculations(gridName, snapshotForCalc);
            }, 0);
          }
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "Failed to load grid data",
            { autoClose: 3000 }
          );
        } finally {
          if (!isInitialLoad) setIsSoftLoading(false);
        }
      } else {
        // Clear grid if params incomplete — also reset gridRev so the
        // gridRows useEffect re-enters CASE 1 instead of skipping as a
        // "already processed" CASE 2 revision.
        let snapshotForCalc;
        setFormArray(prevArray => {
          const updated = prevArray.map(field =>
            field.name === gridName
              ? { ...field, gridRows: [], gridRev: undefined }
              : field
          );
          snapshotForCalc = updated;
          return updated;
        });
        setTimeout(() => {
          if (snapshotForCalc) triggerGridSumCalculations(gridName, snapshotForCalc);
        }, 0);
      }
    }
  }, [getGridApiMappings]); // stable: getGridApiMappings is [] deps; formArray read through ref

  useEffect(() => {
    if (gstValue && prevGstRef.current === null) {
      prevGstRef.current = gstValue;
    }
  }, [gstValue]);

  const customAVTgstFunction = async (gstNumber) => {
    try {
      setIsGlobalLoading(true);

      // Get token (with timeout)
      const tokenResponse = await axios.post(
        'https://ocr.meon.co.in/get_token',
        {
          company_id: "67069",
          email: "karthikeyan.p@avtnatural.com",
          password: "Avtnatural@123"
        },
        {
          timeout: 10000 // 10 seconds
        }
      );

      if (tokenResponse?.status === 200 && tokenResponse?.data?.success === true) {
        const token = tokenResponse?.data?.token;

        // GST validation API (with timeout)
        const response = await axios.post(
          'https://ocr.meon.co.in/gst/search_business_name',
          {
            gst_number: gstNumber,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            timeout: 30000 // Meon queries the GST portal live; responses routinely exceed 5s
          }
        );

        if (response?.data?.error) {
          setValue('vendor_name', '');
          setValue('vendor_address', '');
          toast.error(response.data.error);
          setFormArray(prev => {
            const updated = [...prev];
            const nameIdx = updated.findIndex(item => item.name === 'vendor_name');
            const addrIdx = updated.findIndex(item => item.name === 'vendor_address');
            if (nameIdx !== -1) updated[nameIdx] = { ...updated[nameIdx], value: '' };
            if (addrIdx !== -1) updated[addrIdx] = { ...updated[addrIdx], value: '' };
            return updated;
          });
          setAllsubformData(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (lastIdx >= 0 && updated[lastIdx]?.controls) {
              const controls = [...updated[lastIdx].controls];
              const nameIdx = controls.findIndex(item => item.name === 'vendor_name');
              const addrIdx = controls.findIndex(item => item.name === 'vendor_address');
              if (nameIdx !== -1) controls[nameIdx] = { ...controls[nameIdx], value: '' };
              if (addrIdx !== -1) controls[addrIdx] = { ...controls[addrIdx], value: '' };
              updated[lastIdx] = { ...updated[lastIdx], controls };
            }
            return updated;
          });
          return;
        }

        const data = response?.data;

        setValue('vendor_name', data?.business_name || '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });

        setValue('vendor_address', data?.business_address || '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setFormArray(prev => {
          const updated = [...prev];
          const nameIdx = updated.findIndex(item => item.name === 'vendor_name');
          const addrIdx = updated.findIndex(item => item.name === 'vendor_address');
          if (nameIdx !== -1) updated[nameIdx] = { ...updated[nameIdx], value: data?.business_name || '' };
          if (addrIdx !== -1) updated[addrIdx] = { ...updated[addrIdx], value: data?.business_address || '' };
          return updated;
        });
        setAllsubformData(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx]?.controls) {
            const controls = [...updated[lastIdx].controls];
            const nameIdx = controls.findIndex(item => item.name === 'vendor_name');
            const addrIdx = controls.findIndex(item => item.name === 'vendor_address');
            if (nameIdx !== -1) controls[nameIdx] = { ...controls[nameIdx], value: data?.business_name || '' };
            if (addrIdx !== -1) controls[addrIdx] = { ...controls[addrIdx], value: data?.business_address || '' };
            updated[lastIdx] = { ...updated[lastIdx], controls };
          }
          return updated;
        });
      }

    } catch (error) {
      // Timeout handling
      if (error.code === 'ECONNABORTED') {
        toast.error('GST verification timed out. Please check the GST number.');
      } else {
        toast.error('Invalid GST number or server error.');
      }

      setValue('vendor_name', '');
      setValue('vendor_address', '');
      setFormArray(prev => {
        const updated = [...prev];
        const nameIdx = updated.findIndex(item => item.name === 'vendor_name');
        const addrIdx = updated.findIndex(item => item.name === 'vendor_address');
        if (nameIdx !== -1) updated[nameIdx] = { ...updated[nameIdx], value: '' };
        if (addrIdx !== -1) updated[addrIdx] = { ...updated[addrIdx], value: '' };
        return updated;
      });
      setAllsubformData(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx]?.controls) {
          const controls = [...updated[lastIdx].controls];
          const nameIdx = controls.findIndex(item => item.name === 'vendor_name');
          const addrIdx = controls.findIndex(item => item.name === 'vendor_address');
          if (nameIdx !== -1) controls[nameIdx] = { ...controls[nameIdx], value: '' };
          if (addrIdx !== -1) controls[addrIdx] = { ...controls[addrIdx], value: '' };
          updated[lastIdx] = { ...updated[lastIdx], controls };
        }
        return updated;
      });
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleImageClick = useCallback((imageUrl) => {
    setSelectedImage(imageUrl);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const handlesignatureclick = useCallback((imageUrl) => {
    setSignaturePopupUrl(imageUrl);
  }, []);

  useEffect(() => {
    setHasInitializedGrid(false);
    setOpenGridId(null);
    setGridVisibilityApplied(false);
    getEditData();
  }, [routeParams?.id, routeParams?.form_id]);

  // Close the subform title dropdown when clicking outside of it.
  // Listener logic lives in the reusable useClickOutside hook (src/hooks).
  useClickOutside(dropdownRef, () => setIsTitleOpen(false));

  // M-5: Release all grid component refs on unmount to prevent stale ref retention
  useEffect(() => {
    return () => {
      gridComponentRefs.current = {};
    };
  }, []);

  const handleSelect = async (formId) => {
    setClickedSubform(formId);
    const selectedFormData = formList.find(form => form._id === formId);
    const formName = selectedFormData?.form_name || "This form";
    // EDIT MODE
    if (isEditMode) {
      if (!isFormEdited) {
        setSelectedForm(formId);
        setSelectedSubformName(formName);
        setSubFormSelect(false);
        await executeNavigationOfCode(formId);
        return;
      }
      const isValid = await trigger();
      if (!isValid) return;
      setSubFormSelect(true);
      return;
    }
    // ADD MODE
    if (!hasValidatedOnceRef.current) {
      const isValid = await trigger();
      if (!isValid) return;

      hasValidatedOnceRef.current = true;
      setSubFormSelect(true);
      return;
    }
    if (isFormEdited) {
      setSubFormSelect(true);
      return;
    }
    setSelectedForm(formId);
    setSelectedSubformName(formName);
    await executeNavigationOfCode(formId);
  };

  useEffect(() => {
    setIsFormEdited(false);
  }, [selectedForm]);

  // Function to execute the rest of the code after confirmation
  const executeNavigationOfCode = async (formId) => {
    setIsFormEdited(false);

    // Persist current subform state before navigating so it can be restored later
    if (allsubFormArray.length !== 0) {
      for (let i = 0; i < allsubFormArray.length; i++) {
        if (allsubFormArray[i]._id === subFormID) {
          allsubFormArray[i].controls = formArray;
          break;
        }
      }
    }

    if (order !== allsubFormArray.length) {
      let tempValue = allsubFormArray.filter((itemInArray) => itemInArray._id === formId);
      setSubFormID(tempValue[0]?._id);
      let filterSelect = tempValue[0].controls;
      // ========
      const controlData = [...filterSelect];
      buildGroupIndices(controlData); // P-11: extracted helper
      // ----count-----
      triggerCountValueCalculation(controlData)
      // ========
      // Set before dynamicFormData — its API replay syncs controls back by displayNameRef
      setDisplayName(tempValue[0].form_name);
      await dynamicFormData(controlData);
      if (tempValue[0].order === 0) {
        setShowPrevious(false);
      } else {
        setShowPrevious(true);
      }
      if (tempValue[0].order + 1 === allsubFormArray.length) {
        setShowNext(false);
      } else {
        setShowNext(true);
      }
      setOrder(tempValue[0].order);
      setIsprint(tempValue[0]?.pdfId ? tempValue[0]?.pdfId : null);
      setTemplatePdfId(tempValue[0]?.templatePdfId || null);
      const filteredData = controlData.filter((item) => item.type === "submit_button");
      if (filteredData.length > 0) {
        setSubmitButton(filteredData[0].label);
        setNextButton(filteredData[0].label);
        setButtonArray(filteredData);
      }
      setFormArray(applyInitialGridVisibility(controlData)); // P-13: removed preceding setFormArray([]) — avoids blank-render flash
      setIsTitleOpen(false);
    }
  };

  const handleSubformConfirmNavigation = async () => {
    handleFormNext(null, "subNavigation");
    setParentFormRequired(false);
    setSubFormSelect(false);
    setClickedSubform("");
  }

  const handleSubformCancelNavigation = () => {
    setParentFormRequired(false);
    setSubFormSelect(false);
    setClickedSubform("");
  }

  const handleTitleClose = useCallback(() => {
    setIsTitleOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    const subformRequired = hasRequiredFields(allsubFormArray);
    setParentFormRequired(subformRequired);
    setIsTitleOpen(true);
  }, [allsubFormArray]);

  //  function for save and new
  const hasRequiredFields = (formArray) => {
    const firstItem = formArray[0];
    if (!firstItem || !firstItem.controls) return false; // defensive
    const hasRequired = firstItem.controls.some(control =>
      control?.validators?.required?.value === true
    );
    return hasRequired;
  };

  const handleSaveAndNew = () => {
    if (isSubmittingRef.current) return;
    setSaveAndNewButtonClicked(true);
    setSelectedImages({});
    setSelectedSignature({});
    setSelectedAudio({});
    setSelectedPhoto({});
    setSelectedReceipt({});
    setIsSaveAndNew(true);
  }

  useEffect(() => {
    if (isSaveAndNew) {
      onSubmit1();
    }
  }, [isSaveAndNew]);

  const normalizeRadioValues = (payload) => {
    payload.subforms?.forEach(subform => {
      subform.controls?.forEach(control => {
        if (
          control.type === "Radio" &&
          control.value &&
          Array.isArray(control.values)
        ) {
          const matched = control.values.find(
            opt => opt.label === control.value || opt.value === control.value
          );

          if (matched) {
            control.value = matched.value;
          }
        }
      });
    });
    return payload;
  };

  const resetFormControls = (allsubFormArray) => {
    return allsubFormArray.map(item => {
      if (item.isSubmitted === 0) {
        const matchingItem = temSubformData.find(b => b.form_id === item.form_id);
        if (matchingItem) {
          return { ...item, controls: matchingItem.controls };
        }
      }
      return item; // covers isSubmitted !== 0 and the no-match fallback
    });
  };

  const commitAllGrids = async () => {
    const refs = Object.values(gridComponentRefs.current).filter(Boolean);
    // This will throw if any grid has validation errors (required/duplicate)
    await Promise.all(refs.map(ref => ref.commitAllRows?.()));

    // After commit, filter out any leftover empty rows from formArray grids
    setFormArray(prev => prev.map(f => {
      if (f.type !== "GridComponent" || !Array.isArray(f.gridRows)) return f;
      const nonEmpty = f.gridRows.filter(row => {
        const cols = f.columnData || [];
        return !cols.every((col, i) => {
          const key = col.columnName || `col${i + 1}`;
          const val = row[key];
          if (col.columnType === "Number") return val === "" || val == null || Number.isNaN(val);
          if (col.columnType === "File") {
            if (!val) return true;
            if (typeof val === "object") return !(val.fileKey || val.fileName);
            return !String(val).trim();
          }
          return val === "" || val == null || (typeof val === "string" && val.trim() === "");
        });
      });
      return { ...f, gridRows: nonEmpty };
    }));
  };

  // ─── onSubmit1 sub-functions ─────────────────────────────────────────────
  // Extracted from the monolithic onSubmit1 to keep each concern independently
  // named and readable. All functions close over the same component state.

  // Syncs the latest grid rows from formArray into allsubFormArray before save,
  // and patches multipatch values from RHF if any multipatch rules are present.
  const syncSubFormsBeforeSubmit = () => {
    if (allsubFormArray.length === 0) return;
    // The on-screen subform is tracked by subFormID (set via .order-based
    // navigation), not by raw array position — allsubFormArray isn't guaranteed
    // sorted to match display order, so using its last element can target the
    // wrong subform and drop edits (e.g. on "approval"/"secondApproval" subforms
    // that render last but don't sit last in the array). subFormID is only ever
    // set once multi-subform navigation kicks in, so single-subform submissions
    // fall back to the array's only element.
    const currentSubformId = typeof subFormID === "string" && subFormID
      ? subFormID
      : allsubFormArray[allsubFormArray.length - 1]._id;

    for (let i = 0; i < allsubFormArray.length; i++) {
      if (allsubFormArray[i]?._id !== currentSubformId) continue;

      // findAllFieldsByType also finds a grid nested inside a Container — a
      // flat .filter(...) here would miss it entirely, and its rows would
      // never reach the submission payload below (Container children have
      // no other path in — see syncContainerChildren's own GridComponent
      // branch further down).
      const gridControlsMap = new Map(
        findAllFieldsByType(formArray, "GridComponent")
          .map(({ field: ctrl }) => {
            const rhfVal = getValues(ctrl.name);
            return [ctrl.name, { ...ctrl, value: rhfVal ?? ctrl.value }];
          })
      );

      // Build a map of live formArray values for non-grid fields (user's typed input).
      // GridComponent uses gridControlsMap (RHF-sourced) because commitAllGrids runs
      // setFormArray async, so the formArray closure may not reflect the committed rows.
      const liveFieldMap = new Map(formArray.map(f => [f.name, f]));

      // Container children: `formArray`/`liveFieldMap` only key by TOP-LEVEL
      // name, so a Container's own entry gets picked up above but its
      // children's typed values wouldn't otherwise reach the payload — the
      // per-type onChange handlers (fieldChanged, fieldSelectChanged, …)
      // write straight onto `formArray` by flat name match and were never
      // made container-aware. Rather than touching every one of those handlers
      // (large, hard-to-verify surface), pull a child's value directly from
      // react-hook-form here — RHF registration is keyed by the field's
      // globally-unique name regardless of nesting, so this is correct for
      // any plain-value child (Text/Number/Select/etc.) even though the
      // formArray mirror itself was never updated for it.
      const rhfValuesForContainers = getValues();
      // Field types whose REAL value never goes through react-hook-form's
      // setValue() at all — Image/Signature/FileUpload/etc manage their
      // value directly on `formArray` (via useMediaHandlers.js), with RHF's
      // own registration on the underlying <input type="file"> only ever
      // holding a useless/browser-restricted value (a fake path string, or
      // nothing). Pulling from `rhfValuesForContainers` for these would
      // overwrite an already-correct base64/S3-key value (freshly written
      // onto the LIVE `child` below, same as `live` already reflects for the
      // Container itself) with that garbage — exactly backwards from every
      // other child type here, where the RHF value IS the correct one and
      // `child`'s own formArray mirror is the stale one.
      const RHF_MANAGED_ELSEWHERE_TYPES = new Set([
        "Image", "Signature", "MultiImage", "CropHealthAnalyzer",
        "AudioCapture", "FileUpload",
      ]);
      const syncContainerChildren = (ctrl) => {
        if (ctrl.type !== "Container" || !Array.isArray(ctrl.controls)) return ctrl;
        return {
          ...ctrl,
          controls: ctrl.controls.map((child) => {
            // A grid's data (gridRows/columnData) was never in react-hook-form
            // to begin with — pull it from gridControlsMap (now built
            // recursively, see above) same as a top-level grid does, instead
            // of the RHF-value overlay every other child type gets below.
            if (child.type === "GridComponent" && gridControlsMap.has(child.name)) {
              return gridControlsMap.get(child.name);
            }
            if (RHF_MANAGED_ELSEWHERE_TYPES.has(child.type)) {
              return child;
            }
            return rhfValuesForContainers.hasOwnProperty(child.name)
              ? { ...child, value: rhfValuesForContainers[child.name] }
              : child;
          }),
        };
      };

      allsubFormArray[i].controls = allsubFormArray[i].controls.map(ctrl => {
        if (ctrl.type === "GridComponent" && gridControlsMap.has(ctrl.name))
          return gridControlsMap.get(ctrl.name);
        const live = liveFieldMap.get(ctrl.name);
        return syncContainerChildren(live ?? ctrl);
      });
      break;
    }

    setAllsubformData([...allsubFormArray]);

    const hasMultipatch = formArray.some(ctrl =>
      (Array.isArray(ctrl.multiPatchRules) && ctrl.multiPatchRules.length > 0) ||
      (Array.isArray(ctrl.formgroupMultipatchRules) && ctrl.formgroupMultipatchRules.length > 0)
    );
    if (!hasMultipatch) return;

    const rhfValues = getValues();
    allsubFormArray.forEach(subform => {
      if (!Array.isArray(subform.controls)) return;
      subform.controls.forEach(control => {
        if (rhfValues.hasOwnProperty(control.name)) {
          control.value = rhfValues[control.name];
          return;
        }
        const suffixedMatch = Object.keys(rhfValues).find(
          k => k.startsWith(control.name + "_") &&
            rhfValues[k] !== undefined && rhfValues[k] !== null
        );
        if (suffixedMatch) control.value = rhfValues[suffixedMatch];
      });
    });
  };

  // Resolves the device's current lat/long when the submit button has
  // "Capture Geolocation" enabled. Never rejects — a denied/unavailable
  // location falls back to null so it doesn't block the submission.
  const captureButtonGeoLocation = (button) => {
    if (!button?.captureGeo || typeof navigator === "undefined" || !navigator.geolocation) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {
          toast.error("Unable to capture your location. Submitting without it.", { autoClose: 4000 });
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });
  };

  // Runs the submit-button's optional pre-flight API check.
  // Returns true if the check failed (submit should be blocked).
  const runPreFlightButtonCheck = async (button, lastObject) => {
    const resolvedPreFlightUrl = resolveRuleUrl(button?.api_rules);
    if (!resolvedPreFlightUrl) return false;
    try {
      setIsGlobalLoading(true);
      let response;
      if (button.api_rules?.isPreFullBody) {
        const preflight = {
          form_id: mainarray._id, form_name: mainarray.form_name,
          description: mainarray.description, isFormGroup: mainarray.isFormGroup,
          project_id: mainarray.project_id, survey_id: mainarray.survey_id,
          submittedBy: userData,
          subforms: allsubFormArray.map(s => ({ ...s })),
        };
        response = await authAxios.post(resolvedPreFlightUrl, preflight);
      } else if (button.api_rules?.parameters?.length > 0) {
        const tempFilterControl = lastObject.controls.filter(item =>
          button.api_rules.parameters.includes(item.name)
        );
        response = await authAxios.put(resolvedPreFlightUrl, {
          form_id: mainarray._id, controls: tempFilterControl,
        });
      }
      if (response?.status === 200) {
        setIsGlobalLoading(false);
        setIsLoadingBtn(false); isSubmittingRef.current = false;
        return false;
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 2000 });
      if (error?.response?.data?.errors) setErrorList(error.response.data.errors);
      setIsGlobalLoading(false);
      setIsLoadingBtn(false); isSubmittingRef.current = false;
      return true;
    }
    return false;
  };

  // Builds the base submission payload shared by all three submit modes.
  const buildBasePayload = async (geoLocation) => {
    const updatedForms = await resetFormControls(allsubFormArray);
    const payload = {
      description: mainarray.description, isFormGroup: mainarray.isFormGroup,
      subforms: updatedForms, form_name: mainarray.form_name,
      project_id: mainarray.project_id, survey_id: mainarray.survey_id,
      isReportbuilderTemplate: mainarray.isReportbuilderTemplate ?? false,
    };
    if (geoLocation) {
      payload.savedLocation = geoLocation;
    }
    // Snapshot ApiField options so they can be restored after round-trip
    payload.subforms?.forEach(subform => {
      subform.controls?.forEach(control => {
        if (control.type === "ApiField" && control.sub_type === "multiSelect" && Array.isArray(control.options))
          control.copiedMultiSelectOptions = [...control.options];
        if (control.type === "ApiField" && control.sub_type === "select" && Array.isArray(control.options))
          control.copiedSelectOptions = [...control.options];
      });
    });
    return payload;
  };

  // Handles a new (add-mode or modal) form submission — POST.
  const submitAddMode = async (payload, lastObject) => {
    payload.uuid = crypto.randomUUID();
    payload.submittedBy = userData;
    payload.form_id = mainarray._id;

    payload.subforms?.forEach(subform => {
      subform.controls?.forEach(control => {
        if (control.type === "ApiField" && control.sub_type === "multiSelect" && Array.isArray(control.options))
          control.copiedMultiSelectOptions = [...control.options];
        if (control.type === "ApiField" && control.sub_type === "select" && Array.isArray(control.options))
          control.copiedSelectOptions = [...control.options];
        if (control.type === "Select" || (control.type === "ApiField" && control.sub_type === "text")) {
          if (control.value !== undefined && control.value !== null) {
            control.value = String(control.value);
            setValue(control.name, String(control.value));
          }
        }
        if (control.type === "MultiSelect") {
          control.value = typeof control.value === "string"
            ? [control.value]
            : Array.isArray(control.value) ? control.value : [];
          setValue(control.name, control.value);
        }
      });
    });

    try {
      setIsLoadingBtn(true);
      setIsGlobalLoading(true);
      const response = await authAxios.post(`/api/v1/formSubmission`, payload);
      if (response.status === 200) {
        setIsLoadingBtn(false); isSubmittingRef.current = false;
        setIsGlobalLoading(false);
        if (mainarray.isReportbuilderTemplate && response?.data?.data?.template_id) {
          setPdfParams({
            form_id: Object.keys(subFormNewID).length === 0 ? mainarray._id : subFormNewID?.form_id,
            uuid: response.data.data.uuid, type: 'Normal', isReportBuilder: true,
            template_id: response.data.data.template_id, submission_id: response?.data?.data?._id,
          });
          setShowModal(true);
        } else if (!ModalIsOpen && buttonArray?.[0]?.isPdfDownload &&
          buttonArray[0].pdf_rules?.pdfdownloadapi !== "" && response?.data?.data?.uuid) {
          setPdfParams({ form_id: lastObject._id, uuid: response.data.data.uuid, type: 'Normal' });
          setShowModal(true);
        } else {
          showSuccessToast("Item saved successfully!");
          navigateAfterSubmission();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
      setIsLoadingBtn(false); isSubmittingRef.current = false;
      setIsGlobalLoading(false);
      setIsSaveAndNew(false);
    }
  };

  // Handles first or second approval submission — PUT with approval flags.
  // Also stamps firstApprovalActionAt/secondApprovalActionAt with the current time on each approval,
  // carrying the first approval's timestamp forward when second-approving.
  const submitApprovalMode = async (approveType, payload) => {
    const editID = routeParams?.id;
    const url = approveType === "approve"
      ? `/api/v1/formSubmission/${editID}?approve=true`
      : `/api/v1/formSubmission/${editID}?secondApprove=true`;

    payload.form_id = mainarray.form_id;
    payload.uuid = mainarray.uuid;
    payload.created = mainarray?.createdOn || created;
    payload.secondApprovalPermission = mainarray?.secondApprovalPermission;
    payload.approvalPermission = mainarray?.approvalPermission;
    payload.isApproved = 1;
    payload.created = created;

    const existingFirst = mainarray?.firstApprovalActionBy || payload.firstApprovalActionBy;
    const existingFirstAt = mainarray?.firstApprovalActionAt || payload.firstApprovalActionAt;
    payload.submittedBy = existingFirst || userData;

    if (approveType === "approve") {
      payload.firstApprovalActionBy = userData;
      payload.firstApprovalActionAt = new Date().toISOString();
      if (mainarray?.secondApprovalPermission === true) payload.isSecondApprove = -1;
    } else {
      payload.firstApprovalActionBy = existingFirst || userData;
      payload.firstApprovalActionAt = existingFirstAt || new Date().toISOString();
      payload.secondApprovalActionBy = userData;
      payload.secondApprovalActionAt = new Date().toISOString();
      payload.isSecondApprove = 1;
    }

    setIsGlobalLoading(true);
    try {
      const response = await authAxios.put(url, normalizeRadioValues(payload));
      if (response.status === 200) {
        setIsGlobalLoading(false);
        showSuccessToast("Approved successfully!");
        if (navigateFrom === "dashboard") {
          navigateTo("/dashboard", { state: { dashboardActiveTab } });
        } else {
          navigateTo(`/formsub/${name_id}`);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
      setIsGlobalLoading(false);
    }
  };

  // Handles an existing submission update (edit mode) — PUT.
  const submitEditMode = async (payload, lastObject) => {
    const editID = routeParams?.action === "edit" ? routeParams?.id : subFormNewID?._id;
    payload.form_id = mainarray.form_id;
    payload.uuid = mainarray.uuid;
    payload.created = created;
    payload.isApproved = mainarray?.isApproved ?? 0;
    payload.approvalPermission = mainarray?.approvalPermission ?? false;
    payload.secondApprovalPermission = mainarray?.secondApprovalPermission ?? false;
    if (mainarray?.secondApprovalPermission === true)
      payload.isSecondApprove = mainarray?.isSecondApprove ?? 0;
    payload.submittedBy = userData;

    try {
      setIsLoadingBtn(true);
      setIsGlobalLoading(true);
      const response = await authAxios.put(`/api/v1/formSubmission/${editID}`, payload);
      if (response.status === 200) {
        setIsLoadingBtn(false); isSubmittingRef.current = false;
        setIsGlobalLoading(false);
        if (mainarray.isReportbuilderTemplate) {
          setPdfParams({
            form_id: mainarray.form_id || mainarray._id, uuid: pdfParams.uuid || mainarray.uuid,
            type: 'Normal', isReportBuilder: true, template_id: null,
            existingTemplatePdfId: templatePdfId || null, submission_id: editID,
          });
          setTimeout(() => { setShowModal(true); }, 1000);
        } else if (buttonArray?.[0]?.isPdfDownload &&
          buttonArray[0].pdf_rules?.pdfdownloadapi !== "" && pdfParams?.uuid !== "") {
          setPdfParams({ form_id: lastObject._id, uuid: pdfParams.uuid, type: 'Normal' });
          setTimeout(() => { setShowModal(true); }, 1000);
        } else {
          showSuccessToast("Item saved successfully!");
          navigateAfterSubmission();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
      setIsLoadingBtn(false); isSubmittingRef.current = false;
      setIsGlobalLoading(false);
      setIsSaveAndNew(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Orchestrator — coordinates the pre-submit steps and dispatches to the
  // correct submission mode. Previously this was a single 365-line function.
  const onSubmit1 = async (approveType) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    // UPIPayment fields must show a confirmed 'paid' result (set only by
    // applyUpiPaymentResult, itself only reachable via a verified mock
    // simulate/real webhook status — never a client-asserted flag) before
    // the form can be submitted.
    const unpaidUpiField = formArray.find(
      (f) => f.type === "UPIPayment" && f.paymentResult?.status !== "paid"
    );
    if (unpaidUpiField) {
      toast.error(`Please complete the payment for "${unpaidUpiField.paymentLabel || unpaidUpiField.label}" before submitting.`);
      isSubmittingRef.current = false;
      return;
    }

    // Step 1: Commit + validate grids
    try {
      await commitAllGrids();
    } catch (e) {
      toast.error(e.message || "Grid validation failed");
      isSubmittingRef.current = false;
      return;
    }

    // Step 2: Sync grid data from formArray into allsubFormArray
    syncSubFormsBeforeSubmit();

    if (!validateRequiredGrids()) { isSubmittingRef.current = false; return; }

    setIsLoadingBtn(true);
    setErrorList([]);

    const lastObject = allsubFormArray[allsubFormArray.length - 1];
    const button = buttonArray[0];

    // Step 3: Optional pre-flight validation API
    const apiBlocked = await runPreFlightButtonCheck(button, lastObject);
    if (apiBlocked) return;

    // Step 3b: Capture the submitter's lat/long when the button has
    // "Capture Geolocation" enabled in the form builder.
    const geoLocation = await captureButtonGeoLocation(button);

    // Step 4: Early-exit for multi-subform "next" flow
    allsubFormArray[allsubFormArray.length - 1].isSubmitted = 1;
    if ((routeParams?.action === "add") && !ModalIsOpen && Object.keys(subFormNewID).length !== 0) {
      await submitSubFormData("new", lastObject._id, geoLocation);
      return;
    }

    // Step 5: Build shared payload, then dispatch to the correct mode
    const payload = await buildBasePayload(geoLocation);

    if ((routeParams?.action === "add") || ModalIsOpen) {
      await submitAddMode(payload, lastObject);
    } else if (approveType === "approve" || approveType === "secondApprove") {
      await submitApprovalMode(approveType, payload);
    } else {
      await submitEditMode(payload, lastObject);
    }
  };

  const navigateAfterSubmission = () => {
    const resetForm = () => {
      reset();
      setShowDeleteButton(false);
      setShowPrevious(false);
      setIsSaveAndNew(false);
      setSubFormNewID({});
      setSubmittedBy("");
    };

    const executeSaveAndNew = () => {
      setShowPrevious(false);
      setIsSaveAndNew(false);
      setPdfParams({ form_id: '', uuid: '', type: '' });
      setSubmittedBy("");
      setIsprint(null);
      setTemplatePdfId(null);
      const currentUrl = location.pathname.split('/').filter(part => part !== '');
      const modifiedUrl = `/${currentUrl[0]}/add/${currentUrl[3]}`;
      navigateTo(modifiedUrl);
      setShowDeleteButton(false);
      reset();
      HandleAddData(currentUrl[3]);
    };

    if (routeParams?.action === "add" || ModalIsOpen) {
      if (ModalIsOpen) {
        setIsModelPopup(false);
        RefreshSelect();
      } else if (isSaveAndNew) {
        resetForm();
        getEditData();
      } else {
        handleNavigation();
      }
    } else {
      if (isSaveAndNew) {
        executeSaveAndNew();
      } else {
        handleNavigation();
      }
    }
  };

  const handleNavigation = useCallback(() => {
    if (actionID && actionID !== "Dashboard") {
      navigateTo(`/actiontable_list/${actionID}`);
    } else if (navigateFrom === "dashboard") {
      navigateTo("/dashboard", { state: { dashboardActiveTab } });
    } else {
      navigateTo(`/formsub/${name_id}`);
    }
  }, [actionID, navigateFrom, dashboardActiveTab, name_id, navigateTo]);


  const handleReject = useCallback(async (rejectType) => {
    const editID = routeParams?.id;

    if (!editID) {
      toast.error("Unable to reject: missing submission ID.", { autoClose: 5000 });
      return;
    }

    // Guard: only proceed for known rejection types
    if (rejectType !== "approve" && rejectType !== "secondApprove") {
      toast.error("Unable to reject: unrecognized rejection type.", { autoClose: 5000 });
      return;
    }

    const updatedForms = await resetFormControls(allsubFormArray);

    const formData = {
      description: mainarray.description,
      isFormGroup: mainarray.isFormGroup,
      subforms: updatedForms,
      form_name: mainarray.form_name,
      project_id: mainarray.project_id,
      survey_id: mainarray.survey_id,
      form_id: mainarray.form_id,
      uuid: mainarray.uuid,
      created: mainarray?.createdOn || created,
      secondApprovalPermission: mainarray?.secondApprovalPermission,
      approvalPermission: mainarray?.approvalPermission,
      submittedBy: mainarray?.submittedBy || userData,
      created: created,
    };

    let url = "";

    if (rejectType === "approve") {
      // First-level rejection
      formData.isApproved = 0; // rejected at first approval
      formData.firstRejectionActionBy = userData;
      formData.firstRejectionActionAt = new Date().toISOString();
      url = `/api/v1/formSubmission/${editID}?approve=true`;

      if (mainarray?.secondApprovalPermission === true) {
        formData.isSecondApprove = -1; // waiting for second approval (if required)
      }
    } else {
      // Second-level rejection — first approval already happened, so its
      // approve-by/at is carried forward unchanged; only the second-level
      // rejection gets its own by/at pair (kept separate from
      // secondApprovalActionBy/At, which is reserved for genuine approvals).
      formData.isApproved = 1; // first approval already done
      formData.isSecondApprove = 0; // rejected at second approval level
      formData.firstApprovalActionBy =
        mainarray?.firstApprovalActionBy || firstApprovalData?.approvedBy || userData;
      formData.firstApprovalActionAt = mainarray?.firstApprovalActionAt || new Date().toISOString();
      formData.secondRejectionActionBy = userData;
      formData.secondRejectionActionAt = new Date().toISOString();
      url = `/api/v1/formSubmission/${editID}?secondApprove=true`;
    }

    setIsGlobalLoading(true);
    try {
      const response = await authAxios.put(url, formData);

      if (response.status === 200) {
        showSuccessToast("Rejected successfully!");

        // Mirror handleNavigation — actionID case was missing here
        if (actionID && actionID !== "Dashboard") {
          navigateTo(`/actiontable_list/${actionID}`);
        } else if (navigateFrom === "dashboard") {
          navigateTo("/dashboard", { state: { dashboardActiveTab } });
        } else {
          navigateTo(`/formsub/${name_id}`);
        }
      } else {
        toast.error("Failed to reject. Please try again.", { autoClose: 5000 });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Apologies for the inconvenience. We are currently addressing an unexpected error.",
        { autoClose: 5000 }
      );
    } finally {
      closeModal('reject');
      closeModal('secondApprove');
      setIsGlobalLoading(false);
    }
  }, [routeParams, allsubFormArray, mainarray, userData, firstApprovalData, actionID, navigateFrom, dashboardActiveTab, name_id, navigateTo]);

  // Function to download the pdf.
  const generateRandomPdfName = (prefix = "FFdoc") => {
    const currentDate = new Date();
    const formattedDate = currentDate
      .toISOString()
      .slice(0, 19)
      .replace(/[-T:]/g, "");
    return displayName ? `${displayName}_${formattedDate}.pdf` : `${prefix}_${formattedDate}.pdf`;
  };

  // `fileName` (optional) overrides the auto-generated name. The PDF builder
  // flow passes the template's filename so the download shows the PDF builder
  // name instead of the form name.
  const downloadPdf = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || generateRandomPdfName());
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // approval confirm
  const handleApprovalConfirm = async () => {
    const isValid = await trigger();
    if (isValid) {
      openModal('approve');
    }
  };

  const handleSecondApprovalConfirm = async () => {
    const isValid = await trigger();
    if (isValid) {
      openModal('secondApprove');
    }
  };

  // approve the submission if yes clicked
  const handleConfirmApprove = useCallback(async (type) => {
    setErrorList([]);
    const button = approveArray[0];

    const closeApproveModal = () => {
      if (type === 'secondApprove') {
        closeModal('secondApprove');
      } else {
        closeModal('approve');
      }
    };

    if (button && button.api_rules?.isPreFullBody) {
      // POST full body to validation URL first, then approve only if success
      const validationPayload = {
        form_id: mainarray._id,
        form_name: mainarray.form_name,
        description: mainarray.description,
        isFormGroup: mainarray.isFormGroup,
        project_id: mainarray.project_id,
        survey_id: mainarray.survey_id,
        submittedBy: userData,
        subforms: allsubFormArray.map(subform => ({ ...subform })),
      };
      setIsGlobalLoading(true);
      try {
        const response = await authAxios.post(resolveRuleUrl(button.api_rules), validationPayload);
        if (response.status === 200) {
          closeApproveModal();
          await onSubmit1(type);
        } else {
          toast.error("Validation failed. Please try again.", { autoClose: 5000 });
        }
      } catch (error) {
        if (error?.response?.data?.errors) {
          setErrorList(error.response.data.errors);
        } else {
          toast.error(
            error?.response?.data?.message ||
            "Apologies for the inconvenience. We are currently addressing an unexpected error.",
            { autoClose: 2000 }
          );
        }
        closeApproveModal();
      } finally {
        setIsGlobalLoading(false);
      }
    } else if (button && resolveRuleUrl(button.api_rules)) {
      const requestUrl = `${resolveRuleUrl(button.api_rules)}/${mainarray._id}`;
      setIsGlobalLoading(true);
      try {
        const response = await authAxios.get(requestUrl);
        if (response.status === 200) {
          closeApproveModal();
          await onSubmit1(type);
        } else {
          toast.error("Validation failed. Please try again.", { autoClose: 5000 });
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
          "Apologies for the inconvenience. We are currently addressing an unexpected error.",
          { autoClose: 2000 }
        );
        if (error?.response?.data?.errors) {
          setErrorList(error.response.data.errors);
        }
        closeApproveModal();
      } finally {
        setIsGlobalLoading(false);
      }
    } else {
      closeApproveModal();
      await onSubmit1(type);
    }
  }, [approveArray, mainarray, allsubFormArray, userData, onSubmit1]);

  // reject confirm
  const handleRejectConfirm = () => {
    openModal('reject');
  };

  const handleSecondRejectConfirm = () => {
    openModal('secondReject');
  };

  // reject the submission if yes clicked
  const handleConfirmReject = useCallback(async (type) => {
    setErrorList([]);
    const button = rejectArray[0];

    // Close the correct reject confirmation modal based on which approval level triggered this
    const closeRejectModal = () => {
      if (type === 'secondApprove') {
        closeModal('secondReject');
      } else {
        closeModal('reject');
      }
    };

    if (button && button.api_rules?.isPreFullBody) {
      // POST full body to validation URL first, then reject only if success
      const validationPayload = {
        form_id: mainarray._id,
        form_name: mainarray.form_name,
        description: mainarray.description,
        isFormGroup: mainarray.isFormGroup,
        project_id: mainarray.project_id,
        survey_id: mainarray.survey_id,
        submittedBy: userData,
        subforms: allsubFormArray.map(subform => ({ ...subform })),
      };
      setIsGlobalLoading(true);
      try {
        const response = await authAxios.post(resolveRuleUrl(button.api_rules), validationPayload);
        if (response.status === 200) {
          closeRejectModal();
          await handleReject(type);
        } else {
          toast.error("Validation failed. Please try again.", { autoClose: 5000 });
        }
      } catch (error) {
        if (error?.response?.data?.errors) {
          setErrorList(error.response.data.errors);
        } else {
          toast.error(
            error?.response?.data?.message ||
            "Apologies for the inconvenience. We are currently addressing an unexpected error.",
            { autoClose: 2000 }
          );
        }
        closeRejectModal();
      } finally {
        setIsGlobalLoading(false);
      }
    } else if (button && resolveRuleUrl(button.api_rules)) {
      // GET validation URL first, then reject only if success
      const requestUrl = `${resolveRuleUrl(button.api_rules)}/${mainarray._id}`;
      setIsGlobalLoading(true);
      try {
        const response = await authAxios.get(requestUrl);
        if (response.status === 200) {
          closeRejectModal();
          await handleReject(type);
        } else {
          toast.error("Validation failed. Please try again.", { autoClose: 5000 });
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
          "Apologies for the inconvenience. We are currently addressing an unexpected error.",
          { autoClose: 2000 }
        );
        if (error?.response?.data?.errors) {
          setErrorList(error.response.data.errors);
        }
        closeRejectModal();
      } finally {
        setIsGlobalLoading(false);
      }
    } else {
      closeRejectModal();
      await handleReject(type);
    }
  }, [rejectArray, mainarray, allsubFormArray, userData, handleReject]);

  // Handler for confirming navigation
  const handleConfirmNavigation = async () => {
    closeModal('navigation'); // Close the modal
    try {
      await submitSubFormData("edit", selectedForm);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save. Please try again.", { autoClose: 5000 });
    }
    await executeNavigationOfCode(selectedForm); // Execute the rest of the code
  };

  // Handler for cancelling navigation
  const handleCancelNavigation = () => {
    closeModal('navigation'); // Close the modal
  };

  const handleConfirmDelete = useCallback(async () => {
    const editId = routeParams?.id;
    const form_id = routeParams?.form_id;

    // Guard: prevent API call with undefined IDs in the URL
    if (!editId || !form_id) {
      toast.error("Unable to delete: missing submission ID.", { autoClose: 5000 });
      return;
    }

    setIsGlobalLoading(true);
    try {
      const response = await authAxios.delete(
        `/api/v1/formSubmission/${editId}?form_id=${form_id}`
      );
      if (response.status === 200) {
        showSuccessToast("Item deleted successfully!");
        // Mirror handleNavigation — actionID case was missing here
        if (actionID && actionID !== "Dashboard") {
          navigateTo(`/actiontable_list/${actionID}`);
        } else if (navigateFrom === "dashboard") {
          navigateTo("/dashboard", { state: { dashboardActiveTab } });
        } else {
          navigateTo(`/formsub/${name_id}`);
        }
      } else {
        toast.error("Failed to delete. Please try again.", { autoClose: 5000 });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Apologies for the inconvenience. We are currently addressing an unexpected error.",
        { autoClose: 5000 }
      );
    } finally {
      // Always reset — previously stuck on non-200 responses
      closeModal('delete');
      setIsGlobalLoading(false);
    }
  }, [routeParams, actionID, navigateFrom, dashboardActiveTab, name_id, navigateTo]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleGenerate = async () => {
    let params = pdfParams;
    setShowModal(false);

    if (params?.isReportBuilder) {
      // ===== REPORT BUILDER FLOW =====
      // Always regenerate on click. The previous short-circuit downloaded a
      // cached S3 PDF whenever existingTemplatePdfId was set, which meant
      // (a) edits to the report-builder template never showed up in the next
      // download, and (b) submission/API data that changes between clicks
      // (dynamic master-data, recently updated rows) was frozen at first
      // generation. Regenerating each time guarantees the user gets the
      // current template + current data.
      setIsPrintLoading(true);
      try {
        const genResponse = await authAxios.post(`/api/v1/formSubmission/generateReportBuilderPdf`, {
          uuid: params.uuid,
          form_id: params.form_id,
          submission_id: params.submission_id
        });

        if (genResponse.data?.success && genResponse.data?.templatePdfId) {
          // Download the freshly generated PDF from S3
          const pdfResponse = await authAxios.get(
            `api/v1/formSubmission/submissionToPdf?key=${genResponse.data.templatePdfId}`,
            { responseType: "blob" }
          );
          const file = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, '_blank');
          // Use the PDF builder template's name for the downloaded file (server
          // echoes it as `fileName`); fall back to the form-name default.
          downloadPdf(pdfResponse, genResponse.data.fileName);
          toast.success("PDF downloaded successfully!", { autoClose: 1000 });
          // Track the latest templatePdfId for any flow that still reads it
          // (e.g. background fire-and-forget paths). Not used as a download
          // shortcut anymore.
          setTemplatePdfId(genResponse.data.templatePdfId);
        } else {
          toast.error("PDF generation failed. Please try again.", { autoClose: 3000 });
        }
      } catch (err) {
        toast.info(
          err?.response?.data?.message || "The PDF is currently being generated. Please try again later.",
          { autoClose: 2000 }
        );
      } finally {
        setIsPrintLoading(false);
      }

      // Navigate away
      if (params?.type === 'Normal') {
        navigateAfterSubmission();
      } else if (params?.type === 'edit' || params?.type === 'new') {
        navigateAfterSave(params?.type, actionID, name_id);
      }
      document.body.classList.remove('glloading');

    } else {
      // ===== EXISTING JAVA PDF FLOW — UNCHANGED =====
      setIsPrintLoading(true);
      const loadingDuration = 500;
      await delay(loadingDuration);
      setIsPrintLoading(false);
      setIsPrintLoading(true);
      await delay(2000);
      const handlePrintPromise = handlePrint(false, params);

      if (params?.type === 'Normal') {
        navigateAfterSubmission();
      } else if (params?.type === 'edit' || params?.type === 'new') {
        navigateAfterSave(params?.type, actionID, name_id);
      }
      document.body.classList.remove('glloading');

      try {
        await handlePrintPromise;
      } catch (error) {
      }
    }
  };

  const handlePrint = useCallback(async (type, value) => {
    try {
      let response;

      if (value !== undefined && value !== null && type === true) {
        setIsPrintLoading(true);
        response = await authAxios.get(
          `api/v1/formSubmission/submissionToPdf?key=${value}`,
          { responseType: "blob" }
        );
      } else if (value?.uuid && value.form_id) {
        response = await authAxios.get(
          `api/v1/formSubmission/submissionToPdfFilterUuid?uuid=${value?.uuid}&subform_id=${value.form_id}`,
          { responseType: "blob" }
        );
      } else {
        toast.info("No print configuration found. Please try again.", { autoClose: 2000 });
        return;
      }

      if (response) {
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        // Revoke after a short delay to give the browser time to start loading the blob URL
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
        const previewTab = window.open(fileURL, '_blank');
        if (!previewTab) {
          toast.error("Popup blocked! Please allow popups for this site.");
          return;
        }
        downloadPdf(response);
        toast.success("PDF downloaded successfully!", { autoClose: 1000 });
      }
    } catch (err) {
      toast.info(
        err?.response?.data?.message || "The PDF is currently being generated, Please try again later.",
        { autoClose: 2000 }
      );
    } finally {
      setIsPrintLoading(false);
      document.body.classList.remove('glloading');
    }
  }, [downloadPdf]);

  // Global shortcuts — listener logic lives in the reusable useKeyDown hook
  // (src/hooks). The hook always invokes the latest handler via a ref, so these
  // inline handlers read fresh state/props without a manual dependency array.

  // Escape closes the popup; Delete opens the delete confirmation.
  useKeyDown((e) => {
    if (e.key === "Escape" && ModalIsOpen && setIsModelPopup) {
      setIsModelPopup(false);
    }
    if (e.key === "Delete" && showDeleteButton) {
      openModal('delete');
    }
  });

  // Ctrl/Cmd+Enter submits: Next when there are more subforms, else final save.
  useKeyDown((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();

      if (showNext && !isLoadingBtn) {
        handleSubmit(handleFormNext)();
      } else if (!showNext && !showButtonShow && !isLoadingBtn) {
        handleSubmit(onSubmit1)();
      }
    }
  });

  const handleLater = () => {
    // For report builder: fire-and-forget background generation (if no existing PDF)
    if (pdfParams?.isReportBuilder && !pdfParams?.existingTemplatePdfId) {
      authAxios.post(`/api/v1/formSubmission/generateReportBuilderPdf`, {
        uuid: pdfParams.uuid,
        form_id: pdfParams.form_id,
        submission_id: pdfParams.submission_id
      }).catch(() => {
        // Background fire-and-forget — failure is silent; user can retry via Print button
      });
    }

    // Navigate away (same logic for all flows)
    if (pdfParams?.type === 'Normal') {
      navigateAfterSubmission();
    } else if (pdfParams?.type === 'edit' || pdfParams?.type === 'new') {
      navigateAfterSave(pdfParams?.type, actionID, name_id);
    }
    document.body.classList.remove('glloading');
    setShowModal(false);
  };

  // Sync latest implementations each render so stable wrappers above always
  // call the current closure (which captures the latest formArray / state).
  _impl.current = {
    fieldChanged,
    fieldSelectChanged,
    fieldMultiSelectChanged,
    fieldChangedDate,
    fieldChangedTime,
    fieldChangedGeo,
    handleAPIChanged,
    triggerMultiPatchRules,
    triggerFormgroupMultiPatch,
    applyFaceIdentifyPatch,
    applyFaceCaptureEnroll,
    applyReceiptOcrPatch,
    handleFormGroupAPITrigger,
    handleGrouping,
    ControlFormGroupRadio,
    downloadUploadFile,
    handleBarcodeScanChange,
    handleButtonAPIChanged,
    handlenavigatebutton,
    handleAutoPopulate,
    customAVTgstFunction,
    numberFormatting,
    handleKeyDown: handlePhoneKeyDown,
    handleGridAccordionToggle,
    toggle,
  };

  return (
    <Suspense fallback={<LazyLoaderTrigger />}>
      <div className="submission-overview">
        {routeParams && (
          <PopupDynamicForm
            ModalIsOpen={ModalIsOpen}
            setIsModelPopup={setIsModelPopup}
            DynamicFormId={dynamicFormId}
            RefreshSelect={refreshSelect}
          />
        )}
        {showApproveModal && (
          <ConfirmModal
            message={"Are you sure you want to approve this form?"}
            onConfirm={() => handleConfirmApprove('approve')}
            onCancel={() => closeModal('approve')}
          />
        )}
        {showRejectModal && (
          <ConfirmModal
            message={"Are you sure you want to reject this form?"}
            onConfirm={() => handleConfirmReject('approve')}
            onCancel={() => closeModal('reject')}
          />
        )}
        {showSecondApproveModal && (
          <ConfirmModal
            message={"Are you sure you want to approve this form?"}
            onConfirm={() => handleConfirmApprove('secondApprove')}
            onCancel={() => closeModal('secondApprove')}
          />
        )}
        {showSecondRejectModal && (
          <ConfirmModal
            message={"Are you sure you want to reject this form?"}
            onConfirm={() => handleConfirmReject('secondApprove')}
            onCancel={() => closeModal('secondReject')}
          />
        )}
        {showDeleteModal && (
          <ConfirmModal
            message={"Are you sure you want to delete this submission?"}
            onConfirm={handleConfirmDelete}
            onCancel={() => closeModal('delete')}
          />
        )}

        {showNavigationModal && (
          <ConfirmModal
            message={"You have unsaved changes. Do you want to save?"}
            onConfirm={handleConfirmNavigation}
            onCancel={handleCancelNavigation}
          />
        )}
        {subFormSelect && (
          <ConfirmModal
            message={`${selectedSubformName}  not saved. Do you want to save?`}
            onConfirm={handleSubformConfirmNavigation}
            onCancel={handleSubformCancelNavigation}
          />
        )}
        {showModal && <CustomModal
          show={showModal}
          onClose={handleCloseModal}
          onGenerate={handleGenerate}
          onLater={handleLater}
        />}
        {selectedImage && (
          <ImagePopup imageUrl={selectedImage} onClose={handleClose} />
        )}
        {signaturePopupUrl && (
          <SignaturePopup imageUrl={signaturePopupUrl} onClose={() => setSignaturePopupUrl(null)} />
        )}

        <Backdrop open={isPrintLoading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress color="inherit" />
            <Typography color="inherit">Generating PDF...</Typography>
          </Box>
        </Backdrop>
        <div className={ModalIsOpen ? "popup-wrapper-class" : "form-page-wrapper"}>
          <div className="form-submission-header-div form-submission-header-compact">
            <div
              className="back-icon-container-new sumission-back-icon"
              title="Back"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (ModalIsOpen) {
                  setIsModelPopup(false);
                } else {
                  if (actionID && actionID !== "Dashboard") {
                    navigateTo(`/actiontable_list/${actionID}`);
                  } else if (navigateFrom === 'dashboard') {
                    navigateTo("/dashboard", { state: { dashboardActiveTab, tableUuid, activeGlobalFilters } });
                  } else {
                    navigateTo(`/formsub/${name_id}`);
                  }
                }
              }}
            >
              <SVGIcon
                name="BackIcon"
                className="Back-Close-icon"
                width={12}
                height={12}
              />
            </div>
            <div className={formList.length > 0 ? "form_submission_header_container_new" : "single-form-header-container"}>
              {formList.length === 0 &&
                <>
                  <span className="form_submission_name_header" title={formDisplayName || displayName}>{formDisplayName || displayName}</span>
                  {submitted_By && (
                    <span className="submitted-name" title={`Submitted By ${submitted_By}`}>Submitted By {submitted_By}</span>
                  )}
                  {mainarray?.firstApprovalActionAt && !mainarray?.secondApprovalActionAt && !mainarray?.secondRejectionActionAt && (
                    <span className="approval-timestamp" title={formatApprovalStageLabel('', mainarray.isApproved, mainarray.firstApprovalActionAt, mainarray.firstApprovalActionBy)}>{formatApprovalStageLabel('', mainarray.isApproved, mainarray.firstApprovalActionAt, mainarray.firstApprovalActionBy)}</span>
                  )}
                  {mainarray?.secondApprovalActionAt && (
                    <span className="approval-timestamp" title={formatApprovalStageLabel('Second ', mainarray.isSecondApprove, mainarray.secondApprovalActionAt, mainarray.secondApprovalActionBy)}>{formatApprovalStageLabel('Second ', mainarray.isSecondApprove, mainarray.secondApprovalActionAt, mainarray.secondApprovalActionBy)}</span>
                  )}
                </>
              }
              {formList.length > 0 &&
                <>
                  <span>
                    <div
                      className={`custom-dropdown ${isTitleOpen ? 'open' : ''}`}
                      ref={dropdownRef}
                      role="combobox"
                      aria-haspopup="listbox"
                      aria-expanded={isTitleOpen}
                      aria-label="Select subform"
                      tabIndex={0}
                      onClick={handleOpen}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpen();
                        } else if (e.key === 'Escape' && isTitleOpen) {
                          e.preventDefault();
                          setIsTitleOpen(false);
                        }
                      }}
                    >
                      <div className="selected-form">
                        {selectedForm ? (formList.find(form => form._id === selectedForm)?.form_name ?? '') : ''} <span>
                          <SVGIcon
                            name="arrowDown"
                            width={25}
                            height={25}
                          /></span>
                      </div>
                      <div className="dropdown-options" role="listbox" onClick={handleTitleClose}>
                        <div>
                          {formList.map((form) => (
                            <div
                              key={form._id}
                              role="option"
                              aria-selected={selectedForm === form._id}
                              tabIndex={isTitleOpen ? 0 : -1}
                              className={`form_submission_header_dropdown ${selectedForm === form._id ? 'submission-page-selected' : ''}`}
                              onClick={() => handleSelect(form._id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleSelect(form._id);
                                }
                              }}
                            >
                              {form.isSubmitted === 1 && <SVGIcon name="submitComplte" width={12} height={9} className="custom-icon" />} {" "}
                              {form.form_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </span>
                  <span className="submitted-name-subform">Total:{allsubFormArray.length}</span>
                  {submitted_By && (
                    <span className="submitted-name-subform" title={`Submitted By ${submitted_By}`}>Submitted By {submitted_By}</span>
                  )}
                  {mainarray?.firstApprovalActionAt && !mainarray?.secondApprovalActionAt && !mainarray?.secondRejectionActionAt && (
                    <span className="approval-timestamp-subform" title={formatApprovalStageLabel('', mainarray.isApproved, mainarray.firstApprovalActionAt, mainarray.firstApprovalActionBy)}>{formatApprovalStageLabel('', mainarray.isApproved, mainarray.firstApprovalActionAt, mainarray.firstApprovalActionBy)}</span>
                  )}
                  {mainarray?.secondApprovalActionAt && (
                    <span className="approval-timestamp-subform" title={formatApprovalStageLabel('Second ', mainarray.isSecondApprove, mainarray.secondApprovalActionAt, mainarray.secondApprovalActionBy)}>{formatApprovalStageLabel('Second ', mainarray.isSecondApprove, mainarray.secondApprovalActionAt, mainarray.secondApprovalActionBy)}</span>
                  )}
                </>
              }
            </div>
            {isLoadingBtn && formArray.some(
              (f) => f.type === 'NDVIAnalyzer' && f.value && (!f.ndvi || f.ndvi.error)
            ) && (
              <span className="submitted-name-subform" style={{ fontStyle: 'italic', opacity: 0.8 }}>
                Analyzing satellite imagery… this can take a few seconds
              </span>
            )}
            <ActionButtons
              showDeleteButton={showDeleteButton}
              showNext={showNext}
              showButtonShow={showButtonShow}
              viewOnly={viewOnly}
              ModalIsOpen={ModalIsOpen}
              isApproved={isApproved}
              isSecondApproved={isSecondApproved}
              showApprovePermission={showApprovePermission}
              showSecondApprovePermission={showSecondApprovePermission}
              isLoadingBtn={isLoadingBtn}
              isSaveAndNew={isSaveAndNew}
              isPrintLoading={isPrintLoading}
              isPrint={isPrint}
              templatePdfId={templatePdfId}
              submitButtonLabel={submit_button}
              nextButtonLabel={next_button}
              onDelete={() => openModal('delete')}
              onRejectConfirm={handleRejectConfirm}
              onApprovalConfirm={handleApprovalConfirm}
              onSaveClose={() => handleSubmit(() => handleFormNext("Save"))()}
              onSubmit={handleSubmit(onSubmit1)}
              onSaveAndNew={handleSubmit(handleSaveAndNew)}
              onNext={handleSubmit(handleFormNext)}
              onSecondRejectConfirm={handleSecondRejectConfirm}
              onSecondApprovalConfirm={handleSecondApprovalConfirm}
              onPrint={() => {
                if (templatePdfId) {
                  handlePrint(true, templatePdfId);
                } else if (isPrint) {
                  handlePrint(true, isPrint);
                }
              }}
            />
          </div>
        </div>
        <div className="draggable_submission_content">
          <div className="items-body-draggable-submission">
            <div
              className="group form_submission_body_div"
              key="right_el"
              id="right_card"
            >
              {formArray.length !== 0 && (
                <div className="form_border homeScreen">
                  <form>
                    <fieldset disabled={viewOnly} className="fieldset-reset">
                      <div
                        className="formFieldsContainer two-column">
                        <FormFieldRenderer
                          formArray={formArray}
                          isCompactLayout={isCompactLayout}
                          errors={errors}
                          register={register}
                          setValue={setValue}
                          getValues={getValues}
                          trigger={trigger}
                          clearErrors={clearErrors}
                          setError={setError}
                          selectedImages={selectedImages}
                          setSelectedImages={setSelectedImages}
                          selectedSignature={selectedSignature}
                          setSelectedSignature={setSelectedSignature}
                          selectedAudio={selectedAudio}
                          setSelectedAudio={setSelectedAudio}
                          selectedPhoto={selectedPhoto}
                          setSelectedPhoto={setSelectedPhoto}
                          handlePhotoCapture={handlePhotoCapture}
                          handleClearPhoto={handleClearPhoto}
                          selectedReceipt={selectedReceipt}
                          viewOnly={viewOnly}
                          showOnlyInEdit={SHOW_ONLY_IN_EDIT}
                          disableOnlyInEdit={DISABLE_ONLY_IN_EDIT}
                          isEditMode={isEditMode}
                          gridComponentRefs={gridComponentRefs}
                          handleGrouping={s_handleGrouping}
                          handleGroupingDelete={handleGroupingDelete}
                          handleAPIChanged={s_handleAPIChanged}
                          handleGridApiTrigger={handleGridApiTrigger}
                          numberFormatting={s_numberFormatting}
                          setFormArray={setFormArray}
                          setIsFormEdited={setIsFormEdited}
                          setSelectedImage={setSelectedImage}
                          setSignaturePopupUrl={setSignaturePopupUrl}
                          toggle={s_toggle}
                          setTempMapValue={setTempMapValue}
                          translateLabel={translateLabel}
                          userData={userData}
                          baseURL={baseURL}
                          SignURL={SignURL}
                          toast={toast}
                          authAxios={authAxios}
                          multiPatchCallCache={multiPatchCallCache.current}
                          fieldChanged={s_fieldChanged}
                          fieldSelectChanged={s_fieldSelectChanged}
                          fieldChangedCheckbox={fieldChangedCheckbox}
                          fieldChangedDate={s_fieldChangedDate}
                          fieldChangedTime={s_fieldChangedTime}
                          fieldChangedGeo={s_fieldChangedGeo}
                          fieldMultiSelectChanged={s_fieldMultiSelectChanged}
                          onChangeCalculation={onChangeCalculation}
                          triggerTotalCalculation={triggerTotalCalculation}
                          triggerAggregationTypeCalculation={triggerAggregationTypeCalculation}
                          onChangeDateCalc={onChangeDateCalc}
                          onChangeTime={onChangeTime}
                          triggerMultiPatchRules={s_triggerMultiPatchRules}
                          triggerFormgroupMultiPatch={s_triggerFormgroupMultiPatch}
                          triggerGridSumCalculations={triggerGridSumCalculations}
                          applyFaceIdentifyPatch={s_applyFaceIdentifyPatch}
                          applyFaceCaptureEnroll={s_applyFaceCaptureEnroll}
                          applyReceiptOcrPatch={s_applyReceiptOcrPatch}
                          handleFormGroupAPITrigger={s_handleFormGroupAPITrigger}
                          handleAddMasterData={handleAddMasterData}
                          handleRefreshMasterData={handleRefreshMasterData}
                          handleBarcodeScanChange={s_handleBarcodeScanChange}
                          handleButtonAPIChanged={s_handleButtonAPIChanged}
                          handlenavigatebutton={s_handlenavigatebutton}
                          handleAutoPopulate={s_handleAutoPopulate}
                          customAVTgstFunction={s_customAVTgstFunction}
                          ControlFormGroupRadio={s_ControlFormGroupRadio}
                          handleFileChange={handleFileChange}
                          handleClick={handleClick}
                          clearFile={clearFile}
                          downloadUploadFile={s_downloadUploadFile}
                          onclickImage={onclickImage}
                          handleImageSelect={handleImageSelect}
                          handleImageClick={handleImageClick}
                          handleMultiImageSelect={handleMultiImageSelect}
                          handleDeleteMultiImage={handleDeleteMultiImage}
                          handleCropImageSelect={handleCropImageSelect}
                          handleClearCropImage={handleClearCropImage}
                          onClickSignature={onClickSignature}
                          handleSignatureSelect={handleSignatureSelect}
                          handleAudioCapture={handleAudioCapture}
                          handleClearAudio={handleClearAudio}
                          handlesignatureclick={handlesignatureclick}
                          togglePopup={togglePopup}
                          toggleUpiPayment={openUpiPayment}
                          handleKeyDown={s_handleKeyDown}
                          openGridId={openGridId}
                          handleGridAccordionToggle={s_handleGridAccordionToggle}
                          GroupActionButtons={GroupActionButtons}
                          CustomMenuList={CustomMenuList}
                          MultiSelectMenuList={MultiSelectMenuList}
                          prevGstRef={prevGstRef}
                          refreshButtonClicked={refreshButtonClicked}
                          controlref={controlref}
                          today={today}
                          ModalIsOpen={ModalIsOpen}
                          routeParams={routeParams}
                          setMasterDataSelectionName={setMasterDataSelectionName}
                          setIsModelPopup={setIsModelPopup}
                          setDynamicFormId={setDynamicFormId}
                          setSelectedApiTag={setSelectedApiTag}
                        />
                      </div>
                    </fieldset>
                    {errorList.length > 0 && (
                      <Alert severity="error" role="alert" sx={{ mt: 2, mx: 1 }}>
                        <ul className="fs-error-alert-list">
                          {errorList.map((error, i) => (
                            <li key={i}>{error}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                    <div className="form-bottom-bar">
                      {showPrevious && (
                        <Button
                          sx={BTN_SX.previous}
                          type="button"
                          variant="text"
                          startIcon={<NavigateBeforeIcon />}
                          onClick={handleFormPrevious}
                        >
                          Previous
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
          {isShowing && (
            <MapNewModal
              onCloseModal={toggle}
              props={tempMapValue}
              onSaveModal={toggleMap}
            />
          )}
          {upiPaymentModal && (
            <UpiPaymentModal
              payment={upiPaymentModal}
              onSimulate={handleUpiSimulate}
              onCheckoutSuccess={handleUpiCheckoutSuccess}
              onCheckoutDismiss={handleUpiCheckoutDismiss}
              onClose={closeUpiPaymentModal}
            />
          )}
        </div>
      </div>
    </Suspense>
  );
};

export default SubmissionDynamicform;